// Seeded PNRG for permutations of array `p`, and add `Math.lerp`
const SEED = [0x12345678, 0x9ABCDEF0, 0x12345678, 0x9ABCDEF0];
const fade = function (t) {
  return t * t * t * (t * (t * 6 - 15) + 10);
};
Math.lerp = function (a, b, f) {
  return a + fade(f) * (b - a);
};
Math.seededPNRG = function (a, b, c, d) {
  return function () {
    a >>>= 0; b >>>= 0; c >>>= 0; d >>>= 0;
    let t = (a + b) | 0;
    a = b ^ b >>> 9;
    b = c + (c << 3) | 0;
    c = (c << 21 | c >>> 11);
    d = d + 1 | 0;
    t = t + d | 0;
    c = c + t | 0;
    return (t >>> 0) / 4294967296;
  }
};
// Generate permutation of the array (Knuth shuffle)
const random = Math.seededPNRG(...SEED);
const p = [];
{
  for (let i = 0; i < 256; i++)
    p[i] = i;
  let j, t;
  for (let i = 0; i < 255; i++) {
    j = i + Math.floor(random() * (256 - i));
    t = p[i];
    p[i] = p[j];
    p[j] = t;
  }
  p.push(...p);
}
// Actual Perlin noise generator
const perlin = function (x, y, z) {
  x = x % 512;
  y = y % 512;
  z = z % 512;
  let xi = x & 255, yi = y & 255, zi = z & 255;
  let xf = x - Math.floor(x), yf = y - Math.floor(y), zf = z - Math.floor(z);
  let u = fade(xf), v = fade(yf), w = fade(zf);
  let aaa, aba, aab, abb, baa, bba, bab, bbb;
  {
    let inc = n => n + 1;
    aaa = p[p[p[xi] + yi] + zi];
    aba = p[p[p[xi] + inc(yi)] + zi];
    aab = p[p[p[xi] + yi] + inc(zi)];
    abb = p[p[p[xi] + inc(yi)] + inc(zi)];
    baa = p[p[p[inc(xi)] + yi] + zi];
    bba = p[p[p[inc(xi)] + inc(yi)] + zi];
    bab = p[p[p[inc(xi)] + yi] + inc(zi)];
    bbb = p[p[p[inc(xi)] + inc(yi)] + inc(zi)];
  }
  const grad = function (hash, x, y, z) {
    let h = hash & 15;
    let u = h < 8 ? x : y;
    let v;
    if (h < 4)
      v = y;
    else if (h === 12 || h === 14)
      v = x;
    else v = z;
    return (h & 1 === 0 ? u : -u) + (h & 2 === 0 ? v : -v);
  };
  let x1, x2, y1, y2;
  x1 = Math.lerp(
    grad(aaa, xf, yf, zf),
    grad(baa, xf - 1, yf, zf),
    u
  );
  x2 = Math.lerp(
    grad(aba, xf, yf - 1, zf),
    grad(bba, xf - 1, yf - 1, zf),
    u
  );
  y1 = Math.lerp(x1, x2, v);
  x1 = Math.lerp(
    grad(aab, xf, yf, zf - 1),
    grad(bab, xf - 1, yf, zf - 1),
    u
  );
  x2 = Math.lerp(
    grad(abb, xf, yf - 1, zf - 1),
    grad(bbb, xf - 1, yf - 1, zf - 1),
    u
  );
  y2 = Math.lerp(x1, x2, v);
  return (Math.lerp(y1, y2, w) + 1) / 2;
};
function fbm(x, y, z, octaves = 4, persistence = 0.5) {
  let total = 0;
  let amplitude = 1;
  let maxValue = 0;
  for (let i = 0; i < octaves; i++) {
    total += perlin(x, y, z) * amplitude;
    maxValue += amplitude;
    amplitude *= persistence;
    x *= 2;
    y *= 2;
    z *= 2;
  }
  return total / maxValue;
}
function detectDeviceProfile(glContext) {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
  const isMobile = /mobi|android|iphone|ipad/i.test(ua);
  const lowMem = typeof navigator !== 'undefined' && navigator.deviceMemory && navigator.deviceMemory <= 3;
  const lowCores = typeof navigator !== 'undefined' && navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4;
  let smallTextureCap = false;
  try {
    const cap = glContext ? glContext.getParameter(glContext.MAX_TEXTURE_SIZE) : 0;
    smallTextureCap = cap > 0 && cap < 4096;
  } catch (e) {
    smallTextureCap = false;
  }
  // Treat all mobile devices as low power to keep generation responsive, and
  // include other hardware constraints as additional signals.
  const isLowPower = isMobile || lowMem || lowCores || smallTextureCap;
  return {
    isLowPower,
    lightCount: isLowPower ? 4 : 8,
    interiorRings: isLowPower ? 12 : 24,
    maxEdgeSamples: isLowPower ? 640 : 1024,
    finalSampleCap: isLowPower ? 240 : 512,
    edgeSpacingDivisor: isLowPower ? 240 : 320,
    finalSampleDivisor: isLowPower ? 140 : 200,
    dilationScale: isLowPower ? 0.65 : 1.0,
  };
}
// Pure JavaScript WebGL code for a 3D custom shaped pillow from PNG alpha channel
// Loads from 'bg.png' relative URL
// Features:
// - Camera orbits around object
// - 360 auto-rotation with pause on interaction
// - White background
// - Image on front, horizontally flipped on back
// - Fully unified 3D shape with NO gaps
// - Smooth, pillow-like tapered edges
const canvas = document.getElementById('glcanvas');
// Request alpha channel so the canvas can be transparent over the page background
const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false });
if (!gl) {
  alert((document.documentElement.lang || '').toLowerCase().startsWith('he') ? 'הדפדפן שלכם אינו תומך בתצוגת תלת־ממד (WebGL)' : 'WebGL not supported');
}
const deviceProfile = detectDeviceProfile(gl);
if (typeof window !== 'undefined') {
  window.deviceProfile = deviceProfile;
}
const isLowPowerDevice = !!(deviceProfile && deviceProfile.isLowPower);
const DPR_CAP = isLowPowerDevice ? 1.25 : 2;
const MAX_CANVAS_SIZE = isLowPowerDevice ? 720 : 1100;
function resizeCanvasForDevice() {
  if (!canvas || !gl) return;
  const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
  const availableSize = Math.min(
    Math.min(window.innerWidth || 1000, MAX_CANVAS_SIZE),
    Math.min(window.innerHeight || 1000, MAX_CANVAS_SIZE)
  );
  const cssSize = Math.max(320, Math.floor(availableSize * 0.9));
  canvas.style.width = `${cssSize}px`;
  canvas.style.height = `${cssSize}px`;
  canvas.width = Math.floor(cssSize * dpr);
  canvas.height = Math.floor(cssSize * dpr);
  gl.viewport(0, 0, canvas.width, canvas.height);
}
resizeCanvasForDevice();
window.addEventListener('resize', () => {
  resizeCanvasForDevice();
  drawScene();
}, { passive: true });
// CONFIGURABLE PARAMETERS
const OUTER_MARGIN = 0.00;
const BULGE_BOOST = 0.8; // Reduced to prevent negative derivative and inward bending
const BULGE_EASE_POWER = 1.35; // Controls how quickly the surface falls toward the edge
const BASE_RING_LEVELS = 16; // Used for default ring count calculation
const INTERIOR_RING_COUNT = deviceProfile.interiorRings || Math.max(6, Math.min(24, Math.round(BASE_RING_LEVELS * 2)));
let currentScale = 1;
let isAnimating = false;
let animStartTime = 0;
let animationDuration = 1; // Default to 1 second
let animSpinSpeed = 1.256; // Default animation spin speed
let rotationSpeed = 0.628; // Normal rotation speed
let alphaTolerance = 128; // Alpha threshold for contour detection
let smoothPercent = 5; // Smoothing percentage relative to shape size (e.g., 5 for 5%)
let secondSmoothPercent = 0; // Additional smoothing after the first (0 to disable)
let useFixedCamera = true; // New option for fixed light
let pillowColor = [1.0, 1.0, 1.0]; // Default white for pillow/edges
let backgroundColor = [1.0, 1.0, 1.0, 1.0]; // Default white for background
let paddingFraction;
let centerThickness = 0.18; // New: Thickness at the center
let edgeThickness = 0.05; // New: Thickness at the edge (closure)
let foldAmplitude = 0.05; // New: Amplitude for cloth-like folds
let foldFrequency = 10; // New: Frequency for cloth-like folds (higher = more folds)
let pillowTightness = 1.0; // Tightness of the string wrap (0.0 = loose, 1.0 = tight/convex hull)
let magnetPower = 0.4; // Magnet power for pillow shape creation
let airPressureStrength = 0.15; // [DEPRECATED] No longer used - kept for backward compatibility
let inflationPressure = 0.08; // Inflation pressure to prevent weird objects (0.0 to 1.0)
let edgeSmoothingIterations = 3; // [DEPRECATED] No longer used - kept for backward compatibility
let labelAspectRatio = 4.0; // Label width/height ratio (default 4.0 for rectangle)
let labelHeightPercent = 3.8; // Label height as percentage of max(width, height) of 3D model
let labelTexture = null; // Texture for the label
let isGeneratingModel = false; // Tracks whether a generation is currently running
let currentImageSource = null; // Remember the latest image source in use
let lightingIntensity = 1.5; // Lighting intensity multiplier (ambient-only now, brightened)
let lightHeightPercent = 1.0; // Lighting position as percentage of object height above it
const RENDER_SHADOW = true; // Keep floor shadow enabled (set to false to disable)
// Generate 3D function
function generate3D(imageUrl, zoom = true, animationTime = 1, animationSpinSpeed = 1.256, lightPos = [0, 0, 1], tolerance = 128, smoothPct = 5, fixedCamera = true, pillowHex = '#ffffff', bgHex = '#ffffff', paddingPercent = 10, secondSmoothPct = 0, centerThick = 0.18, edgeThick = 0.15, foldAmp = 0.05, foldFreq = 10, tightness = 1.0, magnetPow = 7, labelRatio = 3.0, labelHeightPct = 3.8, airPressure = 0.15, inflation = 0.08, edgeSmoothIters = 3) {
  isGeneratingModel = true;
  currentImageSource = imageUrl;
  if (typeof window !== 'undefined') {
    window.isGeneratingModel = true;
  }
  // Hide loading overlay when 3D generation starts
  const overlay = document.getElementById('loading-overlay');
  if (overlay) overlay.style.display = 'none';
  
  // Clear current buffers to hide the model while loading the new one
  buffers = null;
  dimensionTextBuffers = null;
  referenceLineBuffers = null;
  dimensionGuideBuffers = null;
  modelBoundingBox = null;
  
  allowZoom = zoom;
  animationDuration = animationTime;
  animSpinSpeed = animationSpinSpeed;
  alphaTolerance = tolerance;
  smoothPercent = smoothPct;
  secondSmoothPercent = secondSmoothPct;
  useFixedCamera = fixedCamera;
  // Parse pillow color hex to RGB
  pillowColor = hexToRgb(pillowHex);
  // Parse background color hex to RGBA
  backgroundColor = hexToRgba(bgHex);
  paddingFraction = paddingPercent / 100;
  centerThickness = centerThick;
  edgeThickness = edgeThick;
  foldAmplitude = foldAmp;
  foldFrequency = foldFreq;
  pillowTightness = Math.max(0, Math.min(1, tightness)); // Clamp between 0 and 1
  magnetPower = magnetPow;
  labelAspectRatio = Math.max(0.1, Math.min(10, labelRatio)); // Clamp between 0.1 and 10
  labelHeightPercent = Math.max(0.1, Math.min(50, labelHeightPct)); // Clamp between 0.1 and 50
  // [DEPRECATED] airPressureStrength and edgeSmoothingIterations no longer used - kept for backward compatibility
  airPressureStrength = Math.max(0, Math.min(1, airPressure)); // Clamp between 0 and 1
  inflationPressure = Math.max(0, Math.min(1, inflation)); // Clamp between 0 and 1
  edgeSmoothingIterations = Math.max(1, Math.min(10, Math.round(edgeSmoothIters))); // Clamp between 1 and 10
  disposeImageTextures();
  texture = loadTexture(gl, imageUrl);
  // Load label texture if not already loaded
  if (!labelTexture) {
    labelTexture = loadLabelTexture(gl, window.labelTextureUrl);
  }
}
function hexToRgb(hex) {
  hex = hex.replace(/^#/, '');
  const bigint = parseInt(hex, 16);
  const r = ((bigint >> 16) & 255) / 255;
  const g = ((bigint >> 8) & 255) / 255;
  const b = (bigint & 255) / 255;
  return [r, g, b];
}
function hexToRgba(hex) {
  hex = hex.replace(/^#/, '');
  const bigint = parseInt(hex, 16);
  const r = ((bigint >> 16) & 255) / 255;
  const g = ((bigint >> 8) & 255) / 255;
  const b = (bigint & 255) / 255;
  return [r, g, b, 1.0];
}
function updatePillowColor(hex) {
  if (!gl || !buffers) return false;
  if (!hex) return false;
  const rgb = hexToRgb(hex);
  if (!rgb || rgb.length !== 3) return false;
  if (pillowColor[0] === rgb[0] && pillowColor[1] === rgb[1] && pillowColor[2] === rgb[2]) {
    return true;
  }
  pillowColor = rgb;
  updatePlainTextureColor(pillowColor);
  drawScene();
  return true;
}
if (typeof window !== 'undefined') {
  window.updatePillowColor = updatePillowColor;
}
// Generate evenly distributed points on a sphere using Fibonacci sphere algorithm
function generateSpherePoints(numPoints) {
  const points = [];
  const directions = [];
  const goldenAngle = Math.PI * (3 - Math.sqrt(5)); // Golden angle in radians
 
  for (let i = 0; i < numPoints; i++) {
    const y = 1 - (i / (numPoints - 1)) * 2; // y goes from 1 to -1
    const radius = Math.sqrt(1 - y * y); // radius at y
   
    const theta = goldenAngle * i; // golden angle increment
   
    const x = Math.cos(theta) * radius;
    const z = Math.sin(theta) * radius;
   
    // Point on unit sphere
    points.push([x, y, z]);
   
    // Direction points toward center (origin), so it's the negative of the position
    directions.push(normalize([-x, -y, -z]));
  }
 
  return { positions: points, directions: directions };
}
// Generate fragment shader code with N lights
// function generateFragmentShader(numLights) {
//   let uniformDeclarations = '';
//   let lightCalculations = '';
//   let lightSum = '';
 
//   for (let i = 1; i <= numLights; i++) {
//     uniformDeclarations += `
//       uniform vec3 uSpotlight${i}Position;
//       uniform vec3 uSpotlight${i}Direction;
//       uniform float uSpotlight${i}Angle;
//       uniform float uSpotlight${i}Intensity;
//       uniform float uSpotlight${i}Decay;`;
   
//     lightCalculations += `
//           float spot${i} = calculateSpotlight(uSpotlight${i}Position, uSpotlight${i}Direction, uSpotlight${i}Angle, uSpotlight${i}Intensity, uSpotlight${i}Decay);`;
   
//     if (i === 1) {
//       lightSum = 'spot1';
//     } else {
//       lightSum += ` + spot${i}`;
//     }
//   }
 
//   return `
//       precision mediump float;
//       varying vec3 vLighting;
//       varying vec2 vTextureCoord;
//       varying vec3 vWorldPosition;
//       varying vec3 vWorldNormal;
//       uniform sampler2D uSampler;
//       uniform bool uUseTexture;
//       uniform bool uIsShadow;
//       uniform bool uIsDimensionGuide;
//       uniform vec3 uPillowColor;
//       uniform vec3 uGuideColor;${uniformDeclarations}
     
//       float calculateSpotlight(vec3 lightPos, vec3 lightDir, float angle, float intensity, float decay) {
//         vec3 toLight = lightPos - vWorldPosition;
//         float dist = length(toLight);
//         vec3 lightDirNorm = normalize(toLight);
//         vec3 lightDirNormalized = normalize(lightDir);
       
//         // Calculate angle between light direction and vector to fragment
//         float cosAngle = dot(-lightDirNormalized, lightDirNorm);
//         // Wider smooth transition for softer falloff
//         float spotFactor = smoothstep(angle, angle + 0.4, cosAngle);
       
//         // Distance attenuation - very minimal with low decay
//         float attenuation = 1.0 / (1.0 + decay * dist * dist);
       
//         // Diffuse lighting
//         float NdotL = max(dot(vWorldNormal, lightDirNorm), 0.0);
       
//         return spotFactor * attenuation * intensity * NdotL;
//       }
     
//       void main(void) {
//         if (uIsShadow) {
//           // Dark gray shadow - increased alpha for clearly visible floor shadow
//           gl_FragColor = vec4(0.0, 0.0, 0.0, 0.2);
//         } else if (uIsDimensionGuide) {
//           // Dimension guides - bright color, always visible
//           gl_FragColor = vec4(uGuideColor, 1.0);
//         } else {
//           // Calculate spotlight contributions${lightCalculations}

//           // Combine directional lights with spotlights
//           vec3 totalLighting = vLighting + vec3(${lightSum});

//           if (uUseTexture) {
//             vec4 texColor = texture2D(uSampler, vTextureCoord);
//             vec3 color = mix(uPillowColor, texColor.rgb, texColor.a);
//             gl_FragColor = vec4(color * totalLighting, 1.0);
//           } else {
//             // Use pillow color for edge seam (closing area) to match 3D surface color
//             gl_FragColor = vec4(uPillowColor * totalLighting, 1.0);
//           }
//         }
//       }
//     `;
// }

// Generate fragment shader code with N lights
function generateFragmentShader(numLights) {
  const hasLights = numLights > 0;
  let uniformDeclarations = '';
  let lightCalculations = '';
  let helperFunctions = '';
  let totalLightingExpr = 'vLighting';

  if (hasLights) {
    let lightSum = '';
    for (let i = 1; i <= numLights; i++) {
      uniformDeclarations += `
      uniform vec3 uSpotlight${i}Position;
      uniform vec3 uSpotlight${i}Direction;
      uniform float uSpotlight${i}Angle;
      uniform float uSpotlight${i}Intensity;
      uniform float uSpotlight${i}Decay;`;
     
      lightCalculations += `
          float spot${i} = calculateSpotlight(
            uSpotlight${i}Position,
            uSpotlight${i}Direction,
            uSpotlight${i}Angle,
            uSpotlight${i}Intensity,
            uSpotlight${i}Decay
          );`;
     
      lightSum += (i === 1) ? 'spot1' : ` + spot${i}`;
    }
    totalLightingExpr = `vLighting + vec3(${lightSum})`;
    helperFunctions = `
      float calculateSpotlight(vec3 lightPos, vec3 lightDir, float angle, float intensity, float decay) {
        vec3 toLight = lightPos - vWorldPosition;
        float dist = length(toLight);
        vec3 lightDirNorm = normalize(toLight);
        vec3 lightDirNormalized = normalize(lightDir);
       
        float cosAngle = dot(-lightDirNormalized, lightDirNorm);
        float spotFactor = smoothstep(angle, angle + 0.4, cosAngle);
        float attenuation = 1.0 / (1.0 + decay * dist * dist);
        float NdotL = max(dot(vWorldNormal, lightDirNorm), 0.0);
       
        return spotFactor * attenuation * intensity * NdotL;
      }`;
  }
 
  return `
      precision mediump float;
      varying vec3 vLighting;
      varying vec2 vTextureCoord;
      varying vec3 vWorldPosition;
      varying vec3 vWorldNormal;
      uniform sampler2D uSampler;
      uniform bool uUseTexture;
      uniform bool uIsShadow;
      uniform bool uIsDimensionGuide;
      uniform vec3 uPillowColor;
      uniform vec3 uGuideColor;${uniformDeclarations}
      ${helperFunctions}
     
      void main(void) {
        if (uIsShadow) {
          // UV-based radial blur (reliable for flat shadow planes)
          vec2 center = vec2(0.5, 0.5);
          float dist = distance(vTextureCoord, center);

          // Soft edge blur
          float blur = smoothstep(0.3, 0.6, dist);

          // Natural shadow opacity
          float alpha = mix(0.15, 0.02, blur);

          gl_FragColor = vec4(0.0, 0.0, 0.0, alpha);
        } 
        else if (uIsDimensionGuide) {
          gl_FragColor = vec4(uGuideColor, 1.0);
        } 
        else {
          ${lightCalculations}

          vec3 totalLighting = ${totalLightingExpr};

          if (uUseTexture) {
            vec4 texColor = texture2D(uSampler, vTextureCoord);
            vec3 color = mix(uPillowColor, texColor.rgb, texColor.a);
            gl_FragColor = vec4(color * totalLighting, 1.0);
          } else {
            gl_FragColor = vec4(uPillowColor * totalLighting, 1.0);
          }
        }
      }
    `;
}

// Generate uniform locations for N lights
function generateUniformLocations(gl, shaderProgram, numLights) {
  const locations = {
    projectionMatrix: gl.getUniformLocation(shaderProgram, 'uProjectionMatrix'),
    modelViewMatrix: gl.getUniformLocation(shaderProgram, 'uModelViewMatrix'),
    normalMatrix: gl.getUniformLocation(shaderProgram, 'uNormalMatrix'),
    uSampler: gl.getUniformLocation(shaderProgram, 'uSampler'),
    uUseTexture: gl.getUniformLocation(shaderProgram, 'uUseTexture'),
    isShadow: gl.getUniformLocation(shaderProgram, 'uIsShadow'),
    isDimensionGuide: gl.getUniformLocation(shaderProgram, 'uIsDimensionGuide'),
    pillowColor: gl.getUniformLocation(shaderProgram, 'uPillowColor'),
    guideColor: gl.getUniformLocation(shaderProgram, 'uGuideColor'),
    lightingIntensity: gl.getUniformLocation(shaderProgram, 'uLightingIntensity'),
  };
 
  for (let i = 1; i <= numLights; i++) {
    locations[`spotlight${i}Position`] = gl.getUniformLocation(shaderProgram, `uSpotlight${i}Position`);
    locations[`spotlight${i}Direction`] = gl.getUniformLocation(shaderProgram, `uSpotlight${i}Direction`);
    locations[`spotlight${i}Angle`] = gl.getUniformLocation(shaderProgram, `uSpotlight${i}Angle`);
    locations[`spotlight${i}Intensity`] = gl.getUniformLocation(shaderProgram, `uSpotlight${i}Intensity`);
    locations[`spotlight${i}Decay`] = gl.getUniformLocation(shaderProgram, `uSpotlight${i}Decay`);
  }
 
  return locations;
}
// Vertex shader
const vsSource = `
      precision highp float;
      attribute vec4 aVertexPosition;
      attribute vec3 aVertexNormal;
      attribute vec2 aTextureCoord;
      uniform mat4 uModelViewMatrix;
      uniform mat4 uProjectionMatrix;
      uniform mat4 uNormalMatrix;
      uniform float uLightingIntensity;
      varying vec3 vLighting;
      varying vec2 vTextureCoord;
      varying vec3 vWorldPosition;
      varying vec3 vWorldNormal;
      void main(void) {
        vec4 worldPos = uModelViewMatrix * aVertexPosition;
        vWorldPosition = worldPos.xyz;
        vec4 transformedNormal = uNormalMatrix * vec4(aVertexNormal, 1.0);
        vWorldNormal = normalize(transformedNormal.xyz);
        gl_Position = uProjectionMatrix * worldPos;
        vTextureCoord = aTextureCoord;
        // Increased ambient light (50% more: 0.4 -> 0.6) with adjustable intensity
        vec3 ambientLight = vec3(0.6, 0.6, 0.6) * uLightingIntensity;
        vLighting = ambientLight;
      }
    `;
// Number of lights - can be changed here
const NUM_LIGHTS = 0; // Disable dynamic spotlights for lighter, ambient-only shading
const LIGHT_SPHERE = generateSpherePoints(NUM_LIGHTS);
const spotlightPositions = Array.from({ length: NUM_LIGHTS }, () => [0, 0, 0]);
const spotlightDirections = Array.from({ length: NUM_LIGHTS }, () => [0, -1, 0]);
const modelSpaceLightPositions = Array.from({ length: NUM_LIGHTS }, () => [0, 0, 0]);
// Fragment shader - generated for NUM_LIGHTS
const fsSource = generateFragmentShader(NUM_LIGHTS);
function loadShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('Shader compile error:', gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}
const shaderProgram = gl.createProgram();
const vertexShader = loadShader(gl, gl.VERTEX_SHADER, vsSource);
const fragmentShader = loadShader(gl, gl.FRAGMENT_SHADER, fsSource);
gl.attachShader(shaderProgram, vertexShader);
gl.attachShader(shaderProgram, fragmentShader);
gl.linkProgram(shaderProgram);
if (!gl.getProgramParameter(shaderProgram, gl.LINK_STATUS)) {
  console.error('Program link error:', gl.getProgramInfoLog(shaderProgram));
}
const programInfo = {
  program: shaderProgram,
  attribLocations: {
    vertexPosition: gl.getAttribLocation(shaderProgram, 'aVertexPosition'),
    vertexNormal: gl.getAttribLocation(shaderProgram, 'aVertexNormal'),
    textureCoord: gl.getAttribLocation(shaderProgram, 'aTextureCoord'),
  },
  uniformLocations: generateUniformLocations(gl, shaderProgram, NUM_LIGHTS),
};
// Matrix functions
function identity() {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
}
function multiply(a, b) {
  const a00 = a[0], a01 = a[1], a02 = a[2], a03 = a[3];
  const a10 = a[4], a11 = a[5], a12 = a[6], a13 = a[7];
  const a20 = a[8], a21 = a[9], a22 = a[10], a23 = a[11];
  const a30 = a[12], a31 = a[13], a32 = a[14], a33 = a[15];
  const b00 = b[0], b01 = b[1], b02 = b[2], b03 = b[3];
  const b10 = b[4], b11 = b[5], b12 = b[6], b13 = b[7];
  const b20 = b[8], b21 = b[9], b22 = b[10], b23 = b[11];
  const b30 = b[12], b31 = b[13], b32 = b[14], b33 = b[15];
  return [
    a00 * b00 + a01 * b10 + a02 * b20 + a03 * b30,
    a00 * b01 + a01 * b11 + a02 * b21 + a03 * b31,
    a00 * b02 + a01 * b12 + a02 * b22 + a03 * b32,
    a00 * b03 + a01 * b13 + a02 * b23 + a03 * b33,
    a10 * b00 + a11 * b10 + a12 * b20 + a13 * b30,
    a10 * b01 + a11 * b11 + a12 * b21 + a13 * b31,
    a10 * b02 + a11 * b12 + a12 * b22 + a13 * b32,
    a10 * b03 + a11 * b13 + a12 * b23 + a13 * b33,
    a20 * b00 + a21 * b10 + a22 * b20 + a23 * b30,
    a20 * b01 + a21 * b11 + a22 * b21 + a23 * b31,
    a20 * b02 + a21 * b12 + a22 * b22 + a23 * b32,
    a20 * b03 + a21 * b13 + a22 * b23 + a23 * b33,
    a30 * b00 + a31 * b10 + a32 * b20 + a33 * b30,
    a30 * b01 + a31 * b11 + a32 * b21 + a33 * b31,
    a30 * b02 + a31 * b12 + a32 * b22 + a33 * b32,
    a30 * b03 + a31 * b13 + a32 * b23 + a33 * b33,
  ];
}
function perspective(fieldOfView, aspect, near, far) {
  const f = 1.0 / Math.tan(fieldOfView / 2);
  const rangeInv = 1 / (near - far);
  return [
    f / aspect, 0, 0, 0,
    0, f, 0, 0,
    0, 0, (near + far) * rangeInv, -1,
    0, 0, near * far * rangeInv * 2, 0
  ];
}
function translate(m, x, y, z) {
  return multiply(m, [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1]);
}
function rotateX(m, angle) {
  const s = Math.sin(angle);
  const c = Math.cos(angle);
  return multiply(m, [1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1]);
}
function rotateY(m, angle) {
  const s = Math.sin(angle);
  const c = Math.cos(angle);
  return multiply(m, [c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1]);
}
// Rotate from one direction vector to another using Rodrigues' rotation formula
function rotateFromTo(m, fromDir, toDir) {
  const from = normalize([fromDir[0], fromDir[1], fromDir[2]]);
  const to = normalize([toDir[0], toDir[1], toDir[2]]);
 
  // Check if vectors are already aligned
  const dot = from[0] * to[0] + from[1] * to[1] + from[2] * to[2];
  if (Math.abs(dot - 1.0) < 1e-6) {
    // Already aligned, return identity
    return m;
  }
  if (Math.abs(dot + 1.0) < 1e-6) {
    // Opposite directions, rotate 180 degrees around perpendicular axis
    let perp;
    if (Math.abs(from[0]) < 0.9) {
      perp = normalize([1, 0, 0]);
    } else {
      perp = normalize([0, 1, 0]);
    }
    const axis = normalize([
      from[1] * perp[2] - from[2] * perp[1],
      from[2] * perp[0] - from[0] * perp[2],
      from[0] * perp[1] - from[1] * perp[0]
    ]);
    return rotateAroundAxis(m, axis, Math.PI);
  }
 
  // Calculate rotation axis (cross product)
  const cross = [
    from[1] * to[2] - from[2] * to[1],
    from[2] * to[0] - from[0] * to[2],
    from[0] * to[1] - from[1] * to[0]
  ];
  const crossLen = Math.sqrt(cross[0] * cross[0] + cross[1] * cross[1] + cross[2] * cross[2]);
 
  // If cross product is zero (shouldn't happen after parallel checks, but safety check)
  if (crossLen < 1e-6) {
    return m; // Return identity if somehow parallel
  }
 
  const axis = normalize(cross);
 
  // Calculate rotation angle
  const angle = Math.acos(Math.max(-1, Math.min(1, dot)));
 
  return rotateAroundAxis(m, axis, angle);
}
// Rotate around an arbitrary axis using Rodrigues' rotation formula
function rotateAroundAxis(m, axis, angle) {
  const ax = normalize([axis[0], axis[1], axis[2]]);
  const s = Math.sin(angle);
  const c = Math.cos(angle);
  const t = 1 - c;
 
  const x = ax[0], y = ax[1], z = ax[2];
  const tx = t * x, ty = t * y;
 
  const rot = [
    tx * x + c, tx * y + s * z, tx * z - s * y, 0,
    tx * y - s * z, ty * y + c, ty * z + s * x, 0,
    tx * z + s * y, ty * z - s * x, t * z * z + c, 0,
    0, 0, 0, 1
  ];
 
  return multiply(m, rot);
}
function scale(m, s) {
  return multiply(m, [s, 0, 0, 0, 0, s, 0, 0, 0, 0, s, 0, 0, 0, 0, 1]);
}
function inverse(m) {
  let o = [];
  let a00 = m[0], a01 = m[1], a02 = m[2], a03 = m[3],
    a10 = m[4], a11 = m[5], a12 = m[6], a13 = m[7],
    a20 = m[8], a21 = m[9], a22 = m[10], a23 = m[11],
    a30 = m[12], a31 = m[13], a32 = m[14], a33 = m[15];
  let b00 = a00 * a11 - a01 * a10,
    b01 = a00 * a12 - a02 * a10,
    b02 = a00 * a13 - a03 * a10,
    b03 = a01 * a12 - a02 * a11,
    b04 = a01 * a13 - a03 * a11,
    b05 = a02 * a13 - a03 * a12,
    b06 = a20 * a31 - a21 * a30,
    b07 = a20 * a32 - a22 * a30,
    b08 = a20 * a33 - a23 * a30,
    b09 = a21 * a32 - a22 * a31,
    b10 = a21 * a33 - a23 * a31,
    b11 = a22 * a33 - a23 * a32;
  let det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  if (!det) return null;
  det = 1.0 / det;
  o[0] = (a11 * b11 - a12 * b10 + a13 * b09) * det;
  o[1] = (a02 * b10 - a03 * b09 - a01 * b11) * det;
  o[2] = (a31 * b05 - a32 * b04 + a33 * b03) * det;
  o[3] = (a23 * b04 - a21 * b05 - a22 * b03) * det;
  o[4] = (a12 * b08 - a10 * b11 - a13 * b07) * det;
  o[5] = (a00 * b11 - a02 * b08 + a03 * b07) * det;
  o[6] = (a32 * b02 - a30 * b05 - a33 * b01) * det;
  o[7] = (a20 * b05 - a22 * b02 + a23 * b01) * det;
  o[8] = (a10 * b10 - a11 * b08 + a13 * b06) * det;
  o[9] = (a01 * b08 - a00 * b10 - a03 * b06) * det;
  o[10] = (a30 * b04 - a31 * b02 + a33 * b00) * det;
  o[11] = (a21 * b02 - a20 * b04 - a23 * b00) * det;
  o[12] = (a11 * b07 - a10 * b09 - a12 * b06) * det;
  o[13] = (a00 * b09 - a01 * b07 + a02 * b06) * det;
  o[14] = (a31 * b01 - a30 * b03 - a32 * b00) * det;
  o[15] = (a20 * b03 - a21 * b01 + a22 * b00) * det;
  return o;
}
function transpose(m) {
  return [
    m[0], m[4], m[8], m[12],
    m[1], m[5], m[9], m[13],
    m[2], m[6], m[10], m[14],
    m[3], m[7], m[11], m[15],
  ];
}
function normalMatrix(modelView) {
  const inv = inverse(modelView);
  if (!inv) return identity();
  return transpose(inv);
}
function normalize(v) {
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  return len > 0 ? [v[0] / len, v[1] / len, v[2] / len] : [0, 0, 1];
}
function matVecMul(m, v) {
  return [
    m[0] * v[0] + m[4] * v[1] + m[8] * v[2] + m[12] * v[3],
    m[1] * v[0] + m[5] * v[1] + m[9] * v[2] + m[13] * v[3],
    m[2] * v[0] + m[6] * v[1] + m[10] * v[2] + m[14] * v[3],
    m[3] * v[0] + m[7] * v[1] + m[11] * v[2] + m[15] * v[3]
  ];
}
let texture;
let flippedTexture;
let flippedReady = false;
let plainTexture;
function disposeImageTextures() {
  if (!gl) return;
  if (texture) {
    gl.deleteTexture(texture);
    texture = null;
  }
  if (flippedTexture) {
    gl.deleteTexture(flippedTexture);
    flippedTexture = null;
    flippedReady = false;
  }
}
function updatePlainTextureColor(color = pillowColor) {
  if (!gl) return;
  if (!plainTexture) {
    plainTexture = gl.createTexture();
  }
  gl.bindTexture(gl.TEXTURE_2D, plainTexture);
  const r = Math.round(color[0] * 255);
  const g = Math.round(color[1] * 255);
  const b = Math.round(color[2] * 255);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([r, g, b, 255]));
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
}
function initPlainTexture() {
  updatePlainTextureColor(pillowColor);
}
function markGenerationDone() {
  isGeneratingModel = false;
  if (typeof window !== 'undefined') {
    window.isGeneratingModel = false;
  }
}
// Create a horizontally flipped copy of the source image.
// The full source (including transparent padding) is flipped so only the visible contour is mapped later.
function createFlippedImage(sourceImage) {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      const width = sourceImage.naturalWidth || sourceImage.width;
      const height = sourceImage.naturalHeight || sourceImage.height;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      /* ═══ עותק, לא שיקוף ═══
         כאן היה translate+scale(-1,1). המדידה הראתה שזה מה שחתך את
         התמונה בגב: קו המתאר של הכרית בגב הוא המראה של החזית — זו
         הגאומטריה ואי אפשר לשנות אותה — ולכן תוכן משוקף כבר לא
         מתיישר איתו, והחלקים שחורגים נחתכים.
         עותק זהה מתיישר בדיוק, ולכן הגב נראה בדיוק כמו היום. */
      ctx.drawImage(sourceImage, 0, 0, width, height);

      const flippedImg = new Image();
      flippedImg.onload = () => resolve(flippedImg);
      flippedImg.onerror = (e) => reject(e);
      flippedImg.src = canvas.toDataURL();
    } catch (e) {
      reject(e);
    }
  });
}
function loadTexture(gl, url) {
  const tex = gl.createTexture();
  const backTex = gl.createTexture();
  const pixel = new Uint8Array([0, 0, 255, 255]);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
  gl.bindTexture(gl.TEXTURE_2D, backTex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
  // Make back texture available immediately (placeholder) for early draw calls
  flippedTexture = backTex;
  flippedReady = false;
  const image = new Image();
  image.decoding = 'async';
  image.loading = 'eager';
  const isInlineData = typeof url === 'string' && (url.startsWith('data:') || url.startsWith('blob:'));
 
  // Set crossOrigin for cross-origin images (like S3 URLs)
  // Check if URL is from a different origin
  try {
    const urlObj = new URL(url, window.location.href);
    const isCrossOrigin = urlObj.origin !== window.location.origin;
    if (isCrossOrigin) {
      image.crossOrigin = 'anonymous';
    }
  } catch (e) {
    // If URL parsing fails (e.g., data URL), try to detect data URL
    if (!url.startsWith('data:') && !url.startsWith('blob:')) {
      // Assume it might be cross-origin and set crossOrigin
      image.crossOrigin = 'anonymous';
    }
  }
 
  // Promise-based image loading with proper validation - more patient for slow-loading images
  const loadAndValidateImage = function(img) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = isInlineData ? 60 : 200; // Inline data should be ready faster
      const startTime = performance.now();
     
      function validateAndProcess() {
        attempts++;
        const elapsedTime = (performance.now() - startTime) / 1000;

        // Inline data URLs decode quickly; fast-path when dimensions are available
        if (isInlineData && img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
          resolve(img);
          return;
        }
       
        // Give it a moment even after onload fires - images need time to decode
        // Wait at least 50ms after onload before first check
        if (attempts === 1 && elapsedTime < 0.05) {
          setTimeout(validateAndProcess, isInlineData ? 40 : 100);
          return;
        }
       
        // Check if image is complete and has valid dimensions
        if (!img.complete) {
          if (attempts >= maxAttempts) {
            reject(new Error('Image failed to load within timeout'));
            return;
          }
          // Longer delay if image is still loading
          setTimeout(validateAndProcess, isInlineData ? 80 : 200);
          return;
        }
       
        // Check dimensions - sometimes naturalWidth/Height take time to populate
        if (img.naturalWidth === 0 || img.naturalHeight === 0) {
          if (attempts >= maxAttempts) {
            reject(new Error('Image has invalid dimensions after waiting'));
            return;
          }
          // Wait a bit longer if dimensions aren't ready yet
          setTimeout(validateAndProcess, 150);
          return;
        }
       
        // Verify we can actually read pixel data - this is the most important check
        const testCanvas = document.createElement('canvas');
        testCanvas.width = img.naturalWidth;
        testCanvas.height = img.naturalHeight;
        const testCtx = testCanvas.getContext('2d');
       
        try {
          // Try to draw the image
          testCtx.clearRect(0, 0, testCanvas.width, testCanvas.height);
          testCtx.drawImage(img, 0, 0);
         
          // Try reading from a few different locations to ensure full decode
          let canRead = true;
          let pixelDataOk = 0;
         
          // Check top-left
          try {
            const testData1 = testCtx.getImageData(0, 0, 1, 1);
            if (testData1 && testData1.data && testData1.data.length >= 4) {
              pixelDataOk++;
            }
          } catch (e) {
            canRead = false;
          }
         
          // Check center
          try {
            const centerX = Math.floor(img.naturalWidth / 2);
            const centerY = Math.floor(img.naturalHeight / 2);
            const testData2 = testCtx.getImageData(centerX, centerY, 1, 1);
            if (testData2 && testData2.data && testData2.data.length >= 4) {
              pixelDataOk++;
            }
          } catch (e) {
            canRead = false;
          }
         
          // Check bottom-right
          try {
            const testData3 = testCtx.getImageData(img.naturalWidth - 1, img.naturalHeight - 1, 1, 1);
            if (testData3 && testData3.data && testData3.data.length >= 4) {
              pixelDataOk++;
            }
          } catch (e) {
            // Cross-origin images might fail here, that's OK if other checks pass
          }
         
          // Need at least one successful pixel read
          if (canRead && pixelDataOk > 0) {
            console.log(`Image validated after ${elapsedTime.toFixed(2)}s (${attempts} attempts)`);
            // Image is fully loaded and pixel data is accessible
            resolve(img);
            return;
          }
         
          // If we can't read yet, wait longer and try again
          if (attempts >= maxAttempts) {
            reject(new Error('Cannot read image pixel data - image may be blocked or corrupted'));
            return;
          }
         
          // Increase delay progressively for large images
          const delay = Math.min(250, (isInlineData ? 60 : 100) + (attempts * 10));
          setTimeout(validateAndProcess, delay);
          return;
         
        } catch (e) {
          // Some cross-origin images might throw errors but still work
          // Give it more time if it's a CORS issue
          if (e.name === 'SecurityError' || e.message.includes('tainted')) {
            if (attempts >= maxAttempts) {
              reject(new Error('Image blocked by CORS policy. Image must be served with proper CORS headers.'));
              return;
            }
            setTimeout(validateAndProcess, 200);
            return;
          }
         
          if (attempts >= maxAttempts) {
            reject(new Error('Error accessing image pixel data: ' + e.message));
            return;
          }
         
          // Wait a bit longer on errors
          setTimeout(validateAndProcess, 200);
        }
      }
     
      // Start validation after a small delay to let onload settle
      setTimeout(validateAndProcess, isInlineData ? 10 : 50);
    });
  };
 
  image.onload = function () {
    // Wait for image to be fully loaded and validated before processing
    loadAndValidateImage(image).then(async function(validatedImage) {
      // Double-check image dimensions before processing
      if (validatedImage.naturalWidth > 0 && validatedImage.naturalHeight > 0) {
        let flippedImage;
        try {
          // Create flipped copy BEFORE any processing so textures are independent
          flippedImage = await createFlippedImage(validatedImage);
        } catch (flipErr) {
          console.error('Failed to create flipped image, using original for both sides:', flipErr);
          flippedImage = validatedImage;
        }

        const processFlippedTexture = () => {
          processImage(flippedImage, alphaTolerance, {
            paddingFraction: paddingFraction,
            smoothPercent: smoothPercent,
            secondSmoothPercent: secondSmoothPercent,
            magnetPower: magnetPower,
            centerThickness: centerThickness,
            uploadTexture: function(canvas) {
              gl.bindTexture(gl.TEXTURE_2D, backTex);
              gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
              gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
              gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
              gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
              gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            },
            onComplete: function() {
              flippedTexture = backTex;
              flippedReady = true;
              drawScene();
            }
          });
        };
        const scheduleFlippedTexture = () => {
          if (typeof requestIdleCallback === 'function') {
            requestIdleCallback(() => processFlippedTexture(), { timeout: 300 });
          } else {
            setTimeout(processFlippedTexture, 0);
          }
        };

        // Process original image first to build the shape data and front texture
        processImage(validatedImage, alphaTolerance, {
          paddingFraction: paddingFraction,
          smoothPercent: smoothPercent,
          secondSmoothPercent: secondSmoothPercent,
          magnetPower: magnetPower,
          centerThickness: centerThickness,
          uploadTexture: function(canvas) {
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          },
          onComplete: function(finalShape, outerContour, centerThick, paddedWidth, paddedHeight) {
            buffers = initBuffers(gl, finalShape, outerContour, centerThick, paddedWidth, paddedHeight);
            currentImageSource = url;

            // Create dimension guide buffers after model buffers are ready
            if (modelBoundingBox) {
              dimensionTextBuffers = createDimensionTextBuffers(gl, modelBoundingBox);
              referenceLineBuffers = createReferenceLineBuffers(gl, modelBoundingBox);
              dimensionGuideBuffers = createDimensionGuideBuffers(gl, modelBoundingBox);
            }

            if (!lightConeBuffer) {
              lightConeBuffer = createLightCone(gl, 0.8, 2.0, 16);
            }
            initPlainTexture();
            drawScene();
            // Start intro animation
            currentScale = 0;
            isAnimating = true;
            animStartTime = performance.now();

            // Process flipped image after original geometry is ready
            scheduleFlippedTexture();
            markGenerationDone();
          }
        });
      } else {
        console.error('Image has invalid dimensions:', validatedImage.naturalWidth, validatedImage.naturalHeight);
        markGenerationDone();
      }
    }).catch(function(error) {
      console.error('Failed to load and validate image after waiting:', error);
     
      // Provide more helpful error messages
      let errorMessage = 'Error: Failed to load image. ';
      if (error.message.includes('timeout')) {
        errorMessage += 'The image took too long to load. Please try a smaller image or check your internet connection.';
      } else if (error.message.includes('CORS')) {
        errorMessage += 'The image is blocked by browser security. Please ensure the image server allows cross-origin access.';
      } else if (error.message.includes('dimensions')) {
        errorMessage += 'The image dimensions could not be determined. The image may be corrupted.';
      } else {
        errorMessage += 'Please ensure the image is fully loaded and try again.';
      }
     
      console.log(errorMessage);
      markGenerationDone();
    });
  };
 
  image.onerror = function () {
    console.error('Failed to load image:', url);
    // Try to continue with a placeholder or show error
    markGenerationDone();
  };
 
  image.src = url;
  return tex;
}
function loadLabelTexture(gl, url) {
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  const pixel = new Uint8Array([255, 255, 255, 255]);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
  const image = new Image();
  image.onload = function () {
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    // Redraw scene when texture loads
    if (buffers) {
      drawScene();
    }
  };
  image.onerror = function () {
    console.warn('Failed to load label texture:', url);
  };
  image.src = url;
  return tex;
}
// Image to shape processing functions have been moved to generateshape.js
function computeBulgeProfile(frac, angle) {
  const clamped = Math.min(Math.max(frac, 0), 1);
  // Compute signed area to determine orientation (CCW if >0)
  let signedArea = 0;
  for (let i = 0; i < nn; i++) {
    const j = (i + 1) % nn;
    signedArea += smoothed[i].x * smoothed[j].y;
    signedArea -= smoothed[j].x * smoothed[i].y;
  }
  signedArea /= 2;
  const isCCW = signedArea > 0;
  // If expanding, first push points outward along their normals
  if (expand > 0) {
    for (let i = 0; i < nn; i++) {
      const prev = smoothed[(i - 1 + nn) % nn];
      const curr = smoothed[i];
      const next = smoothed[(i + 1) % nn];
      const dx1 = curr.x - prev.x;
      const dy1 = curr.y - prev.y;
      const len1 = Math.max(Math.sqrt(dx1 * dx1 + dy1 * dy1), 1e-6);
      const dx2 = next.x - curr.x;
      const dy2 = next.y - curr.y;
      const len2 = Math.max(Math.sqrt(dx2 * dx2 + dy2 * dy2), 1e-6);
      let nx1, ny1, nx2, ny2;
      if (isCCW) {
        // CCW: outward is right perp (dy, -dx)
        nx1 = dy1 / len1;
        ny1 = -dx1 / len1;
        nx2 = dy2 / len2;
        ny2 = -dx2 / len2;
      } else {
        // CW: outward is left perp (-dy, dx)
        nx1 = -dy1 / len1;
        ny1 = dx1 / len1;
        nx2 = -dy2 / len2;
        ny2 = dx2 / len2;
      }
      let nx = (nx1 + nx2) * 0.5;
      let ny = (ny1 + ny2) * 0.5;
      const nlen = Math.sqrt(nx * nx + ny * ny);
      if (nlen > 0) {
        nx /= nlen;
        ny /= nlen;
      }
      smoothed[i].x = curr.x + nx * expand;
      smoothed[i].y = curr.y + ny * expand;
    }
  }
  // Now smooth the (possibly expanded) contour with weighted average (less shrinkage)
  for (let iter = 0; iter < iterations; iter++) {
    let newSm = [];
    for (let i = 0; i < nn; i++) {
      const prev = smoothed[(i - 1 + nn) % nn];
      const curr = smoothed[i];
      const next = smoothed[(i + 1) % nn];
      const avgx = (prev.x + 2 * curr.x + next.x) / 4;
      const avgy = (prev.y + 2 * curr.y + next.y) / 4;
      newSm.push({ x: avgx, y: avgy });
    }
    smoothed = newSm;
  }
  return smoothed;
}
function resamplePolygon(points, targetCount) {
  const n = points.length;
  if (n === 0 || targetCount <= 0) return [];
  if (n === targetCount) {
    return points.map(p => ({ x: p.x, y: p.y }));
  }
  const lengths = new Array(n);
  let perimeter = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const dx = points[j].x - points[i].x;
    const dy = points[j].y - points[i].y;
    const len = Math.hypot(dx, dy);
    lengths[i] = len;
    perimeter += len;
  }
  if (perimeter < 1e-6) {
    return Array.from({ length: targetCount }, () => ({ x: points[0].x, y: points[0].y }));
  }
  const step = perimeter / targetCount;
  const resampled = [];
  let segmentIndex = 0;
  let accumulated = 0;
  for (let k = 0; k < targetCount; k++) {
    const target = k * step;
    while (target > accumulated + lengths[segmentIndex]) {
      accumulated += lengths[segmentIndex];
      segmentIndex = (segmentIndex + 1) % n;
    }
    const segmentLength = lengths[segmentIndex];
    const fraction = segmentLength < 1e-6 ? 0 : (target - accumulated) / segmentLength;
    const p0 = points[segmentIndex];
    const p1 = points[(segmentIndex + 1) % n];
    resampled.push({
      x: p0.x + (p1.x - p0.x) * fraction,
      y: p0.y + (p1.y - p0.y) * fraction
    });
  }
  return resampled;
}
// Calculate curvature at a point (how much the shape bends inward/outward)
function calculateCurvature(points, i, centerX, centerY) {
  const n = points.length;
  const prev = points[(i - 1 + n) % n];
  const curr = points[i];
  const next = points[(i + 1) % n];
 
  // Vector from prev to curr
  const v1x = curr.x - prev.x;
  const v1y = curr.y - prev.y;
  // Vector from curr to next
  const v2x = next.x - curr.x;
  const v2y = next.y - curr.y;
 
  // Normalize
  const len1 = Math.hypot(v1x, v1y);
  const len2 = Math.hypot(v2x, v2y);
  if (len1 < 1e-6 || len2 < 1e-6) return 0;
 
  const nv1x = v1x / len1;
  const nv1y = v1y / len1;
  const nv2x = v2x / len2;
  const nv2y = v2y / len2;
 
  // Angle between vectors
  const dot = nv1x * nv2x + nv1y * nv2y;
  const angle = Math.acos(Math.max(-1, Math.min(1, dot)));
 
  // Distance from center (indicates how deep an indentation is)
  const dx = curr.x - centerX;
  const dy = curr.y - centerY;
  const distFromCenter = Math.hypot(dx, dy);
 
  // Check if it's an inward bend (indentation)
  const toCenterX = centerX - curr.x;
  const toCenterY = centerY - curr.y;
  const perpX = -nv1y;
  const perpY = nv1x;
  const isInward = (toCenterX * perpX + toCenterY * perpY) > 0;
 
  // Higher curvature value for sharp inward bends (deep gaps)
  if (isInward && angle > Math.PI / 3) {
    return angle * (1 + distFromCenter * 0.01);
  }
  return angle;
}
function buildBinaryGrid(image, tolerance) {
  // Use naturalWidth/naturalHeight to ensure we get the actual image dimensions
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
 
  if (width === 0 || height === 0) {
    console.error('Invalid image dimensions in buildBinaryGrid:', width, height);
    return { grid: new Uint8Array(0), width: 0, height: 0 };
  }
  // Validate image is fully loaded before processing
  if (!image.complete) {
    console.error('Image is not complete in buildBinaryGrid');
    return { grid: new Uint8Array(0), width: 0, height: 0 };
  }
 
  const offCanvas = document.createElement('canvas');
  offCanvas.width = width;
  offCanvas.height = height;
  const ctx = offCanvas.getContext('2d');
 
  // Clear canvas first to ensure transparent pixels are truly transparent
  ctx.clearRect(0, 0, width, height);
 
  // Draw the image
  ctx.drawImage(image, 0, 0);
 
  // Get image data - this contains RGBA values
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const grid = new Uint8Array(width * height);
 
  // Only mark pixels as non-transparent if alpha channel is above tolerance
  // This ensures ONLY non-transparent pixels are used for shape generation
  let nonTransparentCount = 0;
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3]; // Alpha channel (transparency)
    const isNonTransparent = alpha > tolerance;
    grid[i / 4] = isNonTransparent ? 1 : 0;
    if (isNonTransparent) {
      nonTransparentCount++;
    }
  }
 
  // Validate that we found non-transparent pixels
  if (nonTransparentCount === 0) {
    console.warn('No non-transparent pixels found in image (all pixels are transparent)');
    // Return empty grid instead of falling back to bounding box
    return { grid: new Uint8Array(width * height), width, height };
  }
 
  console.log(`Found ${nonTransparentCount} non-transparent pixels out of ${width * height} total pixels`);
  return { grid, width, height };
}
function dilateBinary(grid, width, height, iterations) {
  if (iterations <= 0) return grid.slice();
  let src = grid.slice();
  let dst = new Uint8Array(width * height);
  for (let iter = 0; iter < iterations; iter++) {
    dst.set(src);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (!src[y * width + x]) continue;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
              dst[ny * width + nx] = 1;
            }
          }
        }
      }
    }
    const temp = src;
    src = dst;
    dst = temp;
  }
  return src;
}
function erodeBinary(grid, width, height, iterations) {
  if (iterations <= 0) return grid.slice();
  let src = grid.slice();
  let dst = new Uint8Array(width * height);
  for (let iter = 0; iter < iterations; iter++) {
    dst.set(src);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (!src[y * width + x]) continue;
        let allOn = true;
        for (let dy = -1; dy <= 1 && allOn; dy++) {
          for (let dx = -1; dx <= 1 && allOn; dx++) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx < 0 || nx >= width || ny < 0 || ny >= height || !src[ny * width + nx]) {
              allOn = false;
            }
          }
        }
        dst[y * width + x] = allOn ? 1 : 0;
      }
    }
    const temp = src;
    src = dst;
    dst = temp;
  }
  return src;
}
function floodFill(grid, visited, width, height, startX, startY) {
  const pixels = [];
  const stack = [[startX, startY]];
  visited[startY * width + startX] = 1;
  while (stack.length) {
    const [x, y] = stack.pop();
    pixels.push({ x, y });
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const nx = x + dx;
        const ny = y + dy;
        if (nx >= 0 && nx < width && ny >= 0 && ny < height && grid[ny * width + nx] === 1 && !visited[ny * width + nx]) {
          visited[ny * width + nx] = 1;
          stack.push([nx, ny]);
        }
      }
    }
  }
  return pixels;
}
function drawLine(grid, width, height, x0, y0, x1, y1) {
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  while (true) {
    if (x0 >= 0 && x0 < width && y0 >= 0 && y0 < height) {
      grid[y0 * width + x0] = 1;
      // Make thicker (3x3 around each point)
      for (let dy2 = -1; dy2 <= 1; dy2++) {
        for (let dx2 = -1; dx2 <= 1; dx2++) {
          const nx = x0 + dx2;
          const ny = y0 + dy2;
          if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
            grid[ny * width + nx] = 1;
          }
        }
      }
    }
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x0 += sx;
    }
    if (e2 < dx) {
      err += dx;
      y0 += sy;
    }
  }
}
function connectComponents(grid, width, height) {
  const visited = new Uint8Array(width * height);
  const components = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (grid[idx] === 1 && !visited[idx]) {
        const pixels = floodFill(grid, visited, width, height, x, y);
        if (pixels.length > 0) {
          let sumX = 0, sumY = 0;
          for (let p of pixels) {
            sumX += p.x;
            sumY += p.y;
          }
          const cx = sumX / pixels.length;
          const cy = sumY / pixels.length;
          components.push({ centroidX: cx, centroidY: cy });
        }
      }
    }
  }
  if (components.length <= 1) return;
  const edges = [];
  for (let i = 0; i < components.length; i++) {
    for (let j = i + 1; j < components.length; j++) {
      const dx = components[i].centroidX - components[j].centroidX;
      const dy = components[i].centroidY - components[j].centroidY;
      const dist = Math.hypot(dx, dy);
      edges.push({ i, j, dist });
    }
  }
  edges.sort((a, b) => a.dist - b.dist);
  let parent = [];
  for (let i = 0; i < components.length; i++) parent[i] = i;
  function find(p) {
    if (parent[p] !== p) parent[p] = find(parent[p]);
    return parent[p];
  }
  function union(a, b) {
    let pa = find(a), pb = find(b);
    if (pa !== pb) {
      parent[pa] = pb;
      return true;
    }
    return false;
  }
  for (let edge of edges) {
    if (union(edge.i, edge.j)) {
      const cx0 = Math.round(components[edge.i].centroidX);
      const cy0 = Math.round(components[edge.i].centroidY);
      const cx1 = Math.round(components[edge.j].centroidX);
      const cy1 = Math.round(components[edge.j].centroidY);
      drawLine(grid, width, height, cx0, cy0, cx1, cy1);
    }
  }
}
function traceContourFromGrid(grid, width, height) {
  // First, find all non-transparent pixels to ensure we capture everything
  const allNonTransparentPixels = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (grid[y * width + x] === 1) {
        allNonTransparentPixels.push({ x, y });
      }
    }
  }
 
  if (allNonTransparentPixels.length === 0) return [];
 
  // Find the bounding box of ALL non-transparent pixels
  // This ensures we capture everything, not just one connected component
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let p of allNonTransparentPixels) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
 
  // Now find all edge pixels within this bounding box
  const allEdgePixels = [];
  const edgeSet = new Set();
 
  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      if (grid[y * width + x] === 1) {
        // Check if this pixel has at least one transparent neighbor (making it an edge pixel)
        let isEdge = false;
        for (let dy = -1; dy <= 1 && !isEdge; dy++) {
          for (let dx = -1; dx <= 1 && !isEdge; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = x + dx;
            const ny = y + dy;
            if (nx < 0 || nx >= width || ny < 0 || ny >= height || grid[ny * width + nx] === 0) {
              isEdge = true;
              const key = `${x},${y}`;
              if (!edgeSet.has(key)) {
                allEdgePixels.push({ x, y });
                edgeSet.add(key);
              }
              break;
            }
          }
        }
      }
    }
  }
 
  if (allEdgePixels.length === 0) {
    // Fallback to bounding box if no edge pixels found
    if (minX <= maxX && minY <= maxY) {
      return [
        { x: minX, y: minY },
        { x: maxX, y: minY },
        { x: maxX, y: maxY },
        { x: minX, y: maxY }
      ];
    }
    return [];
  }
 
  // Find the top-leftmost edge pixel to start
  let startX = width, startY = height;
  for (let p of allEdgePixels) {
    if (p.y < startY || (p.y === startY && p.x < startX)) {
      startX = p.x;
      startY = p.y;
    }
  }
 
  // Use Moore neighborhood tracing, but allow "jumping" to nearby edge pixels
  // if we can't find a directly connected one - with increased jump distance
  const dirs = [[1, 0], [1, -1], [0, -1], [-1, -1], [-1, 0], [-1, 1], [0, 1], [1, 1]];
  const contour = [];
  let x = startX;
  let y = startY;
 
  // Find initial direction
  let dir = 0;
  for (let d = 0; d < 8; d++) {
    const nx = x + dirs[d][0];
    const ny = y + dirs[d][1];
    if (nx < 0 || nx >= width || ny < 0 || ny >= height || grid[ny * width + nx] === 0) {
      dir = (d + 4) % 8;
      break;
    }
  }
 
  contour.push({ x, y });
  const visited = new Set();
  visited.add(`${x},${y}`);
 
  let steps = 0;
  const maxSteps = width * height * 4;
  let lastFoundX = x, lastFoundY = y;
  let consecutiveFailures = 0;
  const maxConsecutiveFailures = 100; // Allow more attempts before giving up
 
  while (steps < maxSteps) {
    steps++;
    let found = false;
   
    // First, try to find directly connected edge pixel (Moore neighborhood)
    for (let d = 0; d < 8; d++) {
      const nd = (dir + d + 6) % 8;
      const nx = x + dirs[nd][0];
      const ny = y + dirs[nd][1];
     
      if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
        const key = `${nx},${ny}`;
        if (edgeSet.has(key)) {
          x = nx;
          y = ny;
          dir = nd;
          lastFoundX = x;
          lastFoundY = y;
          if (!visited.has(key)) {
            contour.push({ x, y });
            visited.add(key);
          }
          found = true;
          consecutiveFailures = 0;
          break;
        }
      }
    }
   
    // If no directly connected edge pixel found, try to find nearest edge pixel
    // with increased jump distance to handle larger gaps between disconnected shapes
    if (!found) {
      let minDist = Infinity;
      let nearestX = -1, nearestY = -1;
      // Calculate maximum jump distance based on image size to handle larger gaps
      const maxJumpDist = Math.max(10, Math.min(width, height) / 10);
     
      for (let p of allEdgePixels) {
        const key = `${p.x},${p.y}`;
        if (!visited.has(key)) {
          const dist = Math.hypot(p.x - x, p.y - y);
          // Increased jump distance to capture disconnected shapes
          if (dist < minDist && dist <= maxJumpDist && dist > 0) {
            minDist = dist;
            nearestX = p.x;
            nearestY = p.y;
          }
        }
      }
     
      if (nearestX !== -1 && nearestY !== -1) {
        // Add intermediate points to bridge the gap
        const bridgeSteps = Math.max(2, Math.ceil(minDist));
        for (let i = 1; i <= bridgeSteps; i++) {
          const t = i / bridgeSteps;
          const bridgeX = Math.round(x + (nearestX - x) * t);
          const bridgeY = Math.round(y + (nearestY - y) * t);
          const bridgeKey = `${bridgeX},${bridgeY}`;
          if (!visited.has(bridgeKey)) {
            contour.push({ x: bridgeX, y: bridgeY });
            visited.add(bridgeKey);
          }
        }
        x = nearestX;
        y = nearestY;
        // Update direction based on movement
        const dx = x - lastFoundX;
        const dy = y - lastFoundY;
        if (Math.abs(dx) > Math.abs(dy)) {
          dir = dx > 0 ? 0 : 4;
        } else {
          dir = dy > 0 ? 6 : 2;
        }
        lastFoundX = x;
        lastFoundY = y;
        found = true;
        consecutiveFailures = 0;
      } else {
        consecutiveFailures++;
        // If we can't find any nearby unvisited pixels, check if there are any unvisited pixels at all
        if (consecutiveFailures >= maxConsecutiveFailures) {
          // Check if we've visited all edge pixels
          let hasUnvisited = false;
          for (let p of allEdgePixels) {
            if (!visited.has(`${p.x},${p.y}`)) {
              hasUnvisited = true;
              // Try to jump to this unvisited pixel even if far away
              const dist = Math.hypot(p.x - x, p.y - y);
              if (dist < minDist) {
                minDist = dist;
                nearestX = p.x;
                nearestY = p.y;
              }
            }
          }
          if (nearestX !== -1 && nearestY !== -1) {
            // Force jump to unvisited pixel
            const bridgeSteps = Math.max(2, Math.ceil(minDist));
            for (let i = 1; i <= bridgeSteps; i++) {
              const t = i / bridgeSteps;
              const bridgeX = Math.round(x + (nearestX - x) * t);
              const bridgeY = Math.round(y + (nearestY - y) * t);
              const bridgeKey = `${bridgeX},${bridgeY}`;
              if (!visited.has(bridgeKey)) {
                contour.push({ x: bridgeX, y: bridgeY });
                visited.add(bridgeKey);
              }
            }
            x = nearestX;
            y = nearestY;
            const dx = x - lastFoundX;
            const dy = y - lastFoundY;
            if (Math.abs(dx) > Math.abs(dy)) {
              dir = dx > 0 ? 0 : 4;
            } else {
              dir = dy > 0 ? 6 : 2;
            }
            lastFoundX = x;
            lastFoundY = y;
            found = true;
            consecutiveFailures = 0;
          } else if (!hasUnvisited) {
            // All pixels visited, we're done
            break;
          }
        }
      }
    }
   
    if (!found) {
      // If we still can't find anything, check if we've visited all pixels
      let allVisited = true;
      for (let p of allEdgePixels) {
        if (!visited.has(`${p.x},${p.y}`)) {
          allVisited = false;
          break;
        }
      }
      if (allVisited) break;
    }
   
    // Check if we've completed a loop and visited all edge pixels
    if (contour.length > 3 && Math.abs(x - startX) <= 2 && Math.abs(y - startY) <= 2) {
      // Verify we've visited all edge pixels
      let allVisited = true;
      for (let p of allEdgePixels) {
        if (!visited.has(`${p.x},${p.y}`)) {
          allVisited = false;
          break;
        }
      }
      if (allVisited) {
        break;
      } else {
        // Continue tracing from an unvisited pixel
        let nextStart = null;
        for (let p of allEdgePixels) {
          if (!visited.has(`${p.x},${p.y}`)) {
            nextStart = p;
            break;
          }
        }
        if (nextStart) {
          x = nextStart.x;
          y = nextStart.y;
          startX = x;
          startY = y;
          lastFoundX = x;
          lastFoundY = y;
          // Find new initial direction
          for (let d = 0; d < 8; d++) {
            const nx = x + dirs[d][0];
            const ny = y + dirs[d][1];
            if (nx < 0 || nx >= width || ny < 0 || ny >= height || grid[ny * width + nx] === 0) {
              dir = (d + 4) % 8;
              break;
            }
          }
        } else {
          break;
        }
      }
    }
  }
 
  // If contour is too short or doesn't capture all pixels, return bounding box
  // But first check if we captured a reasonable amount
  const visitedRatio = visited.size / allEdgePixels.length;
  if (contour.length < 3 || visitedRatio < 0.5) {
    // Return bounding box of all non-transparent pixels to ensure nothing is lost
    if (minX <= maxX && minY <= maxY) {
      return [
        { x: minX, y: minY },
        { x: maxX, y: minY },
        { x: maxX, y: maxY },
        { x: minX, y: maxY }
      ];
    }
  }
 
  return contour;
}
function computeBulgeProfile(frac, angle) {
  const clamped = Math.min(Math.max(frac, 0), 1);
  const baseHeight = 1 - Math.pow(clamped, BULGE_EASE_POWER);
  const boosted = baseHeight + BULGE_BOOST * baseHeight * (1 - baseHeight);
  const edgeFloor = edgeThickness / centerThickness;
  let height = edgeFloor + (1 - edgeFloor) * boosted;
 
  // INFLATION MECHANISM: Simulate air pressure to prevent weird objects
  // Apply uniform inflation pressure that creates smooth, inflated shape
  // The inflation is applied uniformly but with slight emphasis at edges for smooth closure
  const edgeDistance = 1 - clamped; // Distance from edge (1 at center, 0 at edge)
  // Inflation creates a smooth outward pressure that prevents artifacts
  // Use a smooth curve that peaks slightly toward edges but maintains overall inflation
  const inflationFactor = inflationPressure * (0.7 + 0.3 * Math.pow(edgeDistance, 1.5));
  height += inflationFactor * (1 - clamped * 0.5); // Add inflation with smooth distribution
 
  // Add balloon-like outward curve at edges to prevent inward shadows
  // Create a smooth outward bulge near the edge (like an inflated balloon/pillow)
  const balloonEffect = 0.12; // Increased to 12% for stronger outward curve at edges
  // Apply balloon effect most strongly at edges, fading toward center
  // Use stronger curve near edges to prevent shadows
  const balloonFactor = Math.pow(edgeDistance, 1.5) * balloonEffect; // Stronger near edges
  height += balloonFactor * clamped; // Add outward curve
 
  // Add cloth-like folds using Perlin noise for more random and realistic wrinkles
  const cartX = clamped * Math.cos(angle);
  const cartY = clamped * Math.sin(angle);
  const noiseScale = foldFrequency;
  const noiseValue = fbm(cartX * noiseScale, cartY * noiseScale, 0);
  let foldOffset = foldAmplitude * (2 * noiseValue - 1) * clamped;
  // Fade out folds near the edge for smoother closing (no twisting at seams)
  // Enhanced fade: completely eliminate folds at edges for smooth closure
  const edgeFade = Math.pow(1 - clamped, 2.5); // Stronger fade, completely zero at edge
  foldOffset *= edgeFade * 4 * clamped; // Peak in mid, 0 at edges
  height += foldOffset;
  height = Math.max(edgeFloor, Math.min(1, height)); // Clamp to valid range
 
  // Calculate derivatives for smooth normals
  const dBase = -BULGE_EASE_POWER * Math.pow(clamped, BULGE_EASE_POWER - 1);
  let derivativeFactor = 1 + BULGE_BOOST * (1 - 2 * baseHeight);
  derivativeFactor = Math.max(derivativeFactor, 0); // Ensure non-negative to prevent inward bending
  const dHeight = (1 - edgeFloor) * dBase * derivativeFactor;
 
  // Add derivative of inflation effect
  // d/dx of inflationFactor * (1 - clamped * 0.5) where inflationFactor = inflationPressure * (0.7 + 0.3 * (1-x)^1.5)
  // Using product rule and chain rule
  const dInflationFactor = -0.3 * 1.5 * inflationPressure * Math.pow(edgeDistance, 0.5);
  const inflationValue = inflationPressure * (0.7 + 0.3 * Math.pow(edgeDistance, 1.5));
  const dHeightInflation = dInflationFactor * (1 - clamped * 0.5) - inflationValue * 0.5;
 
  // Add derivative of balloon effect (creates smooth outward curve)
  // d/dx of (1-x)^1.5 * balloonEffect * x = balloonEffect * ((1-x)^1.5 - 1.5*x*(1-x)^0.5)
  const dBalloonFactor = balloonEffect * (Math.pow(edgeDistance, 1.5) - 1.5 * clamped * Math.pow(edgeDistance, 0.5));
  const dHeightBalloon = dBalloonFactor;
 
  // Approximate derivative for folds using finite difference
  const epsilon = 1e-5;
  const clamped_eps = Math.min(Math.max(frac + epsilon, 0), 1);
  const cartX_eps = clamped_eps * Math.cos(angle);
  const cartY_eps = clamped_eps * Math.sin(angle);
  const noise_eps = fbm(cartX_eps * noiseScale, cartY_eps * noiseScale, 0);
  let fold_eps = foldAmplitude * (2 * noise_eps - 1) * clamped_eps;
  const edgeFade_eps = Math.pow(1 - clamped_eps, 2.5);
  fold_eps *= edgeFade_eps * 4 * clamped_eps;
  const dFold = (fold_eps - foldOffset) / epsilon;
 
  return {
    height,
    dHeight: dHeight + dFold + dHeightBalloon + dHeightInflation
  };
}
// Image to shape processing functions have been moved to generateshape.js
function initBuffers(gl, contour, outerContour, thickness, paddedWidth, paddedHeight) {
  if (contour.length < 3 || outerContour.length !== contour.length) return null;
  const n = contour.length;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let p of outerContour) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
    return null;
  }
  const spanX = maxX - minX;
  const spanY = maxY - minY;
  const baseSize = Math.max(spanX, spanY);
  const normalizeScale = baseSize > 0 ? baseSize * 1.05 : 1;
  // Store model dimensions for overlay guides
  modelWidth = spanX;
  modelHeight = spanY;
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const appliedScale = 1.0;
  // Scale down to 80% of canvas to leave room for guides
  const canvasScale = 1.6; // 2.0 * 0.8 = 1.6 (80% of canvas)
  for (let p of contour) {
    const baseNx = canvasScale * (p.x - centerX) / normalizeScale;
    const baseNy = -canvasScale * (p.y - centerY) / normalizeScale;
    p.nx = baseNx * appliedScale;
    p.ny = baseNy * appliedScale;
    p.u = p.x / paddedWidth;
    p.v = p.y / paddedHeight;
  }
  for (let p of outerContour) {
    const baseNx = canvasScale * (p.x - centerX) / normalizeScale;
    const baseNy = -canvasScale * (p.y - centerY) / normalizeScale;
    p.nx = baseNx * appliedScale;
    p.ny = baseNy * appliedScale;
    p.u = p.x / paddedWidth;
    p.v = p.y / paddedHeight;
  }

  // Check orientation and reverse if necessary to ensure CCW
  let signedArea = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    signedArea += contour[i].nx * contour[j].ny - contour[j].nx * contour[i].ny;
  }
  if (signedArea < 0) {
    contour.reverse();
    outerContour.reverse();
  }

  // Actual Perlin noise generator
  let sumNx = 0, sumNy = 0, sumU = 0, sumV = 0;
  for (let p of contour) {
    sumNx += p.nx;
    sumNy += p.ny;
    sumU += p.u;
    sumV += p.v;
  }
  const centerNx = sumNx / n;
  const centerNy = sumNy / n;
  const centerU = sumU / n;
  const centerV = sumV / n;
  const radialData = contour.map((p, i) => ({
    nx: p.nx,
    ny: p.ny,
    u: p.u,
    v: p.v,
    dx: p.nx - centerNx,
    dy: p.ny - centerNy,
    du: p.u - centerU,
    dv: p.v - centerV,
    angle: Math.atan2(p.ny - centerNy, p.nx - centerNx)
  }));
  // Positions, normals, texture coords
  const positions = [];
  const normals = [];
  const textureCoords = [];
  const indices = [];
  const interiorRingCount = INTERIOR_RING_COUNT;
  // === FRONT FACE INNER (TEXTURE) WITH VOLUMETRIC RINGS ===
  const frontInterior = [];
  const centerProfile = computeBulgeProfile(0, 0);
  const centerZ = (thickness / 2) * centerProfile.height;
  const centerOffset = positions.length / 3;
  positions.push(centerNx, centerNy, centerZ);
  normals.push(0, 0, 1);
  textureCoords.push(centerU, centerV);
  frontInterior.push({ offset: centerOffset, count: 1 });
  for (let level = 1; level <= interiorRingCount; level++) {
    const radial = level / interiorRingCount;
    const ringOffset = positions.length / 3;
    for (let i = 0; i < n; i++) {
      const data = radialData[i];
      const profile = computeBulgeProfile(radial, data.angle);
      const z = (thickness / 2) * profile.height;
      const dzdf = (thickness / 2) * profile.dHeight;
      let nx_prof = -dzdf;
      let nz_prof = 1.0;
      const invLen = 1 / Math.sqrt(nx_prof * nx_prof + nz_prof * nz_prof);
      nx_prof *= invLen;
      nz_prof *= invLen;
      const px = centerNx + data.dx * radial;
      const py = centerNy + data.dy * radial;
      const pu = centerU + data.du * radial;
      const pv = centerV + data.dv * radial;
      positions.push(px, py, z);
      const dirLen = Math.hypot(data.dx, data.dy);
      let dirx = 0, diry = 0;
      if (dirLen > 1e-6) {
        dirx = data.dx / dirLen;
        diry = data.dy / dirLen;
      }
      normals.push(dirx * nx_prof, diry * nx_prof, nz_prof);
      textureCoords.push(pu, pv);
    }
    frontInterior.push({ offset: ringOffset, count: n });
  }
  if (frontInterior.length > 1) {
    const firstRing = frontInterior[1].offset;
    const centerIdx = frontInterior[0].offset;
    for (let i = 0; i < n; i++) {
      const i2 = (i + 1) % n;
      indices.push(centerIdx, firstRing + i, firstRing + i2);
    }
  }
  for (let level = 1; level < frontInterior.length - 1; level++) {
    const thisRing = frontInterior[level];
    const nextRing = frontInterior[level + 1];
    for (let i = 0; i < n; i++) {
      const i2 = (i + 1) % n;
      indices.push(thisRing.offset + i, nextRing.offset + i, nextRing.offset + i2);
      indices.push(thisRing.offset + i, nextRing.offset + i2, thisRing.offset + i2);
    }
  }
  const frontEdgeOffset = frontInterior[frontInterior.length - 1].offset;
  const frontInnerCount = indices.length;
  // === BACK FACE INNER (PRE-FLIPPED TEXTURE) ===
  const backInterior = [];
  const backCenterProfile = computeBulgeProfile(0, 0);
  const backCenterZ = -(thickness / 2) * backCenterProfile.height;
  const backCenterOffset = positions.length / 3;
  positions.push(centerNx, centerNy, backCenterZ);
  normals.push(0, 0, -1);
  textureCoords.push(centerU, centerV);
  backInterior.push({ offset: backCenterOffset, count: 1 });
  for (let level = 1; level <= interiorRingCount; level++) {
    const radial = level / interiorRingCount;
    const ringOffset = positions.length / 3;
    for (let i = 0; i < n; i++) {
      const data = radialData[i];
      const profile = computeBulgeProfile(radial, data.angle);
      const z = -(thickness / 2) * profile.height;
      const dzdf = -(thickness / 2) * profile.dHeight;
      let nx_prof = -dzdf;
      let nz_prof = -1.0;
      const invLen = 1 / Math.sqrt(nx_prof * nx_prof + nz_prof * nz_prof);
      nx_prof *= invLen;
      nz_prof *= invLen;
      const px = centerNx + data.dx * radial;
      const py = centerNy + data.dy * radial;
      const pu = centerU + data.du * radial;
      const pv = centerV + data.dv * radial;
      positions.push(px, py, z);
      const dirLen = Math.hypot(data.dx, data.dy);
      let dirx = 0, diry = 0;
      if (dirLen > 1e-6) {
        dirx = data.dx / dirLen;
        diry = data.dy / dirLen;
      }
      normals.push(dirx * nx_prof, diry * nx_prof, nz_prof);
      textureCoords.push(pu, pv);
    }
    backInterior.push({ offset: ringOffset, count: n });
  }
  if (backInterior.length > 1) {
    const firstRing = backInterior[1].offset;
    const centerIdx = backInterior[0].offset;
    for (let i = 0; i < n; i++) {
      const i2 = (i + 1) % n;
      indices.push(centerIdx, firstRing + i2, firstRing + i);
    }
  }
  for (let level = 1; level < backInterior.length - 1; level++) {
    const thisRing = backInterior[level];
    const nextRing = backInterior[level + 1];
    for (let i = 0; i < n; i++) {
      const i2 = (i + 1) % n;
      indices.push(thisRing.offset + i, nextRing.offset + i2, nextRing.offset + i);
      indices.push(thisRing.offset + i, thisRing.offset + i2, nextRing.offset + i2);
    }
  }
  const backEdgeOffset = backInterior[backInterior.length - 1].offset;
  const backInnerCount = indices.length - frontInnerCount;
  // === EDGE VOLUME PADDING CLOSURE WITH ROUNDED BORDER ===
  // Direct closure from front edge to back edge with faster, rounded transition
  // Both front and back shapes close at the same location - no gaps, no transparency
  // Center volume is already represented by centerThickness at the 3D center
 
  const edgeVolume = thickness * edgeThickness; // Edge volume distance for padding around edges
  const centerVolume = thickness * centerThickness; // Center volume at 3D center (used in bulge profile)
 
  // frontEdgeOffset and backEdgeOffset are already declared above
  // Create direct closure: front edge -> back edge with rounded transition
  // Use faster curve for steeper slope at edges (rounded border effect)
  // Reduced from 6 to 3 rings for better performance (cuts triangle count in half)
  const numClosureRings = 1; // Optimized for smooth rotation performance
  const closureRings = [];
 
  for (let ringLevel = 0; ringLevel <= numClosureRings; ringLevel++) {
    const ringProgress = ringLevel / numClosureRings; // 0.0 at front edge, 1.0 at back edge
    const ringOffset = positions.length / 3;
   
    // Faster curve profile for rounded border (steeper transition at edges)
    // Power curve creates faster slope near the edge for rounded border effect
    const powerCurve = Math.pow(ringProgress, 1.5); // 1.5 power for faster initial slope
    const smoothFactor = ringProgress * ringProgress * (3 - 2 * ringProgress); // Smoothstep for smooth end
    const zTransition = powerCurve * 0.8 + smoothFactor * 0.2; // Blend for rounded border
   
    for (let i = 0; i < n; i++) {
      const data = radialData[i];
      const edgeX = centerNx + data.dx;
      const edgeY = centerNy + data.dy;
     
      // Keep X/Y at edge position - closure happens in Z direction only
      const paddedX = edgeX;
      const paddedY = edgeY;
     
      // Get front and back edge Z positions
      const frontProfile = computeBulgeProfile(1.0, data.angle);
      const backProfile = computeBulgeProfile(1.0, data.angle);
      const frontZ = (thickness / 2) * frontProfile.height;
      const backZ = -(thickness / 2) * backProfile.height;
     
      // Create rounded transition in Z (curves from front edge to back edge)
      const paddedZ = frontZ * (1 - zTransition) + backZ * zTransition;
     
      positions.push(paddedX, paddedY, paddedZ);
     
      // Calculate normal for rounded border - SIMPLIFIED and ROBUST for ALL edges
      // This ensures consistent normals matching the top-right quality across all sides
      const radialLen = Math.hypot(data.dx, data.dy);
      let radialDirX = 0, radialDirY = 0;
      if (radialLen > 1e-6) {
        radialDirX = data.dx / radialLen;
        radialDirY = data.dy / radialLen;
      } else {
        // Fallback if radial length is zero
        radialDirX = 1;
        radialDirY = 0;
      }
     
      // Calculate Z derivative along the curve (from front to back)
      // Use finite difference for more accurate derivative calculation
      const epsilon = 0.001;
      let ringProgressNext = Math.min(1.0, ringProgress + epsilon);
      let powerCurveNext = Math.pow(ringProgressNext, 1.5);
      let smoothFactorNext = ringProgressNext * ringProgressNext * (3 - 2 * ringProgressNext);
      let zTransitionNext = powerCurveNext * 0.8 + smoothFactorNext * 0.2;
      let paddedZNext = frontZ * (1 - zTransitionNext) + backZ * zTransitionNext;
      const dz_dt = (paddedZNext - paddedZ) / epsilon;
      
      // Calculate tangent along edge (around perimeter) for more accurate normal
      const prevIdx = (i - 1 + n) % n;
      const nextIdx = (i + 1) % n;
      const prevData = radialData[prevIdx];
      const nextData = radialData[nextIdx];
      
      // Edge tangent in XY plane (direction along perimeter)
      const edgeTanX = (nextData.dx - prevData.dx) * 0.5;
      const edgeTanY = (nextData.dy - prevData.dy) * 0.5;
      const edgeTanLen = Math.hypot(edgeTanX, edgeTanY);
      
      // Surface normal calculation using cross product of edge tangent and curve tangent
      // Edge tangent: (edgeTanX/edgeTanLen, edgeTanY/edgeTanLen, 0) - direction around perimeter
      // Curve tangent: (0, 0, dz_dt) - direction along curve (front to back)
      // Normal = edgeTan × curveTan (perpendicular to both)
      // Cross product: (edgeTanY * dz_dt, -edgeTanX * dz_dt, edgeTanX*0 - edgeTanY*0)
      // But we need to ensure it points outward, so we use radial direction as primary
      
      let normalX, normalY, normalZ;
      if (edgeTanLen > 1e-6) {
        const edgeTanNormX = edgeTanX / edgeTanLen;
        const edgeTanNormY = edgeTanY / edgeTanLen;
        
        // Cross product: edgeTan × curveTan = (edgeTanNormY * dz_dt, -edgeTanNormX * dz_dt, 0)
        // But this gives us a normal in the XY plane. We need to combine with radial direction.
        // Actually, the surface normal should point outward (radial) with Z component from curve
        normalX = radialDirX;
        normalY = radialDirY;
        // Z component accounts for the curve slope
        // The sign should ensure the normal follows the surface correctly
        normalZ = -dz_dt / Math.max(radialLen, 1e-6);
      } else {
        // Fallback: use radial direction with Z component from curve
        normalX = radialDirX;
        normalY = radialDirY;
        normalZ = -dz_dt / Math.max(radialLen, 1e-6);
      }
      
      // CRITICAL: Ensure normal always points outward (away from center)
      // The radial component must point outward, and Z should follow the curve
      // Double-check: normal should have positive dot product with radial direction
      const dotWithRadial = normalX * radialDirX + normalY * radialDirY;
      if (dotWithRadial < 0) {
        // Normal points inward, flip it
        normalX = -normalX;
        normalY = -normalY;
        normalZ = -normalZ;
      }
     
      // Normalize the normal vector
      const normalLen = Math.sqrt(normalX * normalX + normalY * normalY + normalZ * normalZ);
      if (normalLen > 1e-6) {
        const invLen = 1 / normalLen;
        const finalNormalX = normalX * invLen;
        const finalNormalY = normalY * invLen;
        const finalNormalZ = normalZ * invLen;
        
        // Final verification: ensure normal still points outward after normalization
        const finalDot = finalNormalX * radialDirX + finalNormalY * radialDirY;
        if (finalDot < 0) {
          // Should never happen, but safety check: flip if needed
          normals.push(-finalNormalX, -finalNormalY, -finalNormalZ);
        } else {
          normals.push(finalNormalX, finalNormalY, finalNormalZ);
        }
      } else {
        // Fallback: pure radial direction (outward) - should never happen but safety check
        normals.push(radialDirX, radialDirY, 0);
      }
     
      // Edge texture coordinates
      const edgeU = centerU + data.du;
      const edgeV = centerV + data.dv;
      textureCoords.push(edgeU, edgeV);
    }
   
    closureRings.push({ offset: ringOffset, count: n });
  }
 
  // Close the shape: front edge -> closure rings -> back edge
  // CRITICAL: Ensure ALL edges are closed with correct winding for ALL sides
  // The winding must be consistent - check how front/back faces are wound
  // Front face: viewed from +Z, triangles are CCW
  // Back face: viewed from -Z, triangles are CCW (but indices are reversed)
  // Edge closure: viewed from outside (radial direction), must be CCW
 
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
   
    // Front face edge indices
    const frontI = frontEdgeOffset + i;
    const frontJ = frontEdgeOffset + j;
   
    // Back face edge indices
    const backI = backEdgeOffset + i;
    const backJ = backEdgeOffset + j;
   
    // Connect front edge to first closure ring
    const firstRing = closureRings[0];
    const firstRingI = firstRing.offset + i;
    const firstRingJ = firstRing.offset + j;
   
    // Winding from outside (radial view): front -> first ring
    // Triangle 1: frontI, firstRingI, firstRingJ
    indices.push(frontI, firstRingI, firstRingJ);
    // Triangle 2: frontI, firstRingJ, frontJ
    indices.push(frontI, firstRingJ, frontJ);
   
    // Connect all intermediate closure rings
    for (let ringIdx = 0; ringIdx < closureRings.length - 1; ringIdx++) {
      const thisRing = closureRings[ringIdx];
      const nextRing = closureRings[ringIdx + 1];
      const thisI = thisRing.offset + i;
      const thisJ = thisRing.offset + j;
      const nextI = nextRing.offset + i;
      const nextJ = nextRing.offset + j;
     
      // Winding from outside: this ring -> next ring
      // Triangle 1: thisI, nextI, nextJ
      indices.push(thisI, nextI, nextJ);
      // Triangle 2: thisI, nextJ, thisJ
      indices.push(thisI, nextJ, thisJ);
    }
   
    // Connect last closure ring to back edge
    const lastRing = closureRings[closureRings.length - 1];
    const lastRingI = lastRing.offset + i;
    const lastRingJ = lastRing.offset + j;
   
    // Winding from outside: last ring -> back edge
    // Use consistent winding - same pattern as front connection
    // Triangle 1: lastRingI, backI, backJ
    indices.push(lastRingI, backI, backJ);
    // Triangle 2: lastRingI, backJ, lastRingJ
    indices.push(lastRingI, backJ, lastRingJ);
  }
 
  // Calculate sideCount: INDEX COUNT used for edge closure draw call.
  //
  // IMPORTANT (bug source / why only “top-right” looked sealed):
  // - The edge closure triangles above are generated for the ENTIRE perimeter (all i from 0..n-1).
  // - WebGL's gl.drawElements(count=...) expects an *index count*, not a triangle count.
  // - If you pass a triangle count here, only the first slice of the side index range is rendered,
  //   which makes only one perimeter region (often where the contour starts, e.g. top-right) look
  //   perfectly closed while the remaining edges appear transparent/open.
  //
  // Compute directly from the indices array to guarantee the full edge closure range is drawn.
  const sideCount = indices.length - (frontInnerCount + backInnerCount);
  console.log('sideCount', sideCount);
  // Calculate model bounding box for dimension guides (before adding label geometry)
  let bboxMinX = Infinity, bboxMinY = Infinity, bboxMinZ = Infinity;
  let bboxMaxX = -Infinity, bboxMaxY = -Infinity, bboxMaxZ = -Infinity;
  for (let i = 0; i < positions.length; i += 3) {
    bboxMinX = Math.min(bboxMinX, positions[i]);
    bboxMaxX = Math.max(bboxMaxX, positions[i]);
    bboxMinY = Math.min(bboxMinY, positions[i + 1]);
    bboxMaxY = Math.max(bboxMaxY, positions[i + 1]);
    bboxMinZ = Math.min(bboxMinZ, positions[i + 2]);
    bboxMaxZ = Math.max(bboxMaxZ, positions[i + 2]);
  }
  modelBoundingBox = { minX: bboxMinX, maxX: bboxMaxX, minY: bboxMinY, maxY: bboxMaxY, minZ: bboxMinZ, maxZ: bboxMaxZ };

  // Regenerate dimension labels with new bounding box
  regenerateDimensionLabels();

  // === PAPER-LIKE LABEL (RIGHT BOTTOM, 5% ABOVE BOTTOM, FOLLOWING EDGE CURVE) ===
  // Calculate model bounds in normalized coordinates
  let modelMinX = Infinity, modelMinY = Infinity, modelMaxX = -Infinity, modelMaxY = -Infinity;
  for (let p of contour) {
    modelMinX = Math.min(modelMinX, p.nx);
    modelMinY = Math.min(modelMinY, p.ny);
    modelMaxX = Math.max(modelMaxX, p.nx);
    modelMaxY = Math.max(modelMaxY, p.ny);
  }
  const modelSpanX = modelMaxX - modelMinX;
  const modelSpanY = modelMaxY - modelMinY;
  const modelMaxSpan = Math.max(modelSpanX, modelSpanY);
 
  // Label size: height as percentage of max(width, height) of model, width = height * ratio
  const labelHeight = modelMaxSpan * (labelHeightPercent / 100);
  const labelWidth = labelHeight * labelAspectRatio;
 
  // Position: right bottom, moved 2x up from previous position (was 5%, now 10% above bottom)
  const targetY = modelMinY + (modelSpanY * 0.10); // Bottom edge plus 10% above (2x the previous 5%)
 
  // Find a point on the right side with normal angle between 45 and 135 degrees
  // Convention: 0 is UP, 90 is RIGHT, 180 is DOWN, 270 is LEFT
  let edgePointIdx = -1;
  let bestScore = -Infinity;
  for (let i = 0; i < n; i++) {
    const p = contour[i];
   
    // Calculate tangent and normal to find the orientation
    const prevIdx = (i - 1 + n) % n;
    const nextIdx = (i + 1) % n;
    const prevP = contour[prevIdx];
    const nextP = contour[nextIdx];
   
    let tx = nextP.nx - prevP.nx;
    let ty = nextP.ny - prevP.ny;
    const tLen = Math.hypot(tx, ty);
    if (tLen > 1e-6) { tx /= tLen; ty /= tLen; }
   
    // Normal (outward)
    let nx = -ty;
    let ny = tx;
    const toCenterX = centerNx - p.nx;
    const toCenterY = centerNy - p.ny;
    if (nx * toCenterX + ny * toCenterY > 0) { nx = -nx; ny = -ny; }
   
    // Calculate angle: 0 is UP (0,1), 90 is RIGHT (1,0)
    // standardAngle: (1,0) is 0, (0,1) is 90
    const standardAngle = Math.atan2(ny, nx) * 180 / Math.PI;
    let angle = 90 - standardAngle;
    while (angle < 0) angle += 360;
    while (angle >= 360) angle -= 360;
    // We want the right side: 45 to 135 degrees, aiming for 90
    if (angle >= 45 && angle <= 135) {
      // Score: prefer 90 degrees, and prefer being on the right side (high X)
      const angleScore = 1.0 - Math.abs(angle - 90) / 45.0;
      const xScore = (p.nx - modelMinX) / modelSpanX;
      const yScore = 1.0 - Math.abs(p.ny - targetY) / modelSpanY;
     
      const totalScore = angleScore * 0.6 + xScore * 0.3 + yScore * 0.1;
      if (totalScore > bestScore) {
        bestScore = totalScore;
        edgePointIdx = i;
      }
    }
  }
 
  // Fallback if no point in [45, 135] found: find rightmost point
  if (edgePointIdx === -1) {
    let rightmostX = -Infinity;
    for (let i = 0; i < n; i++) {
      const p = contour[i];
      if (p.nx > rightmostX) {
        rightmostX = p.nx;
        edgePointIdx = i;
      }
    }
  }
 
  // Calculate smoothed tangent at the edge point
  // Use neighboring points to create a smooth line
  const edgePoint = contour[edgePointIdx];
  const prevIdx = (edgePointIdx - 1 + n) % n;
  const nextIdx = (edgePointIdx + 1) % n;
  const prevPoint = contour[prevIdx];
  const nextPoint = contour[nextIdx];
 
  // Calculate tangent direction (smoothed using multiple points)
  const prev2Idx = (edgePointIdx - 2 + n) % n;
  const next2Idx = (edgePointIdx + 2) % n;
  const prev2Point = contour[prev2Idx];
  const next2Point = contour[next2Idx];
 
  // Weighted average for smoother tangent
  const dx1 = edgePoint.nx - prevPoint.nx;
  const dy1 = edgePoint.ny - prevPoint.ny;
  const dx2 = nextPoint.nx - edgePoint.nx;
  const dy2 = nextPoint.ny - edgePoint.ny;
  const dx3 = nextPoint.nx - prev2Point.nx;
  const dy3 = nextPoint.ny - prev2Point.ny;
  const dx4 = next2Point.nx - prevPoint.nx;
  const dy4 = next2Point.ny - prevPoint.ny;
 
  // Average tangent direction (smoothed)
  let tangentX = (dx1 + dx2 + dx3 * 0.5 + dx4 * 0.5) / 3.0;
  let tangentY = (dy1 + dy2 + dy3 * 0.5 + dy4 * 0.5) / 3.0;
  const tangentLen = Math.hypot(tangentX, tangentY);
  if (tangentLen > 1e-6) {
    tangentX /= tangentLen;
    tangentY /= tangentLen;
  } else {
    tangentX = 1;
    tangentY = 0;
  }
 
  // Calculate normal (perpendicular to tangent, pointing outward)
  // Normal is 90 degrees rotated: (-tangentY, tangentX) for outward
  let normalX = -tangentY;
  let normalY = tangentX;
 
  // Ensure normal points outward (to the right for right side)
  const toCenterX = centerNx - edgePoint.nx;
  const toCenterY = centerNy - edgePoint.ny;
  const dotWithCenter = normalX * toCenterX + normalY * toCenterY;
  if (dotWithCenter > 0) {
    // Normal points inward, flip it
    normalX = -normalX;
    normalY = -normalY;
  }
 
  // Position label: approximately 1/4 inside, 3/4 outside, perpendicular to edge
  const insideFraction = 0.4; // 1/4 inside
  const outsideFraction = 0.6; // 3/4 outside
 
  // Label center position along the normal
  const labelCenterOffset = labelWidth * (outsideFraction - insideFraction) / 2;
  const labelCenterX = edgePoint.nx + normalX * labelCenterOffset;
  // Reduced upward offset to keep label closer to the 3D model
  const upwardOffset = modelSpanY * 0.05; // 5% of model height upward
  const labelCenterY = edgePoint.ny + normalY * labelCenterOffset + upwardOffset;
 
  // Position at Z center (where front and back join, z=0)
  const labelZ = 0.0;
 
  // Label corners relative to center, rotated to be perpendicular to edge
  const halfWidth = labelWidth / 2;
  const halfHeight = labelHeight / 2;
 
  // Rotate label corners to align with normal (perpendicular to tangent)
  // Label extends along normal direction, height along tangent direction
  const cornerOffsets = [
    [-halfWidth, -halfHeight], // Bottom-left (relative to center)
    [halfWidth, -halfHeight], // Bottom-right
    [halfWidth, halfHeight], // Top-right
    [-halfWidth, halfHeight] // Top-left
  ];
 
  // Calculate label surface normal (perpendicular to label surface)
  // Label surface normal is perpendicular to both tangent and normal directions
  // Cross product: tangent × normal = (0, 0, 1) for right-handed system
  // But we want it to face forward for lighting, so use a combination
  const labelNormalX = normalX * 0.7; // Slight forward component
  const labelNormalY = normalY * 0.7;
  const labelNormalZ = 0.7; // Forward component
  const labelNormalLen = Math.sqrt(labelNormalX * labelNormalX + labelNormalY * labelNormalY + labelNormalZ * labelNormalZ);
  const labelNormal = [
    labelNormalX / labelNormalLen,
    labelNormalY / labelNormalLen,
    labelNormalZ / labelNormalLen
  ];
 
  // Create label quad (rectangle plane) rotated to be perpendicular to edge
  const labelOffset = positions.length / 3;
 
  // Four corners of the rectangular label, rotated to follow edge
  for (let i = 0; i < 4; i++) {
    const [localX, localY] = cornerOffsets[i];
    // Rotate local coordinates: X along normal, Y along tangent
    const worldX = labelCenterX + localX * normalX + localY * tangentX;
    const worldY = labelCenterY + localX * normalY + localY * tangentY;
    const worldZ = labelZ; // Use Z center (where front and back join)
   
    positions.push(worldX, worldY, worldZ);
    normals.push(...labelNormal);
  }
 
  // Texture coordinates for label front face (no flipping)
  // localX = -halfWidth (inside) -> u=0, localX = +halfWidth (outside) -> u=1
  textureCoords.push(
    0, 0, // Bottom-left
    1, 0, // Bottom-right
    1, 1, // Top-right
    0, 1 // Top-left
  );
 
  // Create two triangles for the front face
  const labelFrontCount = 6; // 2 triangles = 6 indices
  indices.push(
    labelOffset, labelOffset + 1, labelOffset + 2, // First triangle
    labelOffset, labelOffset + 2, labelOffset + 3 // Second triangle
  );
 
  // Create back face with mirrored texture
  const labelBackOffset = positions.length / 3;
  for (let i = 0; i < 4; i++) {
    const [localX, localY] = cornerOffsets[i];
    // Same positions as front face (same Z)
    const worldX = labelCenterX + localX * normalX + localY * tangentX;
    const worldY = labelCenterY + localX * normalY + localY * tangentY;
    const worldZ = labelZ;
   
    positions.push(worldX, worldY, worldZ);
   
    // Back face normal (opposite direction)
    normals.push(-labelNormal[0], -labelNormal[1], -labelNormal[2]);
  }
 
  // Texture coordinates for label back face (mirrored horizontally)
  textureCoords.push(
    0, 0, // Bottom-left (mirrored - starts from left)
    1, 0, // Bottom-right (mirrored - ends at right)
    1, 1, // Top-right (mirrored - ends at right)
    0, 1 // Top-left (mirrored - starts from left)
  );
 
  // Create two triangles for the back face (winding order reversed)
  const labelBackCount = 6;
  indices.push(
    labelBackOffset, labelBackOffset + 2, labelBackOffset + 1, // First triangle (reversed)
    labelBackOffset, labelBackOffset + 3, labelBackOffset + 2 // Second triangle (reversed)
  );
 
  const labelCount = labelFrontCount + labelBackCount;
  const labelFrontCountFinal = labelFrontCount;
  const labelBackCountFinal = labelBackCount;
  const labelCountFinal = labelCount;

  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
  const normalBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(normals), gl.STATIC_DRAW);
  const textureCoordBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, textureCoordBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(textureCoords), gl.STATIC_DRAW);
  const indexBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);
  return {
    position: positionBuffer,
    normal: normalBuffer,
    textureCoord: textureCoordBuffer,
    indices: indexBuffer,
    frontInnerCount,
    backInnerCount,
    sideCount,
    labelCount: labelCountFinal,
    labelFrontCount: labelFrontCountFinal,
    labelBackCount: labelBackCountFinal
  };
}
// Create a cone shape for light source visualization
// Adjusted size to be more visible and proportional to the model
function createLightCone(gl, radius = 2.0, height = 5.0, segments = 16) {
  const positions = [];
  const normals = [];
  const indices = [];
 
  // Tip of cone at origin
  positions.push(0, height, 0);
  normals.push(0, 1, 0);
 
  // Base circle vertices
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;
    positions.push(x, 0, z);
    // Normal points outward from cone surface
    const normal = normalize([x, 0, z]);
    normals.push(normal[0], normal[1], normal[2]);
  }
 
  // Generate indices for cone sides
  for (let i = 0; i < segments; i++) {
    indices.push(0, i + 1, i + 2);
  }
 
  // Base circle (optional, but makes it look better)
  const baseCenter = positions.length / 3;
  for (let i = 0; i < segments; i++) {
    positions.push(Math.cos((i / segments) * Math.PI * 2) * radius, 0, Math.sin((i / segments) * Math.PI * 2) * radius);
    normals.push(0, -1, 0);
  }
  positions.push(0, 0, 0); // Center of base
  normals.push(0, -1, 0);
 
  for (let i = 0; i < segments; i++) {
    const next = (i + 1) % segments;
    indices.push(baseCenter + segments, baseCenter + i, baseCenter + next);
  }
 
  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
 
  const normalBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(normals), gl.STATIC_DRAW);
 
  const indexBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);
 
  return {
    position: positionBuffer,
    normal: normalBuffer,
    indices: indexBuffer,
    count: indices.length
  };
}
// Create a triangle/arrow pointing in a direction
function createDirectionTriangle(gl, start, direction, length = 80.0, arrowSize = 10.0) {
  // Calculate end point
  const end = [
    start[0] + direction[0] * length,
    start[1] + direction[1] * length,
    start[2] + direction[2] * length
  ];
 
  // Find perpendicular vectors for arrow base
  let perp1;
  if (Math.abs(direction[0]) < 0.9) {
    perp1 = normalize([1, 0, 0]);
  } else {
    perp1 = normalize([0, 1, 0]);
  }
 
  // Cross product to get first perpendicular
  perp1 = [
    direction[1] * perp1[2] - direction[2] * perp1[1],
    direction[2] * perp1[0] - direction[0] * perp1[2],
    direction[0] * perp1[1] - direction[1] * perp1[0]
  ];
  perp1 = normalize(perp1);
 
  // Second perpendicular
  let perp2 = [
    direction[1] * perp1[2] - direction[2] * perp1[1],
    direction[2] * perp1[0] - direction[0] * perp1[2],
    direction[0] * perp1[1] - direction[1] * perp1[0]
  ];
  perp2 = normalize(perp2);
 
  // Arrow tip at end
  const tip = end;
 
  // Arrow base points (slightly back from tip)
  const baseOffset = [
    direction[0] * arrowSize,
    direction[1] * arrowSize,
    direction[2] * arrowSize
  ];
  const baseCenter = [
    end[0] - baseOffset[0],
    end[1] - baseOffset[1],
    end[2] - baseOffset[2]
  ];
 
  // Create a 3-sided pyramid arrow
  const angle1 = 0;
  const angle2 = (2 * Math.PI) / 3;
  const angle3 = (4 * Math.PI) / 3;
 
  const base1 = [
    baseCenter[0] + perp1[0] * arrowSize * 0.6 * Math.cos(angle1) + perp2[0] * arrowSize * 0.6 * Math.sin(angle1),
    baseCenter[1] + perp1[1] * arrowSize * 0.6 * Math.cos(angle1) + perp2[1] * arrowSize * 0.6 * Math.sin(angle1),
    baseCenter[2] + perp1[2] * arrowSize * 0.6 * Math.cos(angle1) + perp2[2] * arrowSize * 0.6 * Math.sin(angle1)
  ];
 
  const base2 = [
    baseCenter[0] + perp1[0] * arrowSize * 0.6 * Math.cos(angle2) + perp2[0] * arrowSize * 0.6 * Math.sin(angle2),
    baseCenter[1] + perp1[1] * arrowSize * 0.6 * Math.cos(angle2) + perp2[1] * arrowSize * 0.6 * Math.sin(angle2),
    baseCenter[2] + perp1[2] * arrowSize * 0.6 * Math.cos(angle2) + perp2[2] * arrowSize * 0.6 * Math.sin(angle2)
  ];
 
  const base3 = [
    baseCenter[0] + perp1[0] * arrowSize * 0.6 * Math.cos(angle3) + perp2[0] * arrowSize * 0.6 * Math.sin(angle3),
    baseCenter[1] + perp1[1] * arrowSize * 0.6 * Math.cos(angle3) + perp2[1] * arrowSize * 0.6 * Math.sin(angle3),
    baseCenter[2] + perp1[2] * arrowSize * 0.6 * Math.cos(angle3) + perp2[2] * arrowSize * 0.6 * Math.sin(angle3)
  ];
 
  // Create arrow as a 3-sided pyramid (3 triangles from tip to base)
  const positions = [
    // Triangle 1: tip, base1, base2
    tip[0], tip[1], tip[2],
    base1[0], base1[1], base1[2],
    base2[0], base2[1], base2[2],
    // Triangle 2: tip, base2, base3
    tip[0], tip[1], tip[2],
    base2[0], base2[1], base2[2],
    base3[0], base3[1], base3[2],
    // Triangle 3: tip, base3, base1
    tip[0], tip[1], tip[2],
    base3[0], base3[1], base3[2],
    base1[0], base1[1], base1[2],
  ];
 
  // Calculate normals for each triangle
  const normals = [];
  for (let i = 0; i < positions.length; i += 9) {
    const v1 = [positions[i+3] - positions[i], positions[i+4] - positions[i+1], positions[i+5] - positions[i+2]];
    const v2 = [positions[i+6] - positions[i], positions[i+7] - positions[i+1], positions[i+8] - positions[i+2]];
    const normal = normalize([
      v1[1] * v2[2] - v1[2] * v2[1],
      v1[2] * v2[0] - v1[0] * v2[2],
      v1[0] * v2[1] - v1[1] * v2[0]
    ]);
    normals.push(normal[0], normal[1], normal[2]);
    normals.push(normal[0], normal[1], normal[2]);
    normals.push(normal[0], normal[1], normal[2]);
  }
 
  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
 
  const normalBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(normals), gl.STATIC_DRAW);
 
  return {
    position: positionBuffer,
    normal: normalBuffer,
    count: positions.length / 3
  };
}
let buffers = null;
let referenceLineBuffers = null;
let dimensionTextBuffers = null;
let dimensionGuideBuffers = null;
let lightConeBuffer = null;
let rotX = 0, rotY = 0, transZ = -2.5;
let showDimensionGuides = false; // Toggle to show dimension guide lines
let maxSizeCm = 40; // Maximum dimension size in cm (from HTML input)

// Function to regenerate dimension labels when max size changes
function regenerateDimensionLabels() {
  if (modelBoundingBox && gl) {
    dimensionTextBuffers = createDimensionTextBuffers(gl, modelBoundingBox);
    drawScene();
  }
}
// Model dimensions for overlay guides
let modelWidth = 0, modelHeight = 0;
// Model bounding box for spotlight positioning
let modelBoundingBox = {
  minX: 0, maxX: 0,
  minY: 0, maxY: 0,
  minZ: 0, maxZ: 0
};
function hexToWebGLRGBA(hex, alpha = 1) {
  hex = hex.replace('#', '');

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  return { r, g, b, a: alpha };
}
const isIphoneV = /iPhone/i.test(navigator.userAgent);
function drawScene() {
  if (!buffers) return;
  gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
  // Clear with zero alpha to let the page background show through the canvas
  // gl.clearColor(backgroundColor[0], backgroundColor[1], backgroundColor[2], 0.0);
  if (isIphoneV) {
    const c = hexToWebGLRGBA(
      window.iphoneWebglColor,
      window.iphoneWebglAlpha
    );

    gl.clearColor(c.r, c.g, c.b, c.a);
  } else {
    gl.clearColor(1.0, 0.8706, 0.3529, 0.25);
  }
  gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
  gl.enable(gl.DEPTH_TEST);
  gl.depthFunc(gl.LEQUAL);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  const fieldOfView = Math.PI / 4;
  const aspect = gl.canvas.clientWidth / gl.canvas.clientHeight;
  const zNear = 0.1;
  const zFar = 100.0;
  let projectionMatrix = perspective(fieldOfView, aspect, zNear, zFar);
  let modelViewMatrix = identity();
  modelViewMatrix = scale(modelViewMatrix, currentScale);
  modelViewMatrix = rotateY(modelViewMatrix, -rotY);
  modelViewMatrix = rotateX(modelViewMatrix, -rotX);
  modelViewMatrix = translate(modelViewMatrix, 0, 0, transZ);
  let normalM = normalMatrix(modelViewMatrix);

  // Create fixed modelViewMatrix for dimension guides (no rotation)
  let guideModelViewMatrix = identity();
  guideModelViewMatrix = scale(guideModelViewMatrix, currentScale);
  guideModelViewMatrix = translate(guideModelViewMatrix, 0, 0, transZ);
 
  // Calculate spotlight positions and directions based on model bounding box
  // Reset reusable spotlight arrays instead of reallocating every frame
  for (let i = 0; i < NUM_LIGHTS; i++) {
    const pos = spotlightPositions[i];
    pos[0] = 0; pos[1] = 0; pos[2] = 0;
    const dir = spotlightDirections[i];
    dir[0] = 0; dir[1] = -1; dir[2] = 0;
    const modelPos = modelSpaceLightPositions[i];
    modelPos[0] = 0; modelPos[1] = 0; modelPos[2] = 0;
  }
 
  if (NUM_LIGHTS > 0 && modelBoundingBox && buffers) {
    // Calculate model center in model space
    const modelCenterX = (modelBoundingBox.minX + modelBoundingBox.maxX) / 2;
    const modelCenterY = (modelBoundingBox.minY + modelBoundingBox.maxY) / 2;
    const modelCenterZ = (modelBoundingBox.minZ + modelBoundingBox.maxZ) / 2;
    const modelDepth = modelBoundingBox.maxZ - modelBoundingBox.minZ;
    const modelHeight = modelBoundingBox.maxY - modelBoundingBox.minY;
    const modelWidth = modelBoundingBox.maxX - modelBoundingBox.minX;
   
    // Use the largest dimension to scale the light distance
    const maxDim = Math.max(modelDepth, Math.max(modelHeight, modelWidth));
    const lightDistance = Math.max(50, maxDim * 1.5) * 0.25; // 25% closer to the model (75% of original distance)
   
    const spherePositions = LIGHT_SPHERE.positions;
    const sphereDirs = LIGHT_SPHERE.directions;
   
    // Position lights on a sphere around the model center
    for (let i = 0; i < NUM_LIGHTS; i++) {
      const spherePoint = spherePositions[i];
     
      // Scale sphere point by lightDistance and translate to model center
      const modelPos = modelSpaceLightPositions[i];
      modelPos[0] = modelCenterX + spherePoint[0] * lightDistance;
      modelPos[1] = modelCenterY + spherePoint[1] * lightDistance;
      modelPos[2] = modelCenterZ + spherePoint[2] * lightDistance;
    }
   
    // Transform model space positions to view space for shader uniforms
    for (let i = 0; i < modelSpaceLightPositions.length; i++) {
      const modelPos = modelSpaceLightPositions[i];
      const modelDir = sphereDirs[i];
     
      // Transform position to view space
      const modelPosVec = [modelPos[0], modelPos[1], modelPos[2], 1];
      const viewPos = matVecMul(modelViewMatrix, modelPosVec);
      const viewPosOut = spotlightPositions[i];
      viewPosOut[0] = viewPos[0];
      viewPosOut[1] = viewPos[1];
      viewPosOut[2] = viewPos[2];
     
      // Transform direction vector (only rotation, no translation)
      // Extract 3x3 rotation part from modelViewMatrix and multiply with direction
      const m = modelViewMatrix;
      const viewDir = [
        m[0] * modelDir[0] + m[4] * modelDir[1] + m[8] * modelDir[2],
        m[1] * modelDir[0] + m[5] * modelDir[1] + m[9] * modelDir[2],
        m[2] * modelDir[0] + m[6] * modelDir[1] + m[10] * modelDir[2]
      ];
      const viewDirNorm = normalize(viewDir);
      const spotlightDirOut = spotlightDirections[i];
      spotlightDirOut[0] = viewDirNorm[0];
      spotlightDirOut[1] = viewDirNorm[1];
      spotlightDirOut[2] = viewDirNorm[2];
    }
   
    // Store model space positions for visualization (so we can transform them correctly)
    // We'll use these in the visualization code
    spotlightPositions._modelSpace = modelSpaceLightPositions;
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, buffers.position);
  gl.vertexAttribPointer(programInfo.attribLocations.vertexPosition, 3, gl.FLOAT, false, 0, 0);
  gl.enableVertexAttribArray(programInfo.attribLocations.vertexPosition);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffers.normal);
  gl.vertexAttribPointer(programInfo.attribLocations.vertexNormal, 3, gl.FLOAT, false, 0, 0);
  gl.enableVertexAttribArray(programInfo.attribLocations.vertexNormal);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffers.textureCoord);
  gl.vertexAttribPointer(programInfo.attribLocations.textureCoord, 2, gl.FLOAT, false, 0, 0);
  gl.enableVertexAttribArray(programInfo.attribLocations.textureCoord);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buffers.indices);
  gl.useProgram(programInfo.program);
  gl.uniformMatrix4fv(programInfo.uniformLocations.projectionMatrix, false, new Float32Array(projectionMatrix));
  gl.uniform3fv(programInfo.uniformLocations.pillowColor, new Float32Array(pillowColor));
  gl.uniform1f(programInfo.uniformLocations.lightingIntensity, lightingIntensity);
 
  if (NUM_LIGHTS > 0) {
    // Set spotlight uniforms - adjust intensity for NUM_LIGHTS lights
    // Base intensity per light is reduced proportionally so total lighting is reasonable
    const baseIntensity = 12.0;
    // Scale intensity per light based on number of lights (original was for 18 lights)
    // With 8 lights, each light will be brighter than with 100 lights
    const finalIntensity = (baseIntensity * lightingIntensity) * (18 / NUM_LIGHTS);

    for (let i = 0; i < spotlightPositions.length; i++) {
      const num = i + 1;
      gl.uniform3fv(programInfo.uniformLocations[`spotlight${num}Position`], new Float32Array(spotlightPositions[i]));
      gl.uniform3fv(programInfo.uniformLocations[`spotlight${num}Direction`], new Float32Array(spotlightDirections[i]));
      gl.uniform1f(programInfo.uniformLocations[`spotlight${num}Angle`], Math.cos(Math.PI / 2.2)); // ~82 degree cone for very wide coverage
      gl.uniform1f(programInfo.uniformLocations[`spotlight${num}Intensity`], finalIntensity); // Adjustable intensity for bright illumination
      gl.uniform1f(programInfo.uniformLocations[`spotlight${num}Decay`], 0.0005); // Minimal decay for even lighting
    }
  }
 
  const frontInnerCount = buffers.frontInnerCount;
  const backInnerCount = buffers.backInnerCount;
  // NOTE: sideCount is an INDEX COUNT for gl.drawElements (not a triangle count).
  // If this is too small, only a partial perimeter region renders as "sealed".
  const sideCount = buffers.sideCount;
  const labelCount = buffers.labelCount || 0;
  const backInnerOffsetBytes = (frontInnerCount) * 2;
  const sideOffsetBytes = (frontInnerCount + backInnerCount) * 2;
  const labelOffsetBytes = (frontInnerCount + backInnerCount + sideCount) * 2;

  gl.activeTexture(gl.TEXTURE0);
  gl.uniform1i(programInfo.uniformLocations.uSampler, 0);

  if (RENDER_SHADOW) {
    // Draw a SINGLE stable, deterministic floor shadow.
    gl.disable(gl.DEPTH_TEST); // Let shadow blend over floor/background
    gl.depthMask(false); // Do not write depth for shadow
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.uniform1i(programInfo.uniformLocations.isShadow, true);
    gl.uniform1i(programInfo.uniformLocations.uUseTexture, false);
    gl.bindTexture(gl.TEXTURE_2D, plainTexture);

    // Shadow plane: at the bottom of the model with a tiny epsilon to ensure it renders cleanly.
    let shadowY = -1.1; // Default fallback
    if (modelBoundingBox && buffers) {
      shadowY = modelBoundingBox.minY + 0.001;
    }

    // Fixed ~45° projection direction in VIEW space (down + away from viewer = "behind" the pillow).
    const Lview = normalize([0.0, -1.0, -1.0]);

    // Convert VIEW->MODEL space using the inverse rotation of modelViewMatrix (transpose of the 3x3).
    const m = modelViewMatrix;
    const Lmodel = normalize([
      m[0] * Lview[0] + m[1] * Lview[1] + m[2] * Lview[2],
      m[4] * Lview[0] + m[5] * Lview[1] + m[6] * Lview[2],
      m[8] * Lview[0] + m[9] * Lview[1] + m[10] * Lview[2],
    ]);
    const dx = Lmodel[0], dy = Lmodel[1], dz = Lmodel[2];

    // Only project if direction points "down" relative to the floor plane (prevents instability near dy=0).
    if (dy < -0.001) {
      const invDy = 1.0 / dy;

      // Directional projection onto the plane Y = shadowY, along Lmodel.
      const shadowMatrix = [
        1, 0, 0, 0,
        -dx * invDy, 0, -dz * invDy, 0,
        0, 0, 1, 0,
        dx * shadowY * invDy, shadowY, dz * shadowY * invDy, 1
      ];

      const blurRadius = modelBoundingBox ? 
        Math.max(modelBoundingBox.maxX - modelBoundingBox.minX, 
                 modelBoundingBox.maxZ - modelBoundingBox.minZ) * 0.02 : 0.05;
      
      const GAUSSIAN_OFFSETS = [
        [0.0, 0.0],
        [1.0, 0.0],
        [-1.0, 0.0],
        [0.0, 1.0],
        [0.0, -1.0],
        [0.707, 0.707],
        [-0.707, 0.707],
        [0.707, -0.707],
        [-0.707, -0.707]
      ];
      
      const SHADOW_PASSES = GAUSSIAN_OFFSETS.length;
      
      for (let p = 0; p < SHADOW_PASSES; p++) {
        const offsetX = GAUSSIAN_OFFSETS[p][0] * blurRadius;
        const offsetZ = GAUSSIAN_OFFSETS[p][1] * blurRadius;
        
        const blurOffsetMatrix = translate(identity(), offsetX, 0, offsetZ);
        const blurredShadowMatrix = multiply(shadowMatrix, blurOffsetMatrix);
        const modelViewShadow = multiply(modelViewMatrix, blurredShadowMatrix);
        
        gl.uniformMatrix4fv(programInfo.uniformLocations.modelViewMatrix, false, new Float32Array(modelViewShadow));
        const normalMShadow = normalMatrix(modelViewShadow);
        gl.uniformMatrix4fv(programInfo.uniformLocations.normalMatrix, false, new Float32Array(normalMShadow));
        
        gl.drawElements(gl.TRIANGLES, frontInnerCount, gl.UNSIGNED_SHORT, 0);
        gl.drawElements(gl.TRIANGLES, backInnerCount, gl.UNSIGNED_SHORT, backInnerOffsetBytes);
        gl.drawElements(gl.TRIANGLES, sideCount, gl.UNSIGNED_SHORT, sideOffsetBytes);
        if (labelCount > 0) {
          gl.drawElements(gl.TRIANGLES, labelCount, gl.UNSIGNED_SHORT, labelOffsetBytes);
        }
      }
    }
  }
 
  // Reset for main draw
  gl.uniform1i(programInfo.uniformLocations.isShadow, false);
  gl.enable(gl.DEPTH_TEST);
  gl.depthMask(true);
  gl.uniformMatrix4fv(programInfo.uniformLocations.modelViewMatrix, false, new Float32Array(modelViewMatrix));
  gl.uniformMatrix4fv(programInfo.uniformLocations.normalMatrix, false, new Float32Array(normalM));
  gl.uniform3fv(programInfo.uniformLocations.pillowColor, new Float32Array(pillowColor));
  // Draw front inner (texture)
  gl.uniform1i(programInfo.uniformLocations.uUseTexture, true);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.drawElements(gl.TRIANGLES, frontInnerCount, gl.UNSIGNED_SHORT, 0);
  // Draw back inner (texture)
  gl.uniform1i(programInfo.uniformLocations.uUseTexture, true);
  gl.bindTexture(gl.TEXTURE_2D, (flippedReady && flippedTexture) ? flippedTexture : texture);
  gl.drawElements(gl.TRIANGLES, backInnerCount, gl.UNSIGNED_SHORT, backInnerOffsetBytes);
  // Draw sides (edge seam) with pillow color to match 3D surface color
  gl.uniform1i(programInfo.uniformLocations.uUseTexture, false);
  gl.uniform3fv(programInfo.uniformLocations.pillowColor, new Float32Array(pillowColor));
  gl.bindTexture(gl.TEXTURE_2D, plainTexture);
  gl.drawElements(gl.TRIANGLES, sideCount, gl.UNSIGNED_SHORT, sideOffsetBytes);
  // Draw label with texture
  if (labelCount > 0 && labelTexture) {
    const labelFrontCount = buffers.labelFrontCount || 0;
    const labelBackCount = buffers.labelBackCount || 0;
    const labelFrontOffsetBytes = labelOffsetBytes;
    const labelBackOffsetBytes = labelOffsetBytes + (labelFrontCount * 2);
   
    // Draw front face with label texture (starts from right)
    gl.uniform1i(programInfo.uniformLocations.uUseTexture, true);
    gl.bindTexture(gl.TEXTURE_2D, labelTexture);
    gl.uniform3fv(programInfo.uniformLocations.pillowColor, new Float32Array([1.0, 1.0, 1.0]));
    if (labelFrontCount > 0) {
      gl.drawElements(gl.TRIANGLES, labelFrontCount, gl.UNSIGNED_SHORT, labelFrontOffsetBytes);
    }
   
    // Draw back face with mirrored label texture
    if (labelBackCount > 0) {
      gl.drawElements(gl.TRIANGLES, labelBackCount, gl.UNSIGNED_SHORT, labelBackOffsetBytes);
    }
   
    // Restore original pillow color
    gl.uniform3fv(programInfo.uniformLocations.pillowColor, new Float32Array(pillowColor));
  } else if (labelCount > 0) {
    // Fallback to white if texture not loaded
    gl.uniform1i(programInfo.uniformLocations.uUseTexture, false);
    gl.bindTexture(gl.TEXTURE_2D, plainTexture);
    gl.uniform3fv(programInfo.uniformLocations.pillowColor, new Float32Array([1.0, 1.0, 1.0]));
    gl.drawElements(gl.TRIANGLES, labelCount, gl.UNSIGNED_SHORT, labelOffsetBytes);
    gl.uniform3fv(programInfo.uniformLocations.pillowColor, new Float32Array(pillowColor));
  }

  // Reset dimension guide flag and modelViewMatrix
  gl.uniform1i(programInfo.uniformLocations.isDimensionGuide, false);
  gl.uniformMatrix4fv(programInfo.uniformLocations.modelViewMatrix, false, new Float32Array(modelViewMatrix));

  // Draw reference lines (always visible white lines)
  if (referenceLineBuffers && modelBoundingBox) {
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.disable(gl.BLEND);

    gl.uniform1i(programInfo.uniformLocations.isShadow, false);
    gl.uniform1i(programInfo.uniformLocations.isDimensionGuide, true);
    gl.uniform1i(programInfo.uniformLocations.uUseTexture, false);
    gl.uniform3fv(programInfo.uniformLocations.guideColor, new Float32Array([0.0, 0.0, 0.0])); // White color for reference lines

    // Use fixed modelViewMatrix for reference lines (no rotation)
    gl.uniformMatrix4fv(programInfo.uniformLocations.modelViewMatrix, false, new Float32Array(guideModelViewMatrix));

    // Bind reference line buffers
    gl.bindBuffer(gl.ARRAY_BUFFER, referenceLineBuffers.position);
    gl.vertexAttribPointer(programInfo.attribLocations.vertexPosition, 3, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexPosition);

    gl.bindBuffer(gl.ARRAY_BUFFER, referenceLineBuffers.normal);
    gl.vertexAttribPointer(programInfo.attribLocations.vertexNormal, 3, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexNormal);

    gl.bindBuffer(gl.ARRAY_BUFFER, referenceLineBuffers.textureCoord);
    gl.vertexAttribPointer(programInfo.attribLocations.textureCoord, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.textureCoord);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, referenceLineBuffers.indices);

    // Draw reference lines
    gl.drawElements(gl.LINES, referenceLineBuffers.vertexCount, gl.UNSIGNED_SHORT, 0);
  }

  // Reset reference line settings
  gl.uniform1i(programInfo.uniformLocations.isDimensionGuide, false);
  gl.uniformMatrix4fv(programInfo.uniformLocations.modelViewMatrix, false, new Float32Array(modelViewMatrix));

  // Draw 3D dimension text labels (always visible)
  if (dimensionTextBuffers && modelBoundingBox) {
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.disable(gl.BLEND);

    gl.uniform1i(programInfo.uniformLocations.isShadow, false);
    gl.uniform1i(programInfo.uniformLocations.isDimensionGuide, true);
    gl.uniform1i(programInfo.uniformLocations.uUseTexture, false);
    gl.uniform3fv(programInfo.uniformLocations.guideColor, new Float32Array([0.0, 0.0, 0.0])); // Bright white color for text labels

    // Use fixed modelViewMatrix for text labels (no rotation)
    gl.uniformMatrix4fv(programInfo.uniformLocations.modelViewMatrix, false, new Float32Array(guideModelViewMatrix));

    // Bind dimension text buffers
    gl.bindBuffer(gl.ARRAY_BUFFER, dimensionTextBuffers.position);
    gl.vertexAttribPointer(programInfo.attribLocations.vertexPosition, 3, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexPosition);

    gl.bindBuffer(gl.ARRAY_BUFFER, dimensionTextBuffers.normal);
    gl.vertexAttribPointer(programInfo.attribLocations.vertexNormal, 3, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexNormal);

    gl.bindBuffer(gl.ARRAY_BUFFER, dimensionTextBuffers.textureCoord);
    gl.vertexAttribPointer(programInfo.attribLocations.textureCoord, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.textureCoord);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, dimensionTextBuffers.indices);

    // Draw dimension text
    gl.drawElements(gl.TRIANGLES, dimensionTextBuffers.vertexCount, gl.UNSIGNED_SHORT, 0);

    // Reset dimension guide flag and modelViewMatrix
    gl.uniform1i(programInfo.uniformLocations.isDimensionGuide, false);
    gl.uniformMatrix4fv(programInfo.uniformLocations.modelViewMatrix, false, new Float32Array(modelViewMatrix));
  }

  // Draw dimension guides if enabled
  if (showDimensionGuides && dimensionGuideBuffers && modelBoundingBox) {
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.disable(gl.BLEND);
    gl.uniform1i(programInfo.uniformLocations.isShadow, false);
    gl.uniform1i(programInfo.uniformLocations.isDimensionGuide, true);
    gl.uniform1i(programInfo.uniformLocations.uUseTexture, false);
    gl.uniform3fv(programInfo.uniformLocations.guideColor, new Float32Array([0.0, 0.0, 0.0])); // White color for guides

    gl.uniformMatrix4fv(programInfo.uniformLocations.modelViewMatrix, false, new Float32Array(modelViewMatrix));

    // Bind dimension guide buffers
    gl.bindBuffer(gl.ARRAY_BUFFER, dimensionGuideBuffers.position);
    gl.vertexAttribPointer(programInfo.attribLocations.vertexPosition, 3, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexPosition);

    gl.bindBuffer(gl.ARRAY_BUFFER, dimensionGuideBuffers.normal);
    gl.vertexAttribPointer(programInfo.attribLocations.vertexNormal, 3, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexNormal);

    gl.bindBuffer(gl.ARRAY_BUFFER, dimensionGuideBuffers.textureCoord);
    gl.vertexAttribPointer(programInfo.attribLocations.textureCoord, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.textureCoord);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, dimensionGuideBuffers.indices);

    // Draw dimension guides as lines
    gl.drawElements(gl.LINES, dimensionGuideBuffers.vertexCount, gl.UNSIGNED_SHORT, 0);

    // Reset dimension guide flag and modelViewMatrix
    gl.uniform1i(programInfo.uniformLocations.isDimensionGuide, false);
    gl.uniformMatrix4fv(programInfo.uniformLocations.modelViewMatrix, false, new Float32Array(modelViewMatrix));
  }

  // Draw light sources and camera visualization if enabled
  if (showLights && lightConeBuffer && modelBoundingBox && buffers) {
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.disable(gl.BLEND);
    gl.uniform1i(programInfo.uniformLocations.isShadow, false);
    gl.uniform1i(programInfo.uniformLocations.uUseTexture, false);
   
    // Bind light cone buffer
    gl.bindBuffer(gl.ARRAY_BUFFER, lightConeBuffer.position);
    gl.vertexAttribPointer(programInfo.attribLocations.vertexPosition, 3, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexPosition);
    gl.bindBuffer(gl.ARRAY_BUFFER, lightConeBuffer.normal);
    gl.vertexAttribPointer(programInfo.attribLocations.vertexNormal, 3, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexNormal);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, lightConeBuffer.indices);
   
    // Draw each spotlight as a colored cone
    const lightColors = [
      [1.0, 0.2, 0.2], // Red
      [0.2, 1.0, 0.2], // Green
      [0.2, 0.2, 1.0], // Blue
      [1.0, 1.0, 0.2], // Yellow
      [1.0, 0.2, 1.0], // Magenta
      [0.2, 1.0, 1.0], // Cyan
      [1.0, 0.5, 0.0], // Orange
      [0.5, 0.0, 1.0], // Purple
      [1.0, 0.2, 0.2], // Red (repeated)
      [0.2, 1.0, 0.2], // Green
      [0.2, 0.2, 1.0], // Blue
      [1.0, 1.0, 0.2], // Yellow
      [1.0, 0.2, 1.0], // Magenta
      [0.2, 1.0, 1.0], // Cyan
      [1.0, 0.5, 0.0], // Orange
      [0.5, 0.0, 1.0], // Purple
      [1.0, 0.8, 0.2], // Gold (for left light)
      [0.8, 0.2, 1.0], // Lavender (for right light)
    ];
   
    const lightPositions = spotlightPositions;
    const lightDirections = spotlightDirections;
   
    // Lighting visualization uses fixed sizes in 3D space
    // They behave like real 3D objects - scale naturally with camera zoom via modelViewMatrix
    // No artificial scaling - they maintain their real size and appear smaller/larger based on camera distance
    // Size should be proportional to model size
    const modelSize = modelBoundingBox ? Math.max(
      modelBoundingBox.maxX - modelBoundingBox.minX,
      Math.max(
        modelBoundingBox.maxY - modelBoundingBox.minY,
        modelBoundingBox.maxZ - modelBoundingBox.minZ
      )
    ) : 100.0;
    const arrowLength = modelSize * 0.3; // Arrow length proportional to model
    const arrowSize = modelSize * 0.04; // Arrow size proportional to model
   
    // Use model space positions for visualization (if available)
    // This ensures lights are positioned correctly relative to the model
    // Lights are arranged in a sphere pattern around the model center
    const visLightPositions = lightPositions._modelSpace || lightPositions;
    const visLightDirections = lightDirections;
   
    for (let i = 0; i < visLightPositions.length; i++) {
      // Use model space position for visualization
      // Lights are arranged in a sphere pattern, so positions are already correct
      let lightPos = visLightPositions[i];
      const lightDir = visLightDirections[i];
      const lightColor = lightColors[i % lightColors.length]; // Use modulo to cycle through colors for all lights
     
      // Create transformation matrix for cone
      // Cone tip is at (0, height, 0) = (0, 5.0, 0) in local space
      // We want the tip to be exactly at lightPos Y position (model center)
      // The cone extends from tip (Y=height) to base (Y=0), pointing downward
     
      // First, rotate the cone to point in light direction (before translation)
      // Default cone direction is [0, -1, 0] (pointing down), target is lightDir (pointing toward model center)
      const defaultConeDirection = [0, -1, 0]; // Cone points downward by default
      let coneMatrix = rotateFromTo(identity(), defaultConeDirection, lightDir);
     
      // The cone tip is at (0, height, 0) in local space where height = 5.0
      // After rotation, we need to translate so the tip is exactly at lightPos
      // Calculate where the tip ends up after rotation
      const coneHeight = 5.0; // Height of cone from createLightCone function
      const tipLocal = [0, coneHeight, 0];
     
      // Apply rotation to tip position
      const rotM = coneMatrix;
      const rotatedTip = [
        rotM[0] * tipLocal[0] + rotM[4] * tipLocal[1] + rotM[8] * tipLocal[2],
        rotM[1] * tipLocal[0] + rotM[5] * tipLocal[1] + rotM[9] * tipLocal[2],
        rotM[2] * tipLocal[0] + rotM[6] * tipLocal[1] + rotM[10] * tipLocal[2]
      ];
     
      // Translate so the rotated tip is exactly at lightPos (Y is exactly at model center)
      // The tip at (0, height, 0) after rotation becomes rotatedTip
      // We translate by lightPos - rotatedTip so the tip ends up exactly at lightPos
      // This ensures the cone tip Y is exactly at model center (lightPos[1])
      coneMatrix = translate(coneMatrix,
        lightPos[0] - rotatedTip[0],
        lightPos[1] - rotatedTip[1], // Tip Y is exactly at model center
        lightPos[2] - rotatedTip[2]
      );
     
      // No artificial scaling - cone uses its original size (radius 0.8, height 2.0)
      // It will scale naturally with modelViewMatrix (which includes currentScale and camera transformations)
      let coneModelView = multiply(modelViewMatrix, coneMatrix);
      let coneNormalM = normalMatrix(coneModelView);
     
      gl.uniformMatrix4fv(programInfo.uniformLocations.modelViewMatrix, false, new Float32Array(coneModelView));
      gl.uniformMatrix4fv(programInfo.uniformLocations.normalMatrix, false, new Float32Array(coneNormalM));
      gl.uniform3fv(programInfo.uniformLocations.pillowColor, new Float32Array(lightColor));
      gl.uniform1f(programInfo.uniformLocations.lightingIntensity, 5.0);
     
      gl.drawElements(gl.TRIANGLES, lightConeBuffer.count, gl.UNSIGNED_SHORT, 0);
     
      // Draw direction triangle/arrow from light toward model center
      // Use the same corrected lightPos (Y at model center) for arrow visualization
      // Fixed size in 3D space - scales naturally with camera
      const arrowBuffer = createDirectionTriangle(gl, lightPos, lightDir, arrowLength, arrowSize);
     
      // Bind arrow buffers
      gl.bindBuffer(gl.ARRAY_BUFFER, arrowBuffer.position);
      gl.vertexAttribPointer(programInfo.attribLocations.vertexPosition, 3, gl.FLOAT, false, 0, 0);
      gl.enableVertexAttribArray(programInfo.attribLocations.vertexPosition);
      gl.bindBuffer(gl.ARRAY_BUFFER, arrowBuffer.normal);
      gl.vertexAttribPointer(programInfo.attribLocations.vertexNormal, 3, gl.FLOAT, false, 0, 0);
      gl.enableVertexAttribArray(programInfo.attribLocations.vertexNormal);
     
      // Use modelViewMatrix for arrow (already in world space)
      gl.uniformMatrix4fv(programInfo.uniformLocations.modelViewMatrix, false, new Float32Array(modelViewMatrix));
      gl.uniformMatrix4fv(programInfo.uniformLocations.normalMatrix, false, new Float32Array(normalM));
      gl.uniform3fv(programInfo.uniformLocations.pillowColor, new Float32Array(lightColor));
      gl.uniform1f(programInfo.uniformLocations.lightingIntensity, 5.0);
     
      // Draw arrow triangles
      gl.drawArrays(gl.TRIANGLES, 0, arrowBuffer.count);
     
      // Clean up arrow buffers
      gl.deleteBuffer(arrowBuffer.position);
      gl.deleteBuffer(arrowBuffer.normal);
     
      // Restore light cone buffer for next iteration
      gl.bindBuffer(gl.ARRAY_BUFFER, lightConeBuffer.position);
      gl.vertexAttribPointer(programInfo.attribLocations.vertexPosition, 3, gl.FLOAT, false, 0, 0);
      gl.enableVertexAttribArray(programInfo.attribLocations.vertexPosition);
      gl.bindBuffer(gl.ARRAY_BUFFER, lightConeBuffer.normal);
      gl.vertexAttribPointer(programInfo.attribLocations.vertexNormal, 3, gl.FLOAT, false, 0, 0);
      gl.enableVertexAttribArray(programInfo.attribLocations.vertexNormal);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, lightConeBuffer.indices);
    }
   
    // Restore lighting intensity
    gl.uniform1f(programInfo.uniformLocations.lightingIntensity, lightingIntensity);
   
    // Restore model buffers for next frame
    gl.bindBuffer(gl.ARRAY_BUFFER, buffers.position);
    gl.vertexAttribPointer(programInfo.attribLocations.vertexPosition, 3, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexPosition);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffers.normal);
    gl.vertexAttribPointer(programInfo.attribLocations.vertexNormal, 3, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexNormal);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffers.textureCoord);
    gl.vertexAttribPointer(programInfo.attribLocations.textureCoord, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(programInfo.attribLocations.textureCoord);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buffers.indices);
  }
}
// Controls
let isDragging = false;
let startX = 0, startY = 0;
let currentRotX = 0, currentRotY = 0;
function handleStart(e) {
  isDragging = true;
  const touch = e.touches ? e.touches[0] : e;
  startX = touch.clientX;
  startY = touch.clientY;
  currentRotX = rotX;
  currentRotY = rotY;
}
function handleEnd() {
  isDragging = false;
}
function handleMove(e) {
  if (!isDragging) return;
  const touch = e.touches ? e.touches[0] : e;
  const deltaX = touch.clientX - startX;
  // Lock Y-axis movement - no vertical rotation, model stays level
  rotY = currentRotY - deltaX * 0.005;
  // rotX remains unchanged - locked to prevent Y-angle viewing
  drawScene();
}
canvas.addEventListener('mousedown', handleStart);
document.addEventListener('mouseup', handleEnd);
document.addEventListener('mousemove', handleMove);
canvas.addEventListener('touchstart', handleStart, { passive: false });
document.addEventListener('touchend', handleEnd, { passive: false });
document.addEventListener('touchmove', handleMove, { passive: false });
canvas.addEventListener('wheel', (e) => {
  if (allowZoom) {
    transZ -= e.deltaY * 0.01;
    transZ = Math.max(-10, Math.min(-0.5, transZ));
    drawScene();
  }
  e.preventDefault();
});


// Auto rotation
// Dynamic font system - loads TTF fonts and generates glyph data for 3D text
let FONT_DATA = {"0":[[0.52099609375,2.7158203125,0.5209445953369141,2.6942367553710938,0.5207901000976562,2.672943115234375,0.5205326080322266,2.6519393920898438,0.520172119140625,2.6312255859375,0.5197086334228516,2.6108016967773438,0.5191421508789062,2.590667724609375,0.5184726715087891,2.5708236694335938,0.5177001953125,2.55126953125,0.5168247222900391,2.5320053100585938,0.5158462524414062,2.513031005859375,0.5147647857666016,2.4943466186523438,0.513580322265625,2.4759521484375,0.5122928619384766,2.4578475952148438,0.5109024047851562,2.440032958984375,0.5094089508056641,2.4225082397460938,0.5078125,2.4052734375,0.5055101182725693,2.382778591579861,0.5029975043402778,2.3608669704861116,0.500274658203125,2.33953857421875,0.49734157986111116,2.318793402777778,0.49419826931423605,2.298631456163194,0.4908447265625,2.279052734375,0.4872809516059028,2.2600572374131946,0.4835069444444444,2.2416449652777777,0.479522705078125,2.22381591796875,0.4753282335069445,2.206570095486111,0.4709235297309028,2.189907497829861,0.46630859375,2.173828125,0.4598102334104938,2.1533383969907405,0.45290195794753085,2.1339699074074074,0.44558376736111116,2.11572265625,0.4378556616512346,2.098596643518518,0.4297176408179012,2.082591869212963,0.42116970486111116,2.0677083333333335,0.4122118537808642,2.0539460358796298,0.4028440875771605,2.041304976851852,0.39306640625,2.02978515625,0.3798628826530613,2.016701211734694,0.36592195471938777,2.0056301817602042,0.35124362244897955,1.9965720663265303,0.33582788584183676,1.9895268654336733,0.3196747448979592,1.9844945790816326,0.302784199617347,1.981475207270408,0.28515625,1.98046875,0.26951416015625,1.981337890625,0.25442382812500003,1.9839453125000006,0.23988525390624998,1.988291015625,0.22589843749999997,1.994375,0.21246337890625,2.002197265625,0.199580078125,2.0117578125,0.18724853515625,2.023056640625,0.17546875,2.03609375,0.16424072265625,2.050869140625,0.153564453125,2.0673828125,0.14627012914540816,2.0801952128507653,0.13924485809948983,2.093799824617347,0.1324886399872449,2.1081966477997445,0.12600147480867346,2.1233856823979593,0.1197833625637755,2.1393669284119894,0.113834303252551,2.1561403858418364,0.108154296875,2.1737060546875,0.10274334343112246,2.1920639349489797,0.09760144292091838,2.2112140266262754,0.09272859534438777,2.2311563297193877,0.08812480070153061,2.251890844228316,0.08379005899234694,2.2734175701530615,0.07972437021683673,2.2957365074936225,0.075927734375,2.31884765625,0.0734283447265625,2.335472412109375,0.07105712890625,2.3524365234375,0.06881408691406249,2.369739990234375,0.06669921875000001,2.3873828125000007,0.0647125244140625,2.405364990234375,0.06285400390625,2.4236865234374996,0.061123657226562496,2.442347412109375,0.059521484374999996,2.46134765625,0.058047485351562504,2.480687255859375,0.05670166015625,2.5003662109375,0.0554840087890625,2.520384521484375,0.05439453125,2.5407421875,0.0534332275390625,2.5614392089843747,0.05260009765625,2.5824755859375,0.0518951416015625,2.603851318359375,0.051318359375000004,2.62556640625,0.050869750976562496,2.6476208496093747,0.05054931640625,2.6700146484375002,0.050357055664062504,2.692747802734375,0.05029296875,2.7158203125,0.050350952148437496,2.738511962890625,0.050524902343750004,2.7608837890625004,0.0508148193359375,2.7829357910156247,0.05122070312500001,2.8046679687500005,0.0517425537109375,2.826080322265625,0.052380371093749996,2.8471728515624997,0.0531341552734375,2.8679455566406244,0.05400390625,2.8883984375000002,0.0549896240234375,2.9085314941406253,0.05609130859375,2.9283447265625,0.0573089599609375,2.9478381347656253,0.058642578125,2.9670117187500002,0.0600921630859375,2.985865478515625,0.06165771484375,3.0043994140625,0.0633392333984375,3.022613525390625,0.06513671875,3.0405078125000005,0.0670501708984375,3.0580822753906247,0.06907958984375,3.0753369140625,0.0712249755859375,3.092271728515625,0.073486328125,3.10888671875,0.07694540218431122,3.132005341198979,0.08069595025510207,3.1543466996173475,0.08473797233737246,3.175910794005102,0.08907146843112246,3.196697624362245,0.09369643853635203,3.216707190688775,0.0986128826530612,3.2359394929846936,0.10382080078125,3.25439453125,0.10932019292091838,3.272072305484694,0.11511105907206634,3.2889728156887754,0.12119339923469388,3.305096061862245,0.127567213408801,3.320442044005102,0.13423250159438777,3.335010762117347,0.14118926379145408,3.348802216198979,0.1484375,3.36181640625,0.15912109375,3.3786083984375,0.17046875000000003,3.3936328125000004,0.18248046874999999,3.4068896484374993,0.19515625,3.41837890625,0.20849609375,3.4281005859375,0.22249999999999998,3.4360546874999995,0.23716796875,3.4422412109375,0.2525,3.44666015625,0.26849609375,3.4493115234375,0.28515625,3.4501953125,0.30098632812500004,3.4493212890625,0.31625000000000003,3.4466992187500005,0.330947265625,3.4423291015624997,0.345078125,3.4362109375,0.358642578125,3.4283447265625,0.37164062499999995,3.4187304687499998,0.38407226562499996,3.4073681640625,0.39593750000000005,3.3942578125000002,0.407236328125,3.3793994140625,0.41796875,3.36279296875,0.4252954599808673,3.3499133051658165,0.4323481345663266,3.3362464126275517,0.43912677375637754,3.321792291135204,0.44563137755102045,3.306550940688776,0.4518619459502551,3.2905223612882653,0.4578184789540816,3.2737065529336733,0.4635009765625,3.256103515625,0.46890943877551017,3.237713249362245,0.47404386559311223,3.218535754145408,0.47890425701530615,3.19857102997449,0.48349061304209184,3.17781907684949,0.4878029336734694,3.1562798947704085,0.49184121890943877,3.133953483737245,0.49560546875,3.11083984375,0.4980810546875,3.0942199707031244,0.5004296875,3.0772705078125,0.5026513671874999,3.059991455078125,0.5047460937500001,3.042382812500001,0.5067138671875,3.024444580078125,0.5085546875,3.0061767578125,0.5102685546875,2.9875793457031254,0.51185546875,2.9686523437499996,0.5133154296875001,2.9493957519531255,0.5146484375,2.9298095703125,0.5158544921874999,2.909893798828125,0.5169335937499999,2.8896484375,0.5178857421875,2.8690734863281246,0.5187109375,2.8481689453124996,0.5194091796875,2.826934814453125,0.51998046875,2.80537109375,0.5204248046875,2.783477783203125,0.5207421875,2.7612548828125,0.5209326171875001,2.738702392578125,0.52099609375,2.7158203125],[0.1318359375,2.7158203125,0.13188831368944637,2.6926125919117645,0.13204544225778547,2.669864430147059,0.1323073232050173,2.6475758272058822,0.13267395653114186,2.625746783088235,0.13314534223615915,2.6043772977941173,0.1337214803200692,2.5834673713235294,0.13440237078287195,2.5630170036764706,0.13518801362456748,2.543026194852941,0.1360784088451557,2.523494944852941,0.1370735564446367,2.5044232536764706,0.13817345642301038,2.4858111213235294,0.13937810878027682,2.4676585477941178,0.140687513516436,2.4499655330882355,0.1421016706314879,2.4327320772058827,0.14362058012543252,2.415958180147059,0.1452442419982699,2.399643841911765,0.14697265625,2.3837890625,0.14988014914772727,2.360194182592975,0.1530983664772727,2.337704997417355,0.15662730823863635,2.31632150697314,0.16046697443181818,2.2960437112603307,0.16461736505681815,2.2768716102789255,0.16907848011363635,2.2588052040289255,0.1738503196022727,2.2418444925103307,0.17893288352272727,2.22598947572314,0.184326171875,2.2112401536673554,0.19003018465909088,2.1975965263429753,0.196044921875,2.18505859375,0.20617426658163268,2.1675701530612246,0.21717055963010204,2.152772241709184,0.22903380102040813,2.140664859693877,0.241763990752551,2.1312480070153064,0.2553611288265306,2.1245216836734695,0.2698252152423469,2.1204858896683674,0.28515625,2.119140625,0.3004872847576531,2.1204759247448983,0.3149513711734694,2.124481823979592,0.32854850924744894,2.1311583227040813,0.34127869897959184,2.1405054209183674,0.353141940369898,2.152523118622449,0.3641382334183674,2.1672114158163267,0.374267578125,2.1845703125,0.3802843330320248,2.1970235020661155,0.38599439888946274,2.2105904571280988,0.39139777569731404,2.22527117768595,0.39649446345557854,2.2410656637396693,0.4012844621642561,2.2579739152892557,0.4057677718233471,2.2759959323347108,0.40994439243285125,2.295131714876033,0.41381432399276863,2.315381262913223,0.4173775665030992,2.336744576446281,0.420634119963843,2.359221655475207,0.423583984375,2.3828125,0.4253402762759515,2.398670658520761,0.42699012624351207,2.414995134083045,0.42853353427768165,2.431785926686851,0.4299705003784602,2.4490430363321796,0.43130102454584773,2.466766463019031,0.4325251067798443,2.4849562067474045,0.4336427470804498,2.503612267517301,0.43465394544766434,2.5227346453287196,0.4355587018814879,2.5423233401816607,0.4363570163819204,2.5623783520761245,0.437048888948962,2.582899681012111,0.43763431958261245,2.6038873269896197,0.438113308282872,2.6253412900086506,0.43848585504974047,2.647261570069204,0.43875195988321797,2.6696481671712804,0.4389116227833045,2.692501081314879,0.43896484375,2.7158203125,0.43891246756055363,2.7386918117430796,0.43875533899221447,2.7611206477076125,0.4384934580449827,2.7831068203935985,0.43812682471885805,2.804650329801038,0.4376554390138408,2.8257511759299305,0.43707930092993075,2.846409358780276,0.4363984104671281,2.866624878352076,0.43561276762543255,2.886397734645329,0.4347223724048443,2.9057279276600343,0.4337272248053633,2.924615457396194,0.4326273248269896,2.943060323853806,0.4314226724697232,2.961062527032872,0.43011326773356406,2.978622066933391,0.4286991106185122,2.9957389435553634,0.42718020112456745,3.0124131568987886,0.42555653925173015,3.028644706963668,0.423828125,3.04443359375,0.42092264979338845,3.067951801394628,0.41771048553719003,3.090388526601239,0.4141916322314049,3.1117437693698347,0.410366089876033,3.132017529700413,0.40623385847107435,3.151209807592975,0.4017949380165289,3.1693206030475207,0.3970493285123967,3.1863499160640494,0.3919970299586777,3.2022977466425617,0.3866380423553719,3.2171640947830578,0.38097236570247933,3.230948960485537,0.375,3.24365234375,0.36491549744897966,3.26139987244898,0.3539142219387755,3.2764170121173466,0.3419961734693877,3.2887037627551017,0.3291613520408163,3.298260124362245,0.31540975765306123,3.3050860969387754,0.30074139030612246,3.309181680484694,0.28515625,3.310546875,0.2695711096938776,3.309181680484694,0.25490274234693877,3.3050860969387754,0.24115114795918366,3.2982601243622445,0.22831632653061223,3.2887037627551017,0.2163982780612245,3.276417012117347,0.20539700255102042,3.2613998724489797,0.1953125,3.24365234375,0.18934215198863635,3.230948960485537,0.18368252840909088,3.2171640947830578,0.17833362926136365,3.2022977466425617,0.17329545454545456,3.1863499160640494,0.16856800426136362,3.1693206030475203,0.1641512784090909,3.1512098075929753,0.16004527698863635,3.132017529700413,0.15625,3.111743769369834,0.15276544744318182,3.09038852660124,0.14959161931818182,3.067951801394628,0.146728515625,3.04443359375,0.14502797902249137,3.028644706963668,0.14343050524437714,3.0124131568987886,0.14193609429065746,2.995738943555363,0.14054474616133217,2.978622066933391,0.13925646085640137,2.9610625270328716,0.13807123837586505,2.943060323853806,0.13698907871972318,2.924615457396194,0.13600998188797578,2.9057279276600347,0.13513394788062283,2.8863977346453287,0.13436097669766436,2.866624878352076,0.13369106833910033,2.846409358780277,0.1331242228049308,2.825751175929931,0.1326604400951557,2.804650329801038,0.1322997202097751,2.783106820393599,0.13204206314878894,2.7611206477076125,0.13188746891219724,2.7386918117430796,0.1318359375,2.7158203125]],"1":[[0.35107421875,3.427734375,0.35107421875,3.4079047309027777,0.35107421875,3.3880750868055554,0.35107421875,3.3682454427083335,0.35107421875,3.348415798611111,0.35107421875,3.328586154513889,0.35107421875,3.3087565104166665,0.35107421875,3.2889268663194446,0.35107421875,3.2690972222222223,0.35107421875,3.249267578125,0.35107421875,3.2294379340277777,0.35107421875,3.2096082899305554,0.35107421875,3.1897786458333335,0.35107421875,3.169949001736111,0.35107421875,3.150119357638889,0.35107421875,3.1302897135416665,0.35107421875,3.1104600694444446,0.35107421875,3.0906304253472223,0.35107421875,3.07080078125,0.35107421875,3.0509711371527777,0.35107421875,3.0311414930555554,0.35107421875,3.0113118489583335,0.35107421875,2.991482204861111,0.35107421875,2.971652560763889,0.35107421875,2.9518229166666665,0.35107421875,2.9319932725694446,0.35107421875,2.9121636284722223,0.35107421875,2.892333984375,0.35107421875,2.8725043402777777,0.35107421875,2.8526746961805554,0.35107421875,2.832845052083333,0.35107421875,2.813015407986111,0.35107421875,2.793185763888889,0.35107421875,2.773356119791667,0.35107421875,2.7535264756944446,0.35107421875,2.7336968315972223,0.35107421875,2.7138671875,0.35107421875,2.6940375434027777,0.35107421875,2.6742078993055554,0.35107421875,2.6543782552083335,0.35107421875,2.634548611111111,0.35107421875,2.614718967013889,0.35107421875,2.5948893229166665,0.35107421875,2.5750596788194446,0.35107421875,2.5552300347222223,0.35107421875,2.535400390625,0.35107421875,2.5155707465277777,0.35107421875,2.4957411024305554,0.35107421875,2.4759114583333335,0.35107421875,2.456081814236111,0.35107421875,2.436252170138889,0.35107421875,2.4164225260416665,0.35107421875,2.3965928819444446,0.35107421875,2.3767632378472223,0.35107421875,2.35693359375,0.35107421875,2.3371039496527777,0.35107421875,2.3172743055555554,0.35107421875,2.2974446614583335,0.35107421875,2.277615017361111,0.35107421875,2.257785373263889,0.35107421875,2.2379557291666665,0.35107421875,2.2181260850694446,0.35107421875,2.1982964409722223,0.35107421875,2.178466796875,0.35107421875,2.1586371527777777,0.35107421875,2.1388075086805554,0.35107421875,2.1189778645833335,0.35107421875,2.099148220486111,0.35107421875,2.079318576388889,0.35107421875,2.0594889322916665,0.35107421875,2.0396592881944446,0.35107421875,2.0198296440972223,0.35107421875,2,0.331298828125,2,0.3115234375,2,0.291748046875,2,0.27197265625,2,0.27197265625,2.0198376225490198,0.27197265625,2.039675245098039,0.27197265625,2.059512867647059,0.27197265625,2.079350490196078,0.27197265625,2.099188112745098,0.27197265625,2.1190257352941178,0.27197265625,2.138863357843137,0.27197265625,2.158700980392157,0.27197265625,2.1785386029411766,0.27197265625,2.198376225490196,0.27197265625,2.2182138480392157,0.27197265625,2.2380514705882355,0.27197265625,2.257889093137255,0.27197265625,2.2777267156862746,0.27197265625,2.297564338235294,0.27197265625,2.3174019607843137,0.27197265625,2.3372395833333335,0.27197265625,2.357077205882353,0.27197265625,2.3769148284313726,0.27197265625,2.396752450980392,0.27197265625,2.4165900735294117,0.27197265625,2.4364276960784315,0.27197265625,2.456265318627451,0.27197265625,2.4761029411764706,0.27197265625,2.4959405637254903,0.27197265625,2.5157781862745097,0.27197265625,2.5356158088235294,0.27197265625,2.555453431372549,0.27197265625,2.5752910539215685,0.27197265625,2.5951286764705883,0.27197265625,2.6149662990196076,0.27197265625,2.6348039215686274,0.27197265625,2.654641544117647,0.27197265625,2.6744791666666665,0.27197265625,2.6943167892156863,0.27197265625,2.714154411764706,0.27197265625,2.7339920343137254,0.27197265625,2.753829656862745,0.27197265625,2.7736672794117645,0.27197265625,2.7935049019607843,0.27197265625,2.813342524509804,0.27197265625,2.8331801470588234,0.27197265625,2.853017769607843,0.27197265625,2.872855392156863,0.27197265625,2.8926930147058822,0.27197265625,2.912530637254902,0.27197265625,2.9323682598039214,0.27197265625,2.952205882352941,0.27197265625,2.972043504901961,0.27197265625,2.9918811274509802,0.27197265625,3.01171875,0.27198242187500005,3.0341210937500005,0.27201171875,3.0552343750000004,0.27206054687500003,3.0750585937499997,0.27212890625,3.09359375,0.272216796875,3.11083984375,0.272369384765625,3.13116455078125,0.2725830078125,3.150634765625,0.272857666015625,3.16925048828125,0.273193359375,3.18701171875,0.2736053466796875,3.204437255859375,0.27410888671875,3.2220458984375,0.2747039794921875,3.239837646484375,0.275390625,3.2578125,0.2679443359375,3.242767333984375,0.2607421875,3.2288818359375,0.2537841796875,3.216156005859375,0.2470703125,3.20458984375,0.240081787109375,3.192962646484375,0.2322998046875,3.1800537109375,0.223724365234375,3.165863037109375,0.21435546875,3.150390625,0.20416259765625,3.134033203125,0.1939697265625,3.11767578125,0.18377685546875,3.101318359375,0.173583984375,3.0849609375,0.16339111328125,3.068603515625,0.1531982421875,3.05224609375,0.14300537109375,3.035888671875,0.1328125,3.01953125,0.125732421875,3.0379231770833335,0.11865234375,3.0563151041666665,0.111572265625,3.07470703125,0.1044921875,3.0930989583333335,0.097412109375,3.1114908854166665,0.09033203125,3.1298828125,0.10107421875,3.1464301215277777,0.11181640625,3.1629774305555554,0.12255859375,3.1795247395833335,0.13330078125,3.196072048611111,0.14404296875,3.212619357638889,0.15478515625,3.2291666666666665,0.16552734375,3.2457139756944446,0.17626953125,3.2622612847222223,0.18701171875,3.27880859375,0.19775390625,3.2953559027777777,0.20849609375,3.3119032118055554,0.21923828125,3.3284505208333335,0.22998046875,3.344997829861111,0.24072265625,3.361545138888889,0.25146484375,3.3780924479166665,0.26220703125,3.3946397569444446,0.27294921875,3.4111870659722223,0.28369140625,3.427734375,0.300537109375,3.427734375,0.3173828125,3.427734375,0.334228515625,3.427734375,0.35107421875,3.427734375]],"2":[[0.51708984375,2.1484375,0.51708984375,2.1298828125,0.51708984375,2.111328125,0.51708984375,2.0927734375,0.51708984375,2.07421875,0.51708984375,2.0556640625,0.51708984375,2.037109375,0.51708984375,2.0185546875,0.51708984375,2,0.4975992838541667,2,0.4781087239583333,2,0.4586181640625,2,0.4391276041666667,2,0.4196370442708333,2,0.400146484375,2,0.38065592447916663,2,0.36116536458333337,2,0.3416748046875,2,0.32218424479166663,2,0.30269368489583337,2,0.283203125,2,0.2637125651041667,2,0.24422200520833331,2,0.2247314453125,2,0.20524088541666669,2,0.18575032552083331,2,0.166259765625,2,0.14676920572916669,2,0.12727864583333331,2,0.1077880859375,2,0.08829752604166669,2,0.06880696614583331,2,0.04931640625,2,0.04931640625,2.0193917410714284,0.04931640625,2.038783482142857,0.04931640625,2.0581752232142856,0.04931640625,2.0775669642857144,0.04931640625,2.096958705357143,0.04931640625,2.1163504464285716,0.04931640625,2.1357421875,0.05799449573863637,2.1533647017045454,0.06667258522727273,2.170987215909091,0.07535067471590909,2.1886097301136362,0.08402876420454546,2.2062322443181817,0.09270685369318182,2.223854758522727,0.10138494318181818,2.241477272727273,0.11006303267045454,2.2590997869318183,0.11874112215909091,2.2767223011363638,0.1274192116477273,2.294344815340909,0.13609730113636365,2.3119673295454546,0.144775390625,2.32958984375,0.15345348011363635,2.3472123579545454,0.16213156960227273,2.364834872159091,0.1708096590909091,2.3824573863636362,0.17948774857954544,2.4000799005681817,0.18816583806818182,2.4177024147727275,0.19684392755681818,2.4353249289772725,0.20552201704545456,2.4529474431818183,0.2142001065340909,2.4705699573863638,0.22287819602272727,2.488192471590909,0.23155628551136365,2.5058149857954546,0.240234375,2.5234375,0.2497720009039256,2.5427750516528924,0.25903118543388426,2.56172520661157,0.268011928589876,2.580287964876033,0.27671423037190085,2.598463326446281,0.28513809077995866,2.616251291322314,0.2932835098140496,2.6336518595041323,0.3011504874741735,2.6506650309917354,0.3087390237603306,2.6672908057851235,0.31604911867252067,2.6835291838842976,0.32308077221074377,2.699380165289256,0.329833984375,2.71484375,0.33770375192901236,2.733476803626543,0.34514551986882713,2.752049575617284,0.3521592881944445,2.7705620659722228,0.35874505690586417,2.789014274691358,0.36490282600308643,2.8074062017746915,0.3706325954861111,2.8257378472222223,0.3759343653549383,2.844009211033951,0.38080813560956794,2.8622202932098766,0.38525390625,2.88037109375,0.38887207031250004,2.8968408203125002,0.39210937500000004,2.913632812500001,0.39496582031249994,2.9307470703124996,0.39744140625,2.94818359375,0.3995361328125,2.9659423828125,0.40125,2.9840234375,0.4025830078125,3.0024267578125,0.40353515625,3.02115234375,0.4041064453125,3.0402001953125,0.404296875,3.0595703125,0.40393798828125,3.0832177734375,0.4028613281250001,3.1057226562500007,0.40106689453125,3.1270849609374998,0.3985546875,3.1473046875,0.39532470703125,3.1663818359375,0.39137695312500004,3.18431640625,0.38671142578125,3.2011083984375,0.381328125,3.2167578125,0.37522705078125,3.2312646484375005,0.368408203125,3.24462890625,0.3576909279336736,3.2615991709183683,0.3460469148596939,3.275958625637755,0.3334761639030612,3.287707270408163,0.31997867506377553,3.296845105229592,0.30555444834183676,3.3033721301020407,0.2902034837372449,3.30728834502551,0.27392578125,3.30859375,0.2571614583333333,3.307617187500001,0.24104817708333337,3.3046875,0.2255859375,3.2998046875,0.21077473958333331,3.29296875,0.19661458333333331,3.2841796875,0.18310546875,3.2734375,0.17176538584183676,3.262794961734694,0.16034558354591838,3.2509566326530615,0.14884606186224486,3.2379225127551012,0.13726682079081634,3.223692602040816,0.12560786033163265,3.2082669005102042,0.11386918048469388,3.1916454081632653,0.10205078125,3.173828125,0.09480794270833333,3.1920572916666665,0.08756510416666667,3.2102864583333335,0.080322265625,3.228515625,0.07307942708333334,3.2467447916666665,0.06583658854166666,3.2649739583333335,0.05859375,3.283203125,0.06817626953125001,3.2988552517361116,0.07804361979166667,3.313910590277778,0.08819580078125,3.328369140625,0.0986328125,3.3422309027777777,0.10935465494791667,3.355495876736111,0.120361328125,3.3681640625,0.13393554687500003,3.382382812500001,0.14790039062500002,3.3953515624999997,0.16225585937499998,3.4070703125,0.17700195312500003,3.4175390625,0.192138671875,3.4267578125,0.20767578125000005,3.4344921875000005,0.223623046875,3.4405078125,0.23998046875,3.4448046874999996,0.256748046875,3.4473828125000003,0.27392578125,3.4482421875,0.29239078443877553,3.447305484693878,0.3101183832908163,3.44449537627551,0.32710857780612246,3.4398118622448974,0.34336136798469385,3.4332549426020407,0.3588767538265306,3.424824617346939,0.3736547353316326,3.4145208864795915,0.3876953125,3.40234375,0.3993186950683594,3.3902587890625,0.4102630615234375,3.376953125,0.4205284118652344,3.3624267578125,0.43011474609375,3.3466796875,0.4390220642089844,3.3297119140625,0.4472503662109375,3.3115234375,0.4547996520996094,3.2921142578125,0.461669921875,3.271484375,0.4662036738119834,3.2557705965909087,0.47030563985020657,3.2395241477272725,0.4739758199896694,3.222745028409091,0.47721421423037186,3.2054332386363638,0.480020822572314,3.187588778409091,0.48239564501549587,3.169211647727273,0.4843386815599174,3.150301846590909,0.4858499322055785,3.130859375,0.4869293969524794,3.110884232954546,0.4875770758006198,3.090376420454546,0.48779296875,3.0693359375,0.4876073411673553,3.0488725142045454,0.48705045841942146,3.0286754261363633,0.48612232050619825,3.008744673295454,0.48482292742768596,2.9890802556818183,0.48315227918388426,2.969682173295454,0.48111037577479343,2.9505504261363638,0.47869721720041325,2.931685014204545,0.47591280346074377,2.9130859375,0.47275713455578516,2.8947531960227275,0.4692302104855372,2.8766867897727275,0.46533203125,2.85888671875,0.4610989152892562,2.8412359568698347,0.4565671810433884,2.8236174780475203,0.45173682851239666,2.8060312822830578,0.44660785769628103,2.788477369576446,0.4411802685950413,2.770955739927685,0.4354540612086777,2.753466393336777,0.42942923553719003,2.736009329803719,0.4231057915805785,2.7185845493285123,0.4164837293388429,2.701192051911157,0.40956304881198347,2.683831837551653,0.40234375,2.66650390625,0.3954942491319444,2.6505567762586804,0.38845486111111116,2.6344536675347228,0.3812255859375,2.618194580078125,0.37380642361111116,2.6017795138888893,0.3661973741319444,2.5852084689670134,0.3583984375,2.5684814453125,0.3504096137152778,2.5515984429253473,0.3422309027777778,2.534559461805556,0.3338623046875,2.517364501953125,0.3253038194444445,2.500013563368056,0.3165554470486111,2.4825066460503473,0.3076171875,2.46484375,0.2989908854166667,2.4476453993055554,0.2903645833333333,2.430447048611111,0.28173828125,2.4132486979166665,0.2731119791666667,2.3960503472222223,0.2644856770833333,2.3788519965277777,0.255859375,2.3616536458333335,0.24723307291666666,2.344455295138889,0.23860677083333334,2.3272569444444446,0.22998046875,2.31005859375,0.22135416666666666,2.2928602430555554,0.21272786458333331,2.275661892361111,0.2041015625,2.2584635416666665,0.19547526041666669,2.2412651909722223,0.18684895833333331,2.2240668402777777,0.17822265625,2.2068684895833335,0.16959635416666669,2.189670138888889,0.16097005208333334,2.1724717881944446,0.15234375,2.1552734375,0.15234375,2.1484375,0.17154091282894737,2.1484375,0.19073807565789475,2.1484375,0.2099352384868421,2.1484375,0.22913240131578946,2.1484375,0.24832956414473684,2.1484375,0.2675267269736842,2.1484375,0.2867238898026316,2.1484375,0.3059210526315789,2.1484375,0.3251182154605263,2.1484375,0.3443153782894737,2.1484375,0.3635125411184211,2.1484375,0.3827097039473684,2.1484375,0.4019068667763158,2.1484375,0.42110402960526316,2.1484375,0.4403011924342105,2.1484375,0.45949835526315785,2.1484375,0.47869551809210525,2.1484375,0.4978926809210526,2.1484375,0.51708984375,2.1484375]],"3":[[0.49072265625,3.0947265625,0.49049358603395055,3.0737726658950613,0.48980637538580246,3.053445698302469,0.48866102430555564,3.0337456597222228,0.4870575327932099,3.014672550154321,0.4849959008487654,2.9962263695987654,0.4824761284722222,2.9784071180555554,0.4794982156635802,2.9612147955246915,0.4760621624228395,2.944649402006173,0.47216796875,2.9287109375,0.46656768176020413,2.9091497927295924,0.4603694993622449,2.890644929846939,0.4535734215561224,2.8731963488520407,0.44617944834183676,2.856804049744898,0.43818757971938777,2.84146803252551,0.42959781568877553,2.8271882971938775,0.42041015625,2.81396484375,0.40901692708333337,2.7998996310763893,0.39697265625000006,2.7873263888888893,0.38427734375,2.7762451171875,0.37093098958333337,2.7666558159722223,0.35693359375,2.758558485243056,0.34228515625,2.751953125,0.34228515625,2.744140625,0.35885498046875003,2.7391796875000005,0.37458007812500005,2.7328906250000005,0.38946044921875,2.7252734375,0.40349609375,2.7163281250000004,0.41668701171875,2.7060546875,0.42903320312500004,2.694453125,0.44053466796875,2.6815234375,0.45119140625,2.667265625,0.46100341796875,2.6516796875,0.469970703125,2.634765625,0.4762909116124261,2.6209203957100593,0.482105503420858,2.606427977071006,0.4874144785502958,2.59128836908284,0.4922178370007397,2.575501571745562,0.49651557877218944,2.559067585059172,0.500307703864645,2.541986409023669,0.5035942122781065,2.5242580436390534,0.506375104012574,2.5058824889053253,0.5086503790680473,2.4868597448224854,0.5104200374445266,2.467189811390533,0.5116840791420119,2.446872688609467,0.512442504160503,2.42590837647929,0.5126953125,2.404296875,0.5124935574001735,2.383822970920139,0.5118882921006945,2.363742404513889,0.5108795166015625,2.34405517578125,0.5094672309027779,2.3247612847222223,0.5076514350043403,2.3058607313368054,0.50543212890625,2.287353515625,0.5028093126085069,2.2692396375868054,0.4997829861111111,2.2515190972222223,0.4963531494140625,2.23419189453125,0.4925198025173611,2.217258029513889,0.4882829454210069,2.200717502170139,0.483642578125,2.1845703125,0.47752441406250007,2.1658447265625003,0.4707910156250001,2.1479492187500004,0.46344238281249994,2.1308837890624996,0.455478515625,2.1146484375,0.4468994140625,2.0992431640625,0.43770507812500004,2.08466796875,0.4278955078125,2.0709228515625,0.417470703125,2.0580078125,0.40643066406249995,2.0459228515625,0.394775390625,2.03466796875,0.38107036072530864,2.023292824074074,0.36653947241512347,2.013255931712963,0.35118272569444453,2.004557291666667,0.33500012056327166,1.9971969039351851,0.3179916570216049,1.9911747685185186,0.3001573350694445,1.9864908854166667,0.28149715470679015,1.9831452546296295,0.262011115933642,1.9811378761574074,0.24169921875,1.98046875,0.22305636935763892,1.9809705946180558,0.20488823784722227,1.9824761284722225,0.18719482421875,1.9849853515625,0.1699761284722222,1.9884982638888888,0.1532321506076389,1.9930148654513888,0.136962890625,1.99853515625,0.12104627821180555,2.005221896701389,0.10536024305555558,2.0132378472222223,0.08990478515625,2.0225830078125,0.0746799045138889,2.0332573784722223,0.059685601128472224,2.045260959201389,0.044921875,2.05859375,0.044921875,2.07763671875,0.044921875,2.0966796875,0.044921875,2.11572265625,0.044921875,2.134765625,0.044921875,2.15380859375,0.044921875,2.1728515625,0.044921875,2.19189453125,0.044921875,2.2109375,0.05777662627551021,2.198770328443878,0.07095025510204081,2.187380420918367,0.08444276147959183,2.176767777423469,0.09825414540816327,2.166932397959184,0.11238440688775511,2.15787428252551,0.12683354591836735,2.149593431122449,0.1416015625,2.14208984375,0.15892198350694445,2.1344807942708335,0.17605251736111113,2.1282552083333335,0.1929931640625,2.1234130859375,0.2097439236111111,2.1199544270833335,0.22630479600694445,2.1178792317708335,0.24267578125,2.1171875,0.26166503906250005,2.1179638671875,0.2795703125000001,2.1202929687500003,0.2963916015625,2.1241748046874998,0.31212890625,2.129609375,0.3267822265625,2.1365966796875,0.3403515625,2.14513671875,0.35283691406249995,2.1552294921875,0.36423828125,2.166875,0.3745556640625,2.1800732421875,0.3837890625,2.19482421875,0.3907301161024305,2.208160400390625,0.3970675998263889,2.2222900390625004,0.402801513671875,2.237213134765625,0.40793185763888895,2.2529296875,0.41245863172743047,2.269439697265625,0.4163818359375,2.2867431640625,0.4197014702690972,2.304840087890625,0.4224175347222222,2.32373046875,0.424530029296875,2.343414306640625,0.4260389539930556,2.3638916015625,0.42694430881076384,2.385162353515625,0.42724609375,2.4072265625,0.4268684387207031,2.4298629760742188,0.4257354736328125,2.451385498046875,0.4238471984863281,2.4717941284179688,0.42120361328125,2.4910888671875,0.4178047180175781,2.5092697143554688,0.4136505126953125,2.526336669921875,0.4087409973144531,2.5422897338867188,0.403076171875,2.55712890625,0.3944363064236111,2.575330946180556,0.3846299913194445,2.5918511284722223,0.3736572265625,2.606689453125,0.3615180121527778,2.6198459201388893,0.3482123480902778,2.631320529513889,0.333740234375,2.64111328125,0.31817626953125006,2.649319118923611,0.30159505208333337,2.6560329861111116,0.28399658203125,2.6612548828125,0.265380859375,2.6649848090277777,0.24574788411458331,2.6672227647569446,0.22509765625,2.66796875,0.207275390625,2.66796875,0.189453125,2.66796875,0.171630859375,2.66796875,0.15380859375,2.66796875,0.15380859375,2.687779017857143,0.15380859375,2.7075892857142856,0.15380859375,2.7273995535714284,0.15380859375,2.7472098214285716,0.15380859375,2.7670200892857144,0.15380859375,2.786830357142857,0.15380859375,2.806640625,0.1717529296875,2.806640625,0.189697265625,2.806640625,0.2076416015625,2.806640625,0.2255859375,2.806640625,0.24446614583333337,2.8075900607638893,0.2623697916666667,2.810438368055556,0.279296875,2.815185546875,0.2952473958333333,2.8218315972222223,0.31022135416666663,2.8303765190972223,0.32421875,2.8408203125,0.3371921115451389,2.8529866536458335,0.3490939670138889,2.8666992187500004,0.35992431640625,2.8819580078125,0.3696831597222222,2.8987630208333335,0.3783704969618056,2.9171142578125,0.385986328125,2.93701171875,0.3909645080566406,2.9528732299804688,0.3952789306640625,2.969451904296875,0.3989295959472656,2.9867477416992188,0.40191650390625,3.0047607421875,0.4042396545410156,3.0234909057617188,0.4058990478515625,3.042938232421875,0.4068946838378906,3.0631027221679688,0.4072265625,3.083984375,0.4067804783950617,3.1074640721450617,0.4054422260802469,3.129726080246914,0.40321180555555564,3.150770399305556,0.4000892168209877,3.1705970293209877,0.3960744598765432,3.1892059702932096,0.3911675347222222,3.2065972222222223,0.3853684413580247,3.2227707851080245,0.3786771797839506,3.2377266589506175,0.37109375,3.25146484375,0.3602419483418368,3.2671396683673475,0.3483338647959184,3.2804029815051017,0.33536949936224486,3.291254783163265,0.3213488520408163,3.2996950733418364,0.3062719228316326,3.3057238520408165,0.29013871173469385,3.3093411192602042,0.27294921875,3.310546875,0.25433349609375,3.3095703125,0.236572265625,3.306640625,0.21966552734375,3.3017578125,0.20361328125,3.294921875,0.188232421875,3.286376953125,0.17333984375,3.2763671875,0.158935546875,3.264892578125,0.14501953125,3.251953125,0.13408203125,3.2406640625,0.12314453125,3.2286718750000003,0.11220703124999999,3.2159765625,0.10126953125,3.202578125,0.09033203125,3.1884765625,0.08349609375,3.20703125,0.07666015625,3.2255859375,0.06982421875,3.244140625,0.06298828125,3.2626953125,0.05615234375,3.28125,0.04931640625,3.2998046875,0.05934906005859375,3.3146820068359375,0.069915771484375,3.32904052734375,0.08101654052734375,3.3428802490234375,0.0926513671875,3.356201171875,0.10482025146484375,3.3690032958984375,0.117523193359375,3.38128662109375,0.13076019287109375,3.3930511474609375,0.14453125,3.404296875,0.15880584716796875,3.4145965576171875,0.173553466796875,3.42352294921875,0.18877410888671875,3.4310760498046875,0.2044677734375,3.437255859375,0.22063446044921875,3.4420623779296875,0.237274169921875,3.44549560546875,0.25438690185546875,3.4475555419921875,0.27197265625,3.4482421875,0.2898220486111111,3.447550455729166,0.3069118923611111,3.445475260416667,0.3232421875,3.4420166015625,0.3388129340277778,3.437174479166667,0.3536241319444444,3.430948893229166,0.36767578125,3.42333984375,0.3809678819444444,3.414347330729167,0.3935004340277778,3.403971354166667,0.4052734375,3.3922119140625,0.4162868923611111,3.379069010416667,0.42654079861111105,3.3645426432291665,0.43603515625,3.3486328125,0.4435686383928572,3.3341238839285716,0.4505440848214286,3.3190569196428577,0.4569614955357143,3.3034319196428577,0.4628208705357143,3.2872488839285716,0.4681222098214285,3.2705078124999996,0.4728655133928571,3.2532087053571423,0.47705078125,3.2353515625,0.48067801339285715,3.216936383928571,0.4837472098214286,3.197963169642857,0.4862583705357143,3.178431919642857,0.4882114955357143,3.158342633928571,0.4896065848214286,3.1376953125,0.49044363839285715,3.116489955357143,0.49072265625,3.0947265625]],"4":[[0.552734375,2.4775390625,0.552734375,2.459228515625,0.552734375,2.44091796875,0.552734375,2.422607421875,0.552734375,2.404296875,0.552734375,2.385986328125,0.552734375,2.36767578125,0.552734375,2.349365234375,0.552734375,2.3310546875,0.534912109375,2.3310546875,0.51708984375,2.3310546875,0.499267578125,2.3310546875,0.4814453125,2.3310546875,0.463623046875,2.3310546875,0.44580078125,2.3310546875,0.44580078125,2.311580882352941,0.44580078125,2.2921070772058822,0.44580078125,2.2726332720588234,0.44580078125,2.2531594669117645,0.44580078125,2.233685661764706,0.44580078125,2.214211856617647,0.44580078125,2.1947380514705883,0.44580078125,2.1752642463235294,0.44580078125,2.1557904411764706,0.44580078125,2.1363166360294117,0.44580078125,2.116842830882353,0.44580078125,2.097369025735294,0.44580078125,2.0778952205882355,0.44580078125,2.0584214154411766,0.44580078125,2.0389476102941178,0.44580078125,2.019473805147059,0.44580078125,2,0.426025390625,2,0.40625,2,0.386474609375,2,0.36669921875,2,0.36669921875,2.019473805147059,0.36669921875,2.0389476102941178,0.36669921875,2.0584214154411766,0.36669921875,2.0778952205882355,0.36669921875,2.097369025735294,0.36669921875,2.116842830882353,0.36669921875,2.1363166360294117,0.36669921875,2.1557904411764706,0.36669921875,2.1752642463235294,0.36669921875,2.1947380514705883,0.36669921875,2.214211856617647,0.36669921875,2.233685661764706,0.36669921875,2.2531594669117645,0.36669921875,2.2726332720588234,0.36669921875,2.2921070772058822,0.36669921875,2.311580882352941,0.36669921875,2.3310546875,0.3475206163194444,2.3310546875,0.3283420138888889,2.3310546875,0.3091634114583333,2.3310546875,0.2899848090277778,2.3310546875,0.2708062065972222,2.3310546875,0.2516276041666667,2.3310546875,0.2324490017361111,2.3310546875,0.21327039930555555,2.3310546875,0.194091796875,2.3310546875,0.17491319444444445,2.3310546875,0.15573459201388887,2.3310546875,0.13655598958333334,2.3310546875,0.11737738715277779,2.3310546875,0.09819878472222221,2.3310546875,0.07902018229166663,2.3310546875,0.059841579861111105,2.3310546875,0.04066297743055558,2.3310546875,0.021484375,2.3310546875,0.021484375,2.3505859375,0.021484375,2.3701171875,0.021484375,2.3896484375,0.021484375,2.4091796875,0.021484375,2.4287109375,0.021484375,2.4482421875,0.021484375,2.4677734375,0.028019831730769232,2.486384465144231,0.034555288461538464,2.5049954927884617,0.041090745192307696,2.5236065204326925,0.04762620192307693,2.542217548076923,0.05416165865384615,2.5608285757211537,0.060697115384615384,2.5794396033653846,0.06723257211538461,2.5980506310096154,0.07376802884615385,2.6166616586538463,0.08030348557692307,2.635272686298077,0.0868389423076923,2.6538837139423075,0.09337439903846154,2.6724947415865383,0.09990985576923077,2.691105769230769,0.1064453125,2.709716796875,0.11298076923076923,2.728327824519231,0.11951622596153845,2.7469388521634617,0.1260516826923077,2.7655498798076925,0.13258713942307693,2.784160907451923,0.13912259615384615,2.8027719350961537,0.14565805288461536,2.8213829627403846,0.1521935096153846,2.8399939903846154,0.15872896634615385,2.8586050180288463,0.16526442307692307,2.877216045673077,0.1717998798076923,2.8958270733173075,0.17833533653846154,2.9144381009615383,0.18487079326923078,2.933049128605769,0.19140625,2.95166015625,0.19794170673076925,2.970271183894231,0.20447716346153846,2.9888822115384617,0.2110126201923077,3.0074932391826925,0.2175480769230769,3.026104266826923,0.22408353365384615,3.0447152944711537,0.2306189903846154,3.0633263221153846,0.2371544471153846,3.0819373497596154,0.24368990384615385,3.1005483774038463,0.25022536057692313,3.119159405048077,0.2567608173076923,3.1377704326923075,0.26329627403846156,3.1563814603365383,0.2698317307692307,3.174992487980769,0.2763671875,3.193603515625,0.2829026442307692,3.212214543269231,0.28943810096153844,3.2308255709134617,0.2959735576923077,3.2494365985576925,0.3025090144230769,3.268047626201923,0.30904447115384615,3.2866586538461537,0.3155799278846154,3.3052696814903846,0.3221153846153846,3.3238807091346154,0.32865084134615385,3.3424917367788463,0.3351862980769231,3.361102764423077,0.3417217548076923,3.3797137920673075,0.34825721153846156,3.3983248197115383,0.3547926682692308,3.416935847355769,0.361328125,3.435546875,0.37822265625,3.435546875,0.3951171875,3.435546875,0.41201171875,3.435546875,0.42890625,3.435546875,0.44580078125,3.435546875,0.44580078125,3.41558837890625,0.44580078125,3.3956298828125,0.44580078125,3.37567138671875,0.44580078125,3.355712890625,0.44580078125,3.33575439453125,0.44580078125,3.3157958984375,0.44580078125,3.29583740234375,0.44580078125,3.27587890625,0.44580078125,3.25592041015625,0.44580078125,3.2359619140625,0.44580078125,3.21600341796875,0.44580078125,3.196044921875,0.44580078125,3.17608642578125,0.44580078125,3.1561279296875,0.44580078125,3.13616943359375,0.44580078125,3.1162109375,0.44580078125,3.09625244140625,0.44580078125,3.0762939453125,0.44580078125,3.05633544921875,0.44580078125,3.036376953125,0.44580078125,3.01641845703125,0.44580078125,2.9964599609375,0.44580078125,2.97650146484375,0.44580078125,2.95654296875,0.44580078125,2.93658447265625,0.44580078125,2.9166259765625,0.44580078125,2.89666748046875,0.44580078125,2.876708984375,0.44580078125,2.85675048828125,0.44580078125,2.8367919921875,0.44580078125,2.81683349609375,0.44580078125,2.796875,0.44580078125,2.77691650390625,0.44580078125,2.7569580078125,0.44580078125,2.73699951171875,0.44580078125,2.717041015625,0.44580078125,2.69708251953125,0.44580078125,2.6771240234375,0.44580078125,2.65716552734375,0.44580078125,2.63720703125,0.44580078125,2.61724853515625,0.44580078125,2.5972900390625,0.44580078125,2.57733154296875,0.44580078125,2.557373046875,0.44580078125,2.53741455078125,0.44580078125,2.5174560546875,0.44580078125,2.49749755859375,0.44580078125,2.4775390625,0.463623046875,2.4775390625,0.4814453125,2.4775390625,0.499267578125,2.4775390625,0.51708984375,2.4775390625,0.534912109375,2.4775390625,0.552734375,2.4775390625],[0.10205078125,2.4775390625,0.12095424107142858,2.4775390625,0.13985770089285715,2.4775390625,0.1587611607142857,2.4775390625,0.1776646205357143,2.4775390625,0.19656808035714285,2.4775390625,0.21547154017857142,2.4775390625,0.234375,2.4775390625,0.2532784598214286,2.4775390625,0.27218191964285715,2.4775390625,0.2910853794642857,2.4775390625,0.3099888392857143,2.4775390625,0.32889229910714285,2.4775390625,0.3477957589285714,2.4775390625,0.36669921875,2.4775390625,0.36669921875,2.4969889322916665,0.36669921875,2.5164388020833335,0.36669921875,2.535888671875,0.36669921875,2.5553385416666665,0.36669921875,2.5747884114583335,0.36669921875,2.59423828125,0.36669921875,2.6136881510416665,0.36669921875,2.6331380208333335,0.36669921875,2.652587890625,0.36669921875,2.6720377604166665,0.36669921875,2.6914876302083335,0.36669921875,2.7109375,0.36669921875,2.7303873697916665,0.36669921875,2.7498372395833335,0.36669921875,2.769287109375,0.36669921875,2.7887369791666665,0.36669921875,2.8081868489583335,0.36669921875,2.82763671875,0.36669921875,2.8470865885416665,0.36669921875,2.8665364583333335,0.36669921875,2.885986328125,0.36669921875,2.9054361979166665,0.36669921875,2.9248860677083335,0.36669921875,2.9443359375,0.3667187500000001,2.9654101562500004,0.36677734375,2.9856640625,0.36687499999999995,3.0050976562500002,0.36701171875000005,3.0237109375,0.3671875,3.04150390625,0.3674023437500001,3.058652343750001,0.36765625,3.07533203125,0.36794921875,3.09154296875,0.36828125,3.10728515625,0.36865234375,3.12255859375,0.3691253662109375,3.141143798828125,0.36956787109375,3.1593017578125,0.3699798583984375,3.177032470703125,0.370361328125,3.1943359375,0.3707122802734375,3.21136474609375,0.37103271484375,3.228271484375,0.3713226318359375,3.24505615234375,0.37158203125,3.26171875,0.36767578125,3.26171875,0.3638574218750001,3.2459179687500006,0.35982421875,3.2297656249999998,0.355576171875,3.21326171875,0.35111328125,3.19640625,0.346435546875,3.17919921875,0.3416406250000001,3.162187500000001,0.336826171875,3.14591796875,0.33199218750000004,3.1303906249999995,0.327138671875,3.11560546875,0.322265625,3.1015625,0.31578871783088236,3.083208869485294,0.3093118106617647,3.0648552389705883,0.3028349034926471,3.0465016084558822,0.29635799632352944,3.0281479779411766,0.28988108915441174,3.0097943474264706,0.2834041819852941,2.9914407169117645,0.27692727481617646,2.973087086397059,0.2704503676470588,2.954733455882353,0.2639734604779412,2.936379825367647,0.25749655330882354,2.918026194852941,0.2510196461397059,2.8996725643382355,0.24454273897058823,2.8813189338235294,0.2380658318014706,2.8629653033088234,0.23158892463235295,2.8446116727941178,0.22511201746323528,2.8262580422794117,0.21863511029411764,2.807904411764706,0.212158203125,2.78955078125,0.20568129595588236,2.771197150735294,0.19920438878676472,2.7528435202205883,0.19272748161764705,2.7344898897058822,0.1862505744485294,2.7161362591911766,0.17977366727941177,2.6977826286764706,0.1732967601102941,2.6794289981617645,0.16681985294117646,2.661075367647059,0.16034294577205882,2.642721737132353,0.15386603860294118,2.624368106617647,0.14738913143382354,2.606014476102941,0.1409122242647059,2.5876608455882355,0.13443531709558823,2.5693072150735294,0.1279584099264706,2.5509535845588234,0.12148150275735295,2.5325999540441178,0.11500459558823531,2.5142463235294117,0.10852768841911764,2.4958926930147056,0.10205078125,2.4775390625]],"5":[[0.27490234375,2.873046875,0.29279327392578125,2.8722763061523438,0.310089111328125,2.869964599609375,0.32678985595703125,2.8661117553710938,0.3428955078125,2.8607177734375,0.35840606689453125,2.8537826538085938,0.373321533203125,2.845306396484375,0.38764190673828125,2.8352890014648438,0.4013671875,2.82373046875,0.41300455729166663,2.8122227044753085,0.4240451388888889,2.7996178144290123,0.43448893229166674,2.7859157986111116,0.4443359375,2.771116657021605,0.45358615451388884,2.7552203896604937,0.46223958333333337,2.7382269965277777,0.47029622395833337,2.720136477623457,0.4777560763888889,2.700948832947531,0.484619140625,2.6806640625,0.48933749728732634,2.6647610134548607,0.49364556206597227,2.6483018663194446,0.4975433349609375,2.63128662109375,0.5010308159722223,2.6137152777777786,0.5041080050998263,2.5955878363715277,0.50677490234375,2.576904296875,0.5090315077039931,2.557664659288194,0.5108778211805556,2.537868923611111,0.5123138427734375,2.51751708984375,0.513339572482639,2.496609157986111,0.5139550103081597,2.475145128038194,0.51416015625,2.453125,0.5139723557692308,2.430828749075444,0.5134089543269231,2.4090121116863905,0.5124699519230769,2.3876750878328403,0.5111553485576923,2.366817677514793,0.5094651442307693,2.346439880732249,0.5073993389423077,2.326541697485207,0.5049579326923077,2.307123127773669,0.5021409254807692,2.288184171597633,0.4989483173076923,2.2697248289571004,0.49538010817307687,2.251745099852071,0.491436298076923,2.2342449842825443,0.4871168870192308,2.217224482248521,0.482421875,2.20068359375,0.47579589843750003,2.1799804687499997,0.4686132812500001,2.1602539062500004,0.4608740234375,2.1415039062499996,0.45257812500000005,2.12373046875,0.4437255859375,2.10693359375,0.43431640625,2.0911132812499997,0.42435058593749997,2.07626953125,0.413828125,2.06240234375,0.4027490234375,2.04951171875,0.39111328125,2.03759765625,0.3775860821759259,2.025607638888889,0.36348017939814814,2.0150282118055554,0.3487955729166667,2.0058593750000004,0.33353226273148145,1.9981011284722223,0.31769024884259256,1.9917534722222223,0.30126953125,1.98681640625,0.2842701099537037,1.9832899305555554,0.2666919849537037,1.9811740451388888,0.24853515625,1.98046875,0.23019748263888892,1.9810112847222225,0.21229383680555558,1.9826388888888893,0.19482421875,1.9853515625,0.17778862847222224,1.9891493055555556,0.1611870659722222,1.9940321180555556,0.14501953125,2,0.12948947482638892,2.0070529513888893,0.11480034722222224,2.0151909722222223,0.1009521484375,2.0244140625,0.08794487847222222,2.0347222222222223,0.0757785373263889,2.0461154513888893,0.064453125,2.05859375,0.064453125,2.077880859375,0.064453125,2.09716796875,0.064453125,2.116455078125,0.064453125,2.1357421875,0.064453125,2.155029296875,0.064453125,2.17431640625,0.064453125,2.193603515625,0.064453125,2.212890625,0.0767550998263889,2.1990559895833335,0.08989800347222224,2.186197916666667,0.1038818359375,2.17431640625,0.11870659722222221,2.1634114583333335,0.1343722873263889,2.153483072916667,0.15087890625,2.14453125,0.16772460937500003,2.1367730034722228,0.18440755208333334,2.1304253472222223,0.200927734375,2.12548828125,0.21728515625,2.1219618055555554,0.23347981770833334,2.119845920138889,0.24951171875,2.119140625,0.2672051323784722,2.1201036241319446,0.2841525607638889,2.122992621527778,0.30035400390625,2.1278076171875,0.3158094618055556,2.134548611111111,0.3305189344618056,2.143215603298611,0.344482421875,2.15380859375,0.355762715242347,2.1644511320153064,0.36630560427295916,2.176568478954082,0.3761110889668367,2.1901606345663263,0.38517916932397966,2.2052275988520407,0.3935098453443877,2.2217693718112246,0.40110311702806123,2.2397859534438775,0.407958984375,2.25927734375,0.41227294921875,2.2738037109375004,0.4161328125000001,2.2890625000000004,0.41953857421874996,2.3050537109374996,0.42249023437499994,2.32177734375,0.42498779296875,2.3392333984375,0.42703125,2.357421875,0.42862060546875,2.3763427734375,0.42975585937500005,2.39599609375,0.43043701171875004,2.4163818359375,0.4306640625,2.4375,0.43036058213975686,2.4608120388454857,0.42945014105902785,2.483222113715278,0.4279327392578125,2.504730224609375,0.4258083767361111,2.5253363715277777,0.4230770534939235,2.545040554470486,0.41973876953125,2.5638427734375,0.4157935248480903,2.5817430284288196,0.4112413194444444,2.5987413194444446,0.4060821533203125,2.614837646484375,0.4003160264756944,2.630032009548611,0.3939429389105903,2.644324408637153,0.386962890625,2.65771484375,0.3777172851562501,2.6724658203125005,0.36746093750000003,2.685664062500001,0.35619384765624995,2.6973095703125,0.343916015625,2.70740234375,0.33062744140625,2.7159423828125,0.316328125,2.7229296875,0.30101806640625,2.7283642578125,0.28469726562500003,2.73224609375,0.26736572265625,2.7345751953125,0.2490234375,2.7353515625,0.2335205078125,2.7347412109375,0.21728515625,2.73291015625,0.2003173828125,2.7298583984375,0.1826171875,2.7255859375,0.16534423828125,2.7205810546875,0.149658203125,2.71533203125,0.13555908203125,2.7098388671875,0.123046875,2.7041015625,0.11279296875,2.717529296875,0.1025390625,2.73095703125,0.09228515625,2.744384765625,0.08203125,2.7578125,0.08283547794117647,2.7775160845588234,0.08363970588235294,2.797219669117647,0.08444393382352941,2.8169232536764706,0.08524816176470588,2.836626838235294,0.08605238970588236,2.8563304227941178,0.08685661764705882,2.876034007352941,0.0876608455882353,2.8957375919117645,0.08846507352941177,2.9154411764705883,0.08926930147058823,2.9351447610294117,0.0900735294117647,2.9548483455882355,0.09087775735294118,2.974551930147059,0.09168198529411764,2.9942555147058822,0.09248621323529412,3.0139590992647056,0.09329044117647059,3.0336626838235294,0.09409466911764705,3.053366268382353,0.09489889705882353,3.0730698529411766,0.095703125,3.0927734375,0.09650735294117647,3.1124770220588234,0.09731158088235295,3.132180606617647,0.09811580882352941,3.1518841911764706,0.09892003676470588,3.1715877757352944,0.09972426470588236,3.1912913602941178,0.10052849264705882,3.210994944852941,0.1013327205882353,3.2306985294117645,0.10213694852941177,3.2504021139705883,0.10294117647058823,3.2701056985294117,0.1037454044117647,3.2898092830882355,0.10454963235294118,3.309512867647059,0.10535386029411764,3.3292164522058822,0.10615808823529412,3.3489200367647056,0.10696231617647059,3.3686236213235294,0.10776654411764705,3.388327205882353,0.10857077205882353,3.4080307904411766,0.109375,3.427734375,0.1290961371527778,3.427734375,0.14881727430555555,3.427734375,0.16853841145833331,3.427734375,0.1882595486111111,3.427734375,0.2079806857638889,3.427734375,0.22770182291666666,3.427734375,0.24742296006944445,3.427734375,0.2671440972222222,3.427734375,0.286865234375,3.427734375,0.3065863715277778,3.427734375,0.3263075086805556,3.427734375,0.3460286458333333,3.427734375,0.3657497829861111,3.427734375,0.3854709201388889,3.427734375,0.4051920572916667,3.427734375,0.4249131944444444,3.427734375,0.4446343315972222,3.427734375,0.46435546875,3.427734375,0.46435546875,3.4091796875,0.46435546875,3.390625,0.46435546875,3.3720703125,0.46435546875,3.353515625,0.46435546875,3.3349609375,0.46435546875,3.31640625,0.46435546875,3.2978515625,0.46435546875,3.279296875,0.4453125,3.279296875,0.42626953125,3.279296875,0.4072265625,3.279296875,0.38818359375,3.279296875,0.369140625,3.279296875,0.35009765625,3.279296875,0.3310546875,3.279296875,0.31201171875,3.279296875,0.29296875,3.279296875,0.27392578125,3.279296875,0.2548828125,3.279296875,0.23583984375,3.279296875,0.216796875,3.279296875,0.19775390625,3.279296875,0.1787109375,3.279296875,0.1778897372159091,3.2597212357954546,0.17706853693181818,3.240145596590909,0.17624733664772727,3.2205699573863638,0.17542613636363635,3.2009943181818183,0.17460493607954544,3.181418678977273,0.17378373579545456,3.161843039772727,0.17296253551136365,3.1422674005681817,0.17214133522727273,3.1226917613636362,0.17132013494318182,3.103116122159091,0.1704989346590909,3.0835404829545454,0.169677734375,3.06396484375,0.1688565340909091,3.0443892045454546,0.16803533380681818,3.024813565340909,0.16721413352272727,3.0052379261363638,0.16639293323863635,2.9856622869318183,0.16557173295454544,2.9660866477272725,0.16475053267045456,2.9465110085227275,0.16392933238636365,2.9269353693181817,0.16310813210227273,2.9073597301136362,0.16228693181818182,2.887784090909091,0.1614657315340909,2.8682084517045454,0.16064453125,2.8486328125,0.17059326171875,2.852569580078125,0.181884765625,2.8565673828125,0.19451904296875,2.860626220703125,0.20849609375,2.86474609375,0.2236328125,2.868377685546875,0.23974609375,2.8709716796875,0.2568359375,2.872528076171875,0.27490234375,2.873046875]],"6":[[0.056640625,2.6103515625,0.0566940759060651,2.630148622411243,0.05685442862426035,2.649876340606509,0.057121683154585795,2.6695347170857984,0.05749583949704142,2.6891237518491122,0.057976897651627224,2.70864344489645,0.058564857618343194,2.7280937962278107,0.05925971939718935,2.7474748058431953,0.060061482988165674,2.7667864737426036,0.06097014839127219,2.7860287999260356,0.061985715606508875,2.805201784393491,0.06310818463387574,2.8243054271449703,0.06433755547337278,2.8433397281804735,0.065673828125,2.8623046875,0.06726582845052081,2.8826938205295134,0.06903076171875,2.9028455946180562,0.0709686279296875,2.922760009765625,0.07307942708333334,2.9424370659722228,0.07536315917968749,2.961876763237847,0.07781982421875,2.9810791015625,0.08044942220052084,3.000044080946181,0.083251953125,3.0187717013888893,0.0862274169921875,3.037261962890625,0.08937581380208334,3.055514865451389,0.0926971435546875,3.0735304090711804,0.09619140625,3.09130859375,0.10064453125,3.1122412109375004,0.10541015625000003,3.1326562500000006,0.11048828125,3.1525537109374997,0.11587890625,3.17193359375,0.12158203125,3.1907958984375,0.12759765625,3.209140625,0.13392578125,3.2269677734375,0.14056640625000003,3.2442773437500003,0.14751953125,3.2610693359375,0.15478515625,3.27734375,0.16434860229492188,3.2968215942382812,0.1744842529296875,3.315216064453125,0.18519210815429688,3.3325271606445312,0.19647216796875,3.3487548828125,0.20832443237304688,3.3638992309570312,0.2207489013671875,3.377960205078125,0.23374557495117188,3.3909378051757812,0.247314453125,3.40283203125,0.2615165710449219,3.4134750366210938,0.2764129638671875,3.422698974609375,0.2920036315917969,3.4305038452148438,0.30828857421875,3.4368896484375,0.3252677917480469,3.4418563842773438,0.3429412841796875,3.445404052734375,0.3613090515136719,3.4475326538085938,0.38037109375,3.4482421875,0.391571044921875,3.447967529296875,0.4031982421875,3.4471435546875,0.415252685546875,3.445770263671875,0.427734375,3.44384765625,0.43988037109375,3.441314697265625,0.450927734375,3.4381103515625,0.46087646484375,3.434234619140625,0.4697265625,3.4296875,0.4697265625,3.4097377232142856,0.4697265625,3.3897879464285716,0.4697265625,3.369838169642857,0.4697265625,3.349888392857143,0.4697265625,3.3299386160714284,0.4697265625,3.3099888392857144,0.4697265625,3.2900390625,0.4603729248046875,3.29595947265625,0.45037841796875,3.301025390625,0.4397430419921875,3.30523681640625,0.428466796875,3.30859375,0.4168548583984375,3.3111572265625,0.40521240234375,3.31298828125,0.3935394287109375,3.3140869140625,0.3818359375,3.314453125,0.36406005859375,3.3136621093750005,0.34706054687500004,3.3112890625000007,0.33083740234375,3.307333984375,0.315390625,3.301796875,0.30072021484375,3.294677734375,0.286826171875,3.2859765625,0.27370849609375003,3.2756933593750004,0.2613671875,3.2638281250000007,0.24980224609374999,3.250380859375,0.239013671875,3.2353515625,0.23054843478732637,3.2217780219184022,0.22248670789930558,3.2074245876736116,0.2148284912109375,3.192291259765625,0.20757378472222224,3.176378038194445,0.2007225884331597,3.1596849229600688,0.19427490234375,3.1422119140625,0.18823072645399305,3.123959011501736,0.1825900607638889,3.1049262152777777,0.1773529052734375,3.085113525390625,0.1725192599826389,3.0645209418402777,0.16808912489149305,3.043148464626736,0.1640625,3.02099609375,0.1612701416015625,3.003957748413086,0.15863037109375,2.9866561889648438,0.1561431884765625,2.9690914154052734,0.15380859375,2.951263427734375,0.1516265869140625,2.9331722259521484,0.14959716796875,2.9148178100585938,0.1477203369140625,2.896200180053711,0.14599609375,2.8773193359375,0.1444244384765625,2.858175277709961,0.14300537109375,2.8387680053710938,0.1417388916015625,2.8190975189208984,0.140625,2.799163818359375,0.1396636962890625,2.7789669036865234,0.13885498046875,2.7585067749023438,0.1381988525390625,2.737783432006836,0.1376953125,2.716796875,0.14306640625,2.716796875,0.14945312500000002,2.7359570312500003,0.15650390625,2.754375,0.16421874999999997,2.77205078125,0.17259765625,2.788984375,0.181640625,2.80517578125,0.19141601562500005,2.8203515625000004,0.2019921875,2.83423828125,0.213369140625,2.8468359375,0.225546875,2.85814453125,0.238525390625,2.8681640625,0.25234375000000003,2.8766015625000008,0.26704101562500004,2.8831640625,0.28261718750000003,2.8878515625,0.299072265625,2.8906640625,0.31640625,2.8916015625,0.3339395328443878,2.8905652104591844,0.3508250956632653,2.887456154336735,0.36706293845663257,2.8822743941326525,0.3826530612244898,2.875019929846939,0.39759546396683676,2.865692761479592,0.41189014668367346,2.8542928890306123,0.425537109375,2.8408203125,0.4356403115354938,2.828968942901234,0.44522509162808643,2.815984278549383,0.4542914496527779,2.8018663194444446,0.4628393856095679,2.78661506558642,0.4708688994984568,2.7702305169753085,0.4783799913194445,2.7527126736111107,0.4853726610725309,2.734061535493827,0.4918469087577161,2.714277102623457,0.497802734375,2.693359375,0.5018971761067708,2.676961263020833,0.5056355794270834,2.6599934895833335,0.5090179443359375,2.6424560546875,0.5120442708333334,2.6243489583333335,0.5147145589192708,2.605672200520833,0.51702880859375,2.58642578125,0.5189870198567708,2.566609700520833,0.5205891927083334,2.5462239583333335,0.5218353271484375,2.5252685546875,0.5227254231770834,2.5037434895833335,0.5232594807942709,2.4816487630208335,0.5234375,2.458984375,0.5232757026627219,2.4365349944526633,0.5227903106508875,2.414559448964497,0.5219813239644969,2.3930577385355023,0.5208487426035503,2.3720298631656807,0.5193925665680473,2.3514758228550297,0.5176127958579883,2.33139561760355,0.5155094304733728,2.3117892474112427,0.5130824704142012,2.2926567122781067,0.5103319156804733,2.273998012204142,0.5072577662721893,2.2558131471893494,0.5038600221893491,2.2381021172337277,0.5001386834319527,2.2208649223372783,0.49609375,2.2041015625,0.49039306640625,2.1831103515625,0.4842285156250001,2.1631054687500004,0.47760009765625,2.1440869140625,0.47050781249999996,2.1260546875,0.46295166015625,2.1090087890625,0.454931640625,2.0929492187500003,0.44644775390625,2.0778759765625,0.4375,2.0637890625,0.42808837890625,2.0506884765624998,0.418212890625,2.03857421875,0.4052772521972656,2.0249557495117188,0.3917388916015625,2.013153076171875,0.3775978088378906,2.0031661987304688,0.36285400390625,1.9949951171875,0.3475074768066406,1.9886398315429688,0.3315582275390625,1.984100341796875,0.3150062561035156,1.9813766479492188,0.2978515625,1.98046875,0.2806667751736111,1.9815809461805558,0.26394314236111116,1.9849175347222225,0.2476806640625,1.990478515625,0.2318793402777778,1.9982638888888888,0.21653917100694445,2.008273654513889,0.20166015625,2.0205078125,0.19085311889648438,2.0311279296875,0.1804046630859375,2.04296875,0.17031478881835938,2.0560302734375,0.16058349609375,2.0703125,0.15121078491210938,2.0858154296875,0.1421966552734375,2.1025390625,0.13354110717773438,2.1204833984375,0.125244140625,2.1396484375,0.11945942019628097,2.1543493349690084,0.11391278731921485,2.1696878228305785,0.10860424199380164,2.1856639010847108,0.10353378422004131,2.202277569731405,0.09870141399793388,2.2195288287706605,0.09410713132747933,2.2374176782024797,0.08975093620867769,2.25594411802686,0.08563282864152892,2.2751081482438016,0.08175280862603307,2.294909768853306,0.07811087616219008,2.3153489798553717,0.07470703125,2.33642578125,0.0722182916135204,2.3534334143813775,0.0699139030612245,2.370834661989796,0.06779386559311225,2.3886295240752546,0.06585817920918367,2.406818000637755,0.06410684390943876,2.4254000916772953,0.06253985969387754,2.444375797193877,0.0611572265625,2.4637451171875,0.059958944515306124,2.483508051658163,0.05894501355229592,2.5036646006058674,0.058115433673469385,2.5242147640306123,0.057470204878826536,2.545158541932398,0.057009327168367346,2.5664959343112246,0.05673280054209183,2.5882269411670915,0.056640625,2.6103515625],[0.296875,2.1171875,0.3113064236111111,2.118236400462963,0.3250868055555555,2.121383101851852,0.33821614583333337,2.126627604166667,0.3506944444444444,2.1339699074074074,0.3625217013888889,2.1434100115740744,0.3736979166666667,2.154947916666667,0.3842230902777778,2.168583622685185,0.3940972222222222,2.1843171296296298,0.4033203125,2.2021484375,0.40883589764030615,2.214729153380102,0.41394292091836743,2.22819674744898,0.41864138233418363,2.2425512197066326,0.4229312818877551,2.2577925701530615,0.42681261957908156,2.273920798788265,0.43028539540816324,2.2909359056122445,0.433349609375,2.308837890625,0.43600526147959184,2.3276267538265305,0.43825235172193877,2.347302495216837,0.4400908801020408,2.3678651147959187,0.441520846619898,2.3893146125637754,0.4425422512755102,2.411650988520408,0.44315509406887754,2.434874242665816,0.443359375,2.458984375,0.44311184353298605,2.482014973958333,0.4423692491319445,2.5042317708333335,0.441131591796875,2.525634765625,0.4393988715277779,2.546223958333334,0.43717108832465273,2.565999348958333,0.4344482421875,2.5849609375,0.4312303331163194,2.6031087239583335,0.42751736111111116,2.620442708333333,0.423309326171875,2.636962890625,0.41860622829861116,2.6526692708333335,0.4134080674913194,2.667561848958333,0.40771484375,2.681640625,0.39934775270061723,2.698857060185185,0.39008849344135804,2.714048032407407,0.37993706597222227,2.727213541666667,0.3688934702932099,2.738353587962963,0.356957706404321,2.7474681712962963,0.3441297743055556,2.7545572916666665,0.33040967399691357,2.759620949074074,0.3157974054783951,2.762659143518519,0.30029296875,2.763671875,0.2843695746527778,2.7625461154513893,0.2691514756944445,2.759168836805556,0.254638671875,2.7535400390625,0.24083116319444448,2.7456597222222223,0.2277289496527778,2.7355278862847223,0.21533203125,2.72314453125,0.20373535156250003,2.709052191840278,0.19303385416666669,2.693793402777778,0.1832275390625,2.6773681640625,0.17431640625,2.6597764756944446,0.16630045572916669,2.641018337673611,0.1591796875,2.62109375,0.15386838329081634,2.603475765306123,0.14937420280612246,2.58577806122449,0.14569714604591835,2.568000637755102,0.14283721301020408,2.5501434948979593,0.14079440369897958,2.532206632653061,0.1395687181122449,2.5141900510204085,0.13916015625,2.49609375,0.1393544722576531,2.4776387117346945,0.13993742028061223,2.4591039540816326,0.14090900031887754,2.440489477040816,0.142269212372449,2.421795280612245,0.14401805644132654,2.4030213647959187,0.1461555325255102,2.384167729591837,0.148681640625,2.365234375,0.15160634566326534,2.3464604591836737,0.1549396125637755,2.328085140306123,0.1586814413265306,2.3101084183673466,0.16283183195153061,2.292530293367347,0.16739078443877553,2.275350765306123,0.1723582987882653,2.2585698341836737,0.177734375,2.2421875,0.18452962239583334,2.2239312065972223,0.19189453125000003,2.206922743055556,0.1998291015625,2.191162109375,0.20833333333333334,2.1766493055555554,0.2174072265625,2.1633843315972223,0.22705078125,2.1513671875,0.23937500000000003,2.1390625,0.25251953125,2.1294921875,0.266484375,2.12265625,0.28126953125,2.1185546875,0.296875,2.1171875]],"7":[[0.23046875,2,0.2126953125,2,0.194921875,2,0.1771484375,2,0.159375,2,0.1416015625,2,0.14604048295454544,2.0193832859848486,0.1504794034090909,2.038766571969697,0.15491832386363635,2.0581498579545454,0.15935724431818182,2.077533143939394,0.16379616477272727,2.096916429924242,0.16823508522727273,2.116299715909091,0.17267400568181818,2.1356830018939394,0.17711292613636365,2.155066287878788,0.1815518465909091,2.1744495738636362,0.18599076704545453,2.193832859848485,0.1904296875,2.2132161458333335,0.19486860795454547,2.2325994318181817,0.1993075284090909,2.2519827178030303,0.20374644886363635,2.271366003787879,0.20818536931818182,2.290749289772727,0.2126242897727273,2.3101325757575757,0.2170632102272727,2.3295158617424243,0.22150213068181818,2.3488991477272725,0.22594105113636365,2.368282433712121,0.2303799715909091,2.3876657196969697,0.23481889204545453,2.4070490056818183,0.2392578125,2.4264322916666665,0.24369673295454547,2.445815577651515,0.2481356534090909,2.4651988636363638,0.25257457386363635,2.484582149621212,0.2570134943181818,2.5039654356060606,0.2614524147727273,2.523348721590909,0.2658913352272727,2.542732007575758,0.2703302556818182,2.562115293560606,0.27476917613636365,2.5814985795454546,0.27920809659090906,2.600881865530303,0.2836470170454546,2.6202651515151514,0.2880859375,2.6396484375,0.2925248579545454,2.6590317234848486,0.29696377840909094,2.678415009469697,0.30140269886363635,2.6977982954545454,0.30584161931818177,2.717181581439394,0.3102805397727273,2.736564867424242,0.3147194602272727,2.755948153409091,0.3191583806818182,2.7753314393939394,0.32359730113636365,2.794714725378788,0.32803622159090906,2.8140980113636362,0.33247514204545453,2.833481297348485,0.3369140625,2.852864583333333,0.3413529829545454,2.8722478693181817,0.34579190340909094,2.8916311553030303,0.35023082386363635,2.911014441287879,0.3546697443181818,2.9303977272727275,0.3591086647727273,2.9497810132575757,0.3635475852272727,2.9691642992424243,0.3679865056818182,2.9885475852272725,0.37242542613636365,3.007930871212121,0.37686434659090906,3.0273141571969697,0.3813032670454546,3.0466974431818183,0.3857421875,3.066080729166667,0.39018110795454547,3.085464015151515,0.39462002840909094,3.1048473011363638,0.39905894886363635,3.124230587121212,0.4034978693181818,3.1436138731060606,0.40793678977272724,3.162997159090909,0.4123757102272727,3.182380445075758,0.4168146306818182,3.2017637310606064,0.42125355113636365,3.2211470170454546,0.4256924715909091,3.2405303030303028,0.43013139204545453,3.2599135890151514,0.4345703125,3.279296875,0.4151123046875,3.279296875,0.395654296875,3.279296875,0.3761962890625,3.279296875,0.35673828125,3.279296875,0.3372802734375,3.279296875,0.317822265625,3.279296875,0.2983642578125,3.279296875,0.27890625,3.279296875,0.25944824218749996,3.279296875,0.239990234375,3.279296875,0.2205322265625,3.279296875,0.20107421875,3.279296875,0.18161621093749997,3.279296875,0.162158203125,3.279296875,0.1427001953125,3.279296875,0.12324218749999999,3.279296875,0.10378417968750003,3.279296875,0.08432617187499997,3.279296875,0.06486816406250001,3.279296875,0.04541015625,3.279296875,0.04541015625,3.2978515625,0.04541015625,3.31640625,0.04541015625,3.3349609375,0.04541015625,3.353515625,0.04541015625,3.3720703125,0.04541015625,3.390625,0.04541015625,3.4091796875,0.04541015625,3.427734375,0.06524658203125,3.427734375,0.0850830078125,3.427734375,0.10491943359375,3.427734375,0.124755859375,3.427734375,0.14459228515625,3.427734375,0.1644287109375,3.427734375,0.18426513671875,3.427734375,0.2041015625,3.427734375,0.22393798828125,3.427734375,0.2437744140625,3.427734375,0.26361083984375,3.427734375,0.283447265625,3.427734375,0.30328369140625,3.427734375,0.3231201171875,3.427734375,0.34295654296875,3.427734375,0.36279296875,3.427734375,0.38262939453125,3.427734375,0.4024658203125,3.427734375,0.42230224609375,3.427734375,0.442138671875,3.427734375,0.46197509765625,3.427734375,0.4818115234375,3.427734375,0.50164794921875,3.427734375,0.521484375,3.427734375,0.521484375,3.409877232142857,0.521484375,3.3920200892857144,0.521484375,3.3741629464285716,0.521484375,3.3563058035714284,0.521484375,3.3384486607142856,0.521484375,3.320591517857143,0.521484375,3.302734375,0.5171408582089553,3.283290578358209,0.5127973414179104,3.263846781716418,0.5084538246268657,3.2444029850746268,0.5041103078358209,3.2249591884328357,0.4997667910447761,3.2055153917910446,0.49542327425373134,3.1860715951492535,0.49107975746268656,3.166627798507463,0.4867362406716418,3.147184001865672,0.482392723880597,3.1277402052238807,0.47804920708955223,3.1082964085820897,0.47370569029850745,3.0888526119402986,0.4693621735074627,3.0694088152985075,0.4650186567164179,3.0499650186567164,0.4606751399253731,3.0305212220149254,0.45633162313432835,3.0110774253731343,0.45198810634328357,2.991633628731343,0.4476445895522388,2.972189832089552,0.443301072761194,2.952746035447761,0.43895755597014924,2.93330223880597,0.43461403917910446,2.9138584421641793,0.4302705223880597,2.894414645522388,0.4259270055970149,2.874970848880597,0.4215834888059702,2.855527052238806,0.41723997201492535,2.836083255597015,0.4128964552238806,2.816639458955224,0.4085529384328358,2.797195662313433,0.4042094216417911,2.777751865671642,0.39986590485074625,2.7583080690298507,0.3955223880597015,2.7388642723880596,0.3911788712686567,2.7194204757462686,0.38683535447761197,2.6999766791044775,0.38249183768656714,2.6805328824626864,0.3781483208955224,2.6610890858208958,0.3738048041044776,2.6416452891791042,0.36946128731343286,2.6222014925373136,0.36511777052238803,2.6027576958955225,0.3607742537313433,2.5833138992537314,0.3564307369402985,2.5638701026119404,0.35208722014925375,2.5444263059701493,0.347743703358209,2.524982509328358,0.3434001865671642,2.505538712686567,0.33905666977611937,2.486094916044776,0.33471315298507465,2.466651119402985,0.3303696361940298,2.447207322761194,0.3260261194029851,2.427763526119403,0.3216826026119403,2.408319729477612,0.31733908582089554,2.3888759328358207,0.31299556902985076,2.36943213619403,0.308652052238806,2.349988339552239,0.30430853544776115,2.330544542910448,0.29996501865671643,2.311100746268657,0.2956215018656716,2.2916569496268657,0.2912779850746269,2.2722131529850746,0.2869344682835821,2.2527693563432836,0.2825909514925373,2.2333255597014925,0.27824743470149255,2.2138817630597014,0.27390391791044777,2.1944379664179103,0.269560401119403,2.1749941697761193,0.2652168843283582,2.1555503731343286,0.26087336753731344,2.136106576492537,0.25652985074626866,2.1166627798507465,0.2521863339552239,2.097218983208955,0.2478428171641791,2.0777751865671643,0.24349930037313433,2.0583313899253732,0.23915578358208955,2.038887593283582,0.23481226679104478,2.019443796641791,0.23046875,2]],"8":[[0.28515625,3.4482421875,0.3025849011479592,3.447444993622449,0.31943558673469385,3.4450534119897958,0.335708306760204,3.4410674426020402,0.3514030612244898,3.435487085459184,0.36651985012755106,3.4283123405612246,0.38105867346938777,3.4195432079081636,0.39501953125,3.4091796875,0.4066581726074219,3.3988418579101562,0.4176483154296875,3.387359619140625,0.4279899597167969,3.3747329711914062,0.43768310546875,3.3609619140625,0.4467277526855469,3.3460464477539062,0.4551239013671875,3.329986572265625,0.4628715515136719,3.3127822875976562,0.469970703125,3.29443359375,0.47511962890625,3.2789501953125004,0.4797265625000001,3.262773437500001,0.48379150390625,3.2459033203125,0.487314453125,3.22833984375,0.49029541015625,3.2100830078125,0.49273437499999995,3.1911328125000002,0.49463134765625,3.1714892578125,0.495986328125,3.15115234375,0.49679931640624997,3.1301220703125,0.4970703125,3.1083984375,0.4967765808105469,3.0870132446289062,0.4958953857421875,3.066314697265625,0.4944267272949219,3.0463027954101562,0.49237060546875,3.0269775390625,0.4897270202636719,3.0083389282226562,0.4864959716796875,2.990386962890625,0.4826774597167969,2.9731216430664062,0.478271484375,2.95654296875,0.47263632015306134,2.9383270886479593,0.46647301498724486,2.920788823341837,0.45978156887755095,2.903928172831632,0.4525619818239796,2.887745137117347,0.4448142538265306,2.8722397161989797,0.43653838488520413,2.8574119100765305,0.427734375,2.84326171875,0.41696506076388895,2.8274332682291665,0.40581597222222227,2.8121744791666665,0.394287109375,2.7974853515625,0.3823784722222222,2.7833658854166665,0.3700900607638889,2.769816080729167,0.357421875,2.7568359375,0.3703513632015307,2.744618941326531,0.3829121492346939,2.731963488520408,0.3951042330994897,2.718869579081632,0.4069276147959183,2.7053372130102042,0.4183822943239796,2.6913663903061225,0.42946827168367346,2.676957110969388,0.440185546875,2.662109375,0.4504045758928572,2.6466637436224496,0.45999581473214285,2.6304607780612246,0.4689592633928571,2.6135004783163263,0.477294921875,2.595782844387755,0.4850027901785714,2.57730787627551,0.49208286830357145,2.558075573979592,0.49853515625,2.5380859375,0.5030442226080246,2.5219184027777777,0.5070228105709876,2.505099826388889,0.510470920138889,2.487630208333334,0.5133885513117284,2.469509548611111,0.5157757040895061,2.4507378472222223,0.5176323784722222,2.431315104166667,0.5189585744598766,2.4112413194444446,0.5197542920524691,2.390516493055556,0.52001953125,2.369140625,0.5197794260072314,2.3475715069731407,0.5190591102789256,2.326543130165289,0.5178585840650827,2.306055494576446,0.5161778473657024,2.2861086002066116,0.514016900180785,2.266702447055785,0.5113757425103305,2.247837035123967,0.5082543743543388,2.229512364411157,0.50465279571281,2.2117284349173554,0.5005710065857438,2.194485246642562,0.49600900697314054,2.177782799586777,0.490966796875,2.16162109375,0.48422429591049376,2.142698688271605,0.4769091194058642,2.1247769579475313,0.46902126736111116,2.107855902777778,0.4605607397762346,2.0919355227623457,0.4515275366512346,2.0770158179012346,0.44192165798611116,2.0630967881944446,0.4317431037808642,2.0501784336419755,0.4209918740354938,2.0382607542438276,0.40966796875,2.02734375,0.39632415771484375,2.016357421875,0.382415771484375,2.0068359375,0.36794281005859375,1.998779296875,0.3529052734375,1.9921875,0.33730316162109375,1.987060546875,0.321136474609375,1.9833984375,0.30440521240234375,1.981201171875,0.287109375,1.98046875,0.2685432434082031,1.9811782836914062,0.2506866455078125,1.983306884765625,0.23353958129882812,1.9868545532226562,0.21710205078125,1.9918212890625,0.20137405395507812,1.9982070922851562,0.1863555908203125,2.006011962890625,0.17204666137695312,2.0152359008789062,0.158447265625,2.02587890625,0.145599365234375,2.0378570556640625,0.133544921875,2.05108642578125,0.122283935546875,2.0655670166015625,0.11181640625,2.081298828125,0.102142333984375,2.0982818603515625,0.09326171875,2.11651611328125,0.085174560546875,2.1360015869140625,0.077880859375,2.15673828125,0.07309287835743801,2.1725610149793386,0.06876089553202479,2.1889567729855366,0.06488491089876033,2.205925555268595,0.06146492445764463,2.223467361828513,0.058500936208677676,2.2415821926652892,0.055992946151859506,2.2602700477789255,0.05394095428719008,2.2795309271694215,0.05234496061466942,2.299364830836777,0.05120496513429752,2.3197717587809916,0.05052096784607438,2.340751711002066,0.05029296875,2.3623046875,0.05055217978395061,2.3839035976080245,0.05132981288580247,2.404863522376543,0.052625868055555566,2.425184461805556,0.05444034529320988,2.4448664158950617,0.056773244598765434,2.4639093846450617,0.059624565972222224,2.4823133680555554,0.06299430941358025,2.5000783661265435,0.06688247492283951,2.5172043788580245,0.0712890625,2.53369140625,0.07756696428571429,2.5540696747448983,0.08440290178571429,2.573630819515306,0.09179687499999999,2.5923748405612237,0.09974888392857142,2.6103017378826534,0.10825892857142858,2.6274115114795915,0.11732700892857144,2.6437041613520407,0.126953125,2.6591796875,0.13868543836805555,2.676296657986111,0.15077039930555555,2.6924913194444446,0.1632080078125,2.707763671875,0.1759982638888889,2.7221137152777777,0.1891411675347222,2.7355414496527777,0.20263671875,2.748046875,0.1909247504340278,2.761366102430556,0.17955186631944445,2.775282118055556,0.16851806640625,2.789794921875,0.15782335069444445,2.8049045138888893,0.1474677191840278,2.8206108940972223,0.137451171875,2.8369140625,0.12926000478316327,2.851492745535715,0.12156708386479591,2.8667689732142856,0.11437240911989793,2.8827427455357144,0.10767598054846939,2.8994140625,0.1014777981505102,2.9167829241071432,0.09577786192602042,2.9348493303571432,0.090576171875,2.95361328125,0.08651351928710938,2.9706802368164062,0.0829925537109375,2.988433837890625,0.08001327514648438,3.0068740844726562,0.07757568359375,3.0260009765625,0.07567977905273438,3.0458145141601562,0.0743255615234375,3.066314697265625,0.07351303100585938,3.0875015258789062,0.0732421875,3.109375,0.07351806640625001,3.1307275390625,0.07434570312500001,3.151425781250001,0.07572509765625,3.1714697265624996,0.07765625000000001,3.1908593750000005,0.08013916015625,3.2095947265625,0.083173828125,3.22767578125,0.08676025390625,3.2451025390624997,0.09089843750000001,3.2618750000000003,0.09558837890625001,3.2779931640625004,0.100830078125,3.29345703125,0.1080474853515625,3.3118133544921875,0.11590576171875,3.32904052734375,0.1244049072265625,3.3451385498046875,0.133544921875,3.360107421875,0.1433258056640625,3.3739471435546875,0.15374755859375,3.38665771484375,0.1648101806640625,3.3982391357421875,0.176513671875,3.40869140625,0.19050940688775514,3.4191844706632657,0.205013352997449,3.42806321747449,0.2200255102040816,3.4353276466836733,0.23554587850765307,3.440977758290816,0.25157445790816324,3.4450135522959187,0.26811124840561223,3.4474350286989797,0.28515625,3.4482421875],[0.1298828125,2.361328125,0.1302685546875,2.3398388671875003,0.13142578125000004,2.3191210937500006,0.13335449218749998,2.2991748046875,0.1360546875,2.28,0.1395263671875,2.2615966796875,0.14376953125,2.2439648437499997,0.14878417968750002,2.2271044921875003,0.15457031250000003,2.211015625,0.1611279296875,2.1956982421875,0.16845703125,2.18115234375,0.17871856689453125,2.1645584106445312,0.190216064453125,2.150177001953125,0.20294952392578125,2.1380081176757812,0.2169189453125,2.1280517578125,0.23212432861328125,2.1203079223632812,0.248565673828125,2.114776611328125,0.26624298095703125,2.1114578247070312,0.28515625,2.1103515625,0.30318450927734375,2.1114578247070312,0.320159912109375,2.114776611328125,0.33608245849609375,2.1203079223632812,0.3509521484375,2.1280517578125,0.36476898193359375,2.1380081176757812,0.377532958984375,2.150177001953125,0.38924407958984375,2.1645584106445312,0.39990234375,2.18115234375,0.40760253906250005,2.1957568359375,0.4144921875000001,2.2112500000000006,0.4205712890624999,2.2276318359375,0.42583984375,2.2449023437499998,0.4302978515625,2.2630615234375,0.4339453125,2.282109375,0.4367822265625,2.3020458984375,0.43880859375000003,2.32287109375,0.4400244140625,2.3445849609375,0.4404296875,2.3671875,0.44005102040816335,2.3872070312500004,0.4389150191326531,2.406529017857143,0.43702168367346933,2.425153459821428,0.43437101403061223,2.4430803571428568,0.4309630102040817,2.460309709821429,0.42679767219387754,2.476841517857143,0.421875,2.49267578125,0.41525607638888895,2.5104302300347223,0.40776909722222227,2.527560763888889,0.3994140625,2.5440673828125,0.3901909722222222,2.5599500868055554,0.38009982638888895,2.575208875868056,0.369140625,2.58984375,0.3574083116319445,2.6039496527777777,0.3449978298611111,2.6176215277777786,0.3319091796875,2.630859375,0.3181423611111111,2.6436631944444446,0.3036973741319445,2.656032986111111,0.28857421875,2.66796875,0.27978515625,2.67431640625,0.27099609375,2.6806640625,0.2563951280381945,2.6680636935763893,0.24254014756944448,2.6550021701388893,0.22943115234375,2.6414794921875,0.2170681423611111,2.6274956597222223,0.2054511176215278,2.6130506727430554,0.194580078125,2.59814453125,0.18449571397569445,2.582668728298611,0.1752387152777778,2.5665147569444446,0.16680908203125,2.5496826171875,0.1592068142361111,2.5321723090277777,0.1524319118923611,2.5139838324652777,0.146484375,2.4951171875,0.14207987882653064,2.478276466836735,0.1383529974489796,2.460678411989796,0.13530373086734693,2.4423230229591835,0.13293207908163265,2.423210299744898,0.13123804209183673,2.403340242346939,0.1302216198979592,2.3827128507653064,0.1298828125,2.361328125],[0.2841796875,3.3154296875,0.26814612563775514,3.3143036511479598,0.25296954719387754,3.310925542091837,0.23864995216836732,3.305295360331632,0.2251873405612245,3.297413105867347,0.212581712372449,3.2872787786989797,0.20083306760204084,3.2748923788265305,0.18994140625,3.26025390625,0.1822554976851852,3.2473958333333326,0.1754738136574074,3.2333441840277777,0.16959635416666669,3.2180989583333335,0.16462311921296297,3.20166015625,0.16055410879629628,3.1840277777777777,0.15738932291666669,3.165201822916667,0.15512876157407407,3.145182291666667,0.15377242476851852,3.1239691840277777,0.1533203125,3.1015625,0.15368403220663268,3.0808254942602047,0.15477519132653061,3.0610251913265305,0.15659378985969385,3.042161591198979,0.15913982780612246,3.024234693877551,0.1624133051658163,3.007244499362245,0.16641422193877553,2.991191007653061,0.171142578125,2.97607421875,0.17744276258680558,2.959391276041667,0.18446180555555558,2.943522135416667,0.19219970703125,2.928466796875,0.2006564670138889,2.914225260416667,0.20983208550347224,2.900797526041667,0.2197265625,2.88818359375,0.23232421875000003,2.8737500000000007,0.2455078125,2.85974609375,0.25927734375,2.846171875,0.27363281250000004,2.8330273437500004,0.28857421875,2.8203125,0.3030859375,2.833066406250001,0.31693359374999996,2.846328125,0.33011718749999996,2.86009765625,0.34263671875,2.874375,0.3544921875,2.88916015625,0.3637559678819445,2.9020860460069446,0.37234157986111116,2.915798611111111,0.3802490234375,2.9302978515625,0.38747829861111116,2.9455837673611107,0.3940294053819445,2.9616563585069446,0.39990234375,2.978515625,0.4043068399234695,2.993722098214286,0.4080337213010204,3.009765625,0.411082987882653,3.0266462053571423,0.4134546396683674,3.0443638392857144,0.41514867665816324,3.0629185267857144,0.41616509885204084,3.0823102678571432,0.41650390625,3.1025390625,0.41605480806327155,3.1249397183641974,0.41470751350308643,3.1461347415123457,0.41246202256944453,3.1661241319444446,0.40931833526234573,3.1849078896604937,0.40527645158179015,3.2024860146604937,0.4003363715277778,3.2188585069444446,0.39449809510030864,3.2340253665123457,0.3877616222993827,3.247986593364198,0.380126953125,3.2607421875,0.36926020408163274,3.2752511160714293,0.35744678730867346,3.2875279017857144,0.3446867028061224,3.2975725446428568,0.3309799505739796,3.305385044642857,0.3163265306122449,3.3109654017857144,0.3007264429209184,3.314313616071429,0.2841796875,3.3154296875]],"9":[[0.51806640625,2.8193359375,0.518012955343935,2.799394415680474,0.5178526026257396,2.7795337925295858,0.5175853480954141,2.759754068047337,0.5172111917529586,2.7400552422337277,0.5167301335983728,2.7204373150887577,0.5161421736316568,2.700900286612426,0.5154473118528107,2.681444156804734,0.5146455482618343,2.6620689256656807,0.5137368828587278,2.6427745931952664,0.5127213156434911,2.623561159393491,0.5115988466161243,2.604428624260355,0.5103694757766273,2.585376987795858,0.509033203125,2.56640625,0.5074395073784721,2.546013726128472,0.5056694878472222,2.525851779513889,0.50372314453125,2.50592041015625,0.5016004774305556,2.4862196180555554,0.49930148654513884,2.4667494032118054,0.496826171875,2.447509765625,0.49417453342013895,2.4285007052951393,0.4913465711805556,2.4097222222222223,0.48834228515625,2.39117431640625,0.4851616753472222,2.3728569878472223,0.4818047417534722,2.354770236545139,0.478271484375,2.3369140625,0.4737719726562501,2.3158886718750002,0.4689648437500001,2.2953906250000005,0.46385009765624996,2.2754199218749998,0.45842773437500006,2.2559765625,0.45269775390625,2.237060546875,0.44666015625,2.218671875,0.44031494140625005,2.200810546875,0.433662109375,2.1834765625,0.42670166015625,2.166669921875,0.41943359375,2.150390625,0.4109549816743827,2.1330234857253085,0.40201220100308643,2.1165123456790123,0.39260525173611116,2.100857204861111,0.3827341338734568,2.086058063271605,0.3723988474151234,2.072114920910494,0.3615993923611111,2.0590277777777777,0.35033576871141975,2.046796633873457,0.33860797646604934,2.035421489197531,0.326416015625,2.02490234375,0.3120994567871094,2.0142593383789062,0.2971038818359375,2.005035400390625,0.2814292907714844,1.9972305297851562,0.26507568359375,1.9908447265625,0.24804306030273438,1.9858779907226562,0.2303314208984375,1.982330322265625,0.21194076538085938,1.9802017211914062,0.19287109375,1.9794921875,0.1820068359375,1.979827880859375,0.17041015625,1.9808349609375,0.1580810546875,1.982513427734375,0.14501953125,1.98486328125,0.1322021484375,1.987823486328125,0.12060546875,1.9913330078125,0.1102294921875,1.995391845703125,0.10107421875,2,0.10107421875,2.017578125,0.10107421875,2.03515625,0.10107421875,2.052734375,0.10107421875,2.0703125,0.10107421875,2.087890625,0.10107421875,2.10546875,0.10107421875,2.123046875,0.10107421875,2.140625,0.110504150390625,2.134613037109375,0.1207275390625,2.1292724609375,0.131744384765625,2.124603271484375,0.1435546875,2.12060546875,0.15570068359375,2.117401123046875,0.167724609375,2.1151123046875,0.17962646484375,2.113739013671875,0.19140625,2.11328125,0.20927734375000004,2.1140673828125003,0.22636718750000004,2.1164257812500007,0.24267578125,2.1203564453125,0.25820312500000003,2.125859375,0.27294921875,2.1329345703125,0.2869140625,2.14158203125,0.30009765624999996,2.1518017578124997,0.3125,2.16359375,0.32412109375000003,2.1769580078125,0.3349609375,2.19189453125,0.3434651692708333,2.2053900824652772,0.35156250000000006,2.219672309027778,0.3592529296875,2.2347412109375,0.3665364583333334,2.2505967881944446,0.37341308593749994,2.2672390407986107,0.3798828125,2.28466796875,0.3859456380208333,2.302883572048611,0.3916015625,2.3218858506944446,0.3968505859375,2.3416748046875,0.4016927083333333,2.3622504340277777,0.4061279296875,2.3836127387152777,0.41015625,2.40576171875,0.4129467010498047,2.422800064086914,0.41558074951171875,2.4401016235351562,0.4180583953857422,2.4576663970947266,0.420379638671875,2.475494384765625,0.4225444793701172,2.4935855865478516,0.42455291748046875,2.5119400024414062,0.4264049530029297,2.530557632446289,0.4281005859375,2.5494384765625,0.4296398162841797,2.568582534790039,0.43102264404296875,2.5879898071289062,0.4322490692138672,2.6076602935791016,0.433319091796875,2.627593994140625,0.4342327117919922,2.6477909088134766,0.43498992919921875,2.6682510375976562,0.4355907440185547,2.688974380493164,0.43603515625,2.7099609375,0.43017578125,2.7099609375,0.4239550781250001,2.691503906250001,0.41701171875,2.6736328125,0.409345703125,2.65634765625,0.40095703125000004,2.6396484375,0.391845703125,2.62353515625,0.3819726562500001,2.6083398437500005,0.371298828125,2.59439453125,0.35982421875,2.58169921875,0.347548828125,2.57025390625,0.33447265625,2.56005859375,0.32054687500000006,2.5514453125000003,0.30572265625,2.54474609375,0.29000000000000004,2.5399609375,0.27337890625,2.53708984375,0.255859375,2.5361328125,0.23846560108418371,2.537169164540817,0.22171954719387754,2.5402782206632653,0.20562121332908162,2.5454599808673466,0.19017059948979592,2.552714445153061,0.1753677056760204,2.5620416135204085,0.1612125318877551,2.5734414859693877,0.147705078125,2.5869140625,0.13770435474537035,2.598765432098765,0.12820999710648145,2.611750096450617,0.11922200520833334,2.6258680555555562,0.11074037905092593,2.6411193094135803,0.10276511863425927,2.6575038580246915,0.09529622395833334,2.675021701388889,0.08833369502314814,2.693672839506173,0.0818775318287037,2.713457272376543,0.075927734375,2.734375,0.07183329264322916,2.7507663302951384,0.06809488932291667,2.767713758680556,0.0647125244140625,2.78521728515625,0.06168619791666668,2.8032769097222228,0.05901590983072915,2.8218926323784714,0.05670166015625,2.841064453125,0.05474344889322917,2.860792371961806,0.05314127604166667,2.881076388888889,0.0518951416015625,2.90191650390625,0.05100504557291667,2.923312717013889,0.050470987955729164,2.945265028211806,0.05029296875,2.9677734375,0.05045909994452664,2.9902257072855036,0.050957493528106516,3.0122099204881656,0.051788149500739636,3.033726077107988,0.05295106786242604,3.054774177144971,0.05444624861316569,3.0753542205991127,0.056273691752958585,3.0954662074704142,0.05843339728180473,3.115110137758876,0.06092536519970414,3.1342860114644973,0.0637495955066568,3.152993828587278,0.06690608820266272,3.171233589127219,0.0703948432877219,3.18900529308432,0.07421586076183433,3.20630894045858,0.078369140625,3.22314453125,0.08421142578125,3.2442333984375002,0.0905078125,3.264335937500001,0.09725830078124999,3.2834521484374997,0.10446289062500001,3.30158203125,0.11212158203125,3.3187255859375,0.120234375,3.3348828125,0.12880126953124998,3.3500537109375,0.13782226562500002,3.3642382812500005,0.14729736328124998,3.3774365234375,0.1572265625,3.3896484375,0.17020416259765625,3.40338134765625,0.183746337890625,3.415283203125,0.19785308837890625,3.42535400390625,0.2125244140625,3.43359375,0.22776031494140625,3.44000244140625,0.243560791015625,3.444580078125,0.25992584228515625,3.44732666015625,0.27685546875,3.4482421875,0.294189453125,3.4471299913194446,0.31103515625,3.443793402777778,0.327392578125,3.438232421875,0.34326171875,3.4304470486111116,0.35864257812500006,3.420437282986111,0.37353515625,3.408203125,0.3843421936035156,3.3975753784179688,0.3947906494140625,3.385711669921875,0.4048805236816406,3.3726119995117188,0.41461181640625,3.3582763671875,0.4239845275878906,3.3427047729492188,0.4329986572265625,3.325897216796875,0.4416542053222656,3.3078536987304688,0.449951171875,3.28857421875,0.4557338746126033,3.2737926136363633,0.4612744544163223,3.2583895596590904,0.46657291128615697,3.242365056818181,0.4716292452221074,3.2257191051136362,0.47644345622417356,3.208451704545454,0.4810155442923554,3.1905628551136367,0.4853455094266529,3.1720525568181817,0.48943335162706614,3.152920809659091,0.4932790708935951,3.1331676136363633,0.4968826672262397,3.11279296875,0.500244140625,3.091796875,0.5026992486447704,3.0748614875637754,0.5049724968112246,3.0575374681122454,0.5070638851243622,3.0398248166454076,0.5089734135841837,3.0217235331632653,0.5107010821906887,3.003233617665816,0.5122468909438775,2.984355070153061,0.51361083984375,2.965087890625,0.5147929288903061,2.9454320790816326,0.515793158083546,2.9253876355229593,0.5166115274234694,2.9049545599489797,0.5172480369100765,2.884132852359694,0.5177026865433674,2.862922512755102,0.5179754763233418,2.8413235411352042,0.51806640625,2.8193359375],[0.27685546875,3.310546875,0.2610054016113281,3.3092193603515625,0.2459259033203125,3.30523681640625,0.23161697387695312,3.2985992431640625,0.21807861328125,3.289306640625,0.20531082153320312,3.2773590087890625,0.1933135986328125,3.26275634765625,0.18208694458007812,3.2454986572265625,0.171630859375,3.2255859375,0.1660816426179847,3.2130102040816326,0.16094347895408168,3.1995575573979598,0.1562163683832908,3.1852279974489797,0.15190031090561226,3.170021524234694,0.1479953065210459,3.1539381377551017,0.14450135522959182,3.136977838010204,0.14141845703125,3.119140625,0.13874661192602042,3.10042649872449,0.13648581991390307,3.0808354591836733,0.13463608099489796,3.060367506377551,0.13319739516900508,3.039022640306123,0.1321697624362245,3.016800860969388,0.13155318279655612,2.9937021683673466,0.13134765625,2.9697265625,0.13158840603298608,2.9465399848090272,0.13231065538194448,2.9241807725694446,0.133514404296875,2.90264892578125,0.1351996527777778,2.8819444444444446,0.13736640082465276,2.8620673285590272,0.1400146484375,2.843017578125,0.14314439561631945,2.824795193142361,0.1467556423611111,2.807400173611111,0.150848388671875,2.79083251953125,0.1554226345486111,2.7750922309027777,0.16047837999131942,2.7601793077256946,0.166015625,2.74609375,0.17418378665123455,2.728877314814815,0.1832802854938272,2.7136863425925926,0.1933051215277778,2.700520833333334,0.20425829475308643,2.689380787037037,0.2161398051697531,2.6802662037037037,0.2289496527777778,2.6731770833333335,0.2426878375771605,2.6681134259259256,0.25735435956790126,2.665075231481482,0.27294921875,2.6640625,0.28917778862847227,2.665174696180556,0.3046603732638889,2.6685112847222223,0.31939697265625,2.674072265625,0.3333875868055556,2.681857638888889,0.3466322157118056,2.6918674045138893,0.359130859375,2.7041015625,0.3707953559027778,2.7180447048611116,0.3815375434027778,2.733181423611111,0.391357421875,2.74951171875,0.4002549913194444,2.7670355902777777,0.40823025173611116,2.7857530381944446,0.415283203125,2.8056640625,0.4205297353316327,2.823301977040817,0.42496910873724486,2.8410594706632653,0.4286013233418367,2.8589365433673466,0.43142637914540816,2.876933195153061,0.4334442761479592,2.895049426020408,0.43465501434948983,2.9132852359693877,0.43505859375,2.931640625,0.434864277742347,2.950095663265307,0.43428132971938777,2.9686304209183674,0.4333097496811224,2.9872448979591835,0.431949537627551,3.005939094387755,0.4302006935586735,3.0247130102040813,0.42806321747448983,3.0435666454081636,0.425537109375,3.0625,0.42261240433673475,3.0812639508928577,0.41927913743622447,3.099609375,0.41553730867346933,3.1175362723214284,0.4113869180484694,3.1350446428571432,0.40682796556122447,3.1521344866071432,0.40186045121173475,3.168805803571429,0.396484375,3.18505859375,0.3896891276041667,3.2031792534722223,0.38232421875000006,3.220106336805556,0.3743896484375,3.23583984375,0.3658854166666667,3.250379774305556,0.3568115234375,3.2637261284722223,0.34716796875,3.27587890625,0.33482421875,3.2883593750000006,0.32162109375000003,3.29806640625,0.30755859375,3.3049999999999997,0.29263671875,3.30916015625,0.27685546875,3.310546875]],"C":[[0.40283203125,3.3017578125,0.3861955915178572,3.300930723852041,0.3700474330357143,3.298449457908163,0.3543875558035714,3.2943140146683674,0.3392159598214286,3.288524394132653,0.3245326450892857,3.2810805963010203,0.3103376116071429,3.2719826211734695,0.296630859375,3.26123046875,0.28507232666015625,3.2505340576171875,0.273956298828125,3.23870849609375,0.26328277587890625,3.2257537841796875,0.2530517578125,3.211669921875,0.24326324462890625,3.1964569091796875,0.233917236328125,3.18011474609375,0.22501373291015625,3.1626434326171875,0.216552734375,3.14404296875,0.21011474609375,3.1283935546875004,0.20398437500000005,3.1121093750000006,0.19816162109375,3.0951904296874995,0.192646484375,3.07763671875,0.18743896484375,3.0594482421875,0.1825390625,3.040625,0.17794677734375,3.0211669921875,0.173662109375,3.0010742187500004,0.16968505859375002,2.9803466796875,0.166015625,2.958984375,0.16341531065088763,2.9421458949704147,0.16102302144970415,2.924983820266272,0.1588387573964497,2.9074981508875735,0.15686251849112426,2.889688886834319,0.15509430473372782,2.871556028106509,0.15353411612426035,2.853099574704142,0.1521819526627219,2.834319526627219,0.15103781434911243,2.81521588387574,0.15010170118343197,2.795788646449704,0.14937361316568046,2.7760378143491122,0.14885355029585798,2.7559633875739644,0.1485415125739645,2.7355653661242605,0.1484375,2.71484375,0.14854717254638672,2.692567825317383,0.14887619018554688,2.6706619262695312,0.14942455291748047,2.6491260528564453,0.1501922607421875,2.627960205078125,0.15117931365966797,2.6071643829345703,0.15238571166992188,2.5867385864257812,0.15381145477294922,2.566682815551758,0.15545654296875,2.5469970703125,0.15732097625732422,2.527681350708008,0.15940475463867188,2.5087356567382812,0.16170787811279297,2.4901599884033203,0.1642303466796875,2.471954345703125,0.16697216033935547,2.4541187286376953,0.16993331909179688,2.4366531372070312,0.17311382293701172,2.419557571411133,0.176513671875,2.40283203125,0.18138631184895831,2.381195068359375,0.18664550781250003,2.360392252604167,0.192291259765625,2.340423583984375,0.19832356770833334,2.3212890625,0.20474243164062497,2.3029886881510415,0.2115478515625,2.2855224609375,0.21873982747395831,2.268890380859375,0.226318359375,2.253092447916667,0.234283447265625,2.238128662109375,0.24263509114583334,2.2239990234375,0.251373291015625,2.2107035319010415,0.260498046875,2.1982421875,0.27326871141975306,2.183075327932099,0.2867326147762346,2.1696928047839505,0.3008897569444445,2.158094618055556,0.3157401379243827,2.1482807677469133,0.3312837577160494,2.1402512538580245,0.3475206163194444,2.134006076388889,0.36445071373456794,2.1295452353395063,0.38207404996141975,2.1268687307098766,0.400390625,2.1259765625,0.4193066406250001,2.1266210937500003,0.4377734375,2.1285546875000003,0.455791015625,2.13177734375,0.473359375,2.1362890625000004,0.490478515625,2.14208984375,0.5072851562500001,2.1488671875000005,0.523916015625,2.15630859375,0.5403710937499999,2.1644140625,0.556650390625,2.17318359375,0.57275390625,2.1826171875,0.57275390625,2.16455078125,0.57275390625,2.146484375,0.57275390625,2.12841796875,0.57275390625,2.1103515625,0.57275390625,2.09228515625,0.57275390625,2.07421875,0.57275390625,2.05615234375,0.57275390625,2.0380859375,0.5569726562500001,2.0269726562500003,0.54087890625,2.0170703125,0.5244726562500001,2.00837890625,0.50775390625,2.0008984375,0.49072265625,1.99462890625,0.4759521484375,1.990302191840278,0.4602864583333334,1.986762152777778,0.4437255859375,1.9840087890625,0.42626953125,1.9820421006944444,0.4079182942708333,1.9808620876736112,0.388671875,1.98046875,0.37077331542968744,1.981096055772569,0.35337320963541674,1.9829779730902781,0.3364715576171875,1.986114501953125,0.32006835937500006,1.9905056423611114,0.3041636149088541,1.996151394314236,0.28875732421875,2.0030517578125,0.2738494873046875,2.0112067328559027,0.2594401041666667,2.0206163194444446,0.2455291748046875,2.031280517578125,0.23211669921875,2.0431993272569446,0.21920267740885419,2.0563727484809027,0.206787109375,2.07080078125,0.197216796875,2.0831770833333336,0.18797200520833335,2.096217447916667,0.17905273437500005,2.109921875000001,0.17045898437500004,2.1242903645833335,0.16219075520833337,2.139322916666667,0.15424804687500002,2.15501953125,0.146630859375,2.171380208333334,0.13933919270833334,2.1884049479166667,0.132373046875,2.20609375,0.125732421875,2.224446614583333,0.11941731770833333,2.2434635416666664,0.113427734375,2.26314453125,0.10776367187500001,2.283489583333333,0.10242513020833334,2.3044986979166664,0.097412109375,2.326171875,0.09386535644531249,2.34282470703125,0.09050048828125,2.3597753906250003,0.0873175048828125,2.37702392578125,0.08431640625000002,2.3945703125000004,0.0814971923828125,2.41241455078125,0.07885986328125,2.430556640625,0.0764044189453125,2.44899658203125,0.074130859375,2.467734375,0.07203918457031251,2.4867700195312503,0.07012939453125,2.506103515625,0.0684014892578125,2.52573486328125,0.06685546875000001,2.5456640625,0.0654913330078125,2.56589111328125,0.06430908203125,2.586416015625,0.0633087158203125,2.60723876953125,0.062490234375,2.628359375,0.06185363769531249,2.64977783203125,0.061398925781249995,2.671494140625,0.06112609863281251,2.69350830078125,0.06103515625,2.7158203125,0.06113606770833334,2.7373111979166667,0.061438802083333334,2.7585546875,0.061943359375000014,2.7795507812500007,0.06264973958333335,2.8002994791666667,0.06355794270833333,2.8208007812500004,0.06466796875,2.8410546875,0.06597981770833333,2.8610611979166665,0.06749348958333333,2.8808203125,0.069208984375,2.9003320312499996,0.07112630208333333,2.919596354166667,0.07324544270833333,2.93861328125,0.07556640625000001,2.9573828125,0.07808919270833334,2.975904947916667,0.08081380208333334,2.9941796875,0.083740234375,3.01220703125,0.08735756055843198,3.0326483912721898,0.09122336122411243,3.0526563655695265,0.09533763637204141,3.0722309541420114,0.09970038600221892,3.091372156989645,0.10431161011464497,3.110079974112426,0.10917130870931953,3.128354405510355,0.11427948178624261,3.1461954511834316,0.1196361293454142,3.163603111131657,0.12524125138683434,3.1805773853550297,0.13109484791050297,3.19711827385355,0.1371969189164201,3.213225776627219,0.1435474644045858,3.2288998936760356,0.150146484375,3.244140625,0.1590869140625,3.2632275390625,0.16842773437500003,3.2814257812500007,0.1781689453125,3.2987353515624998,0.188310546875,3.31515625,0.1988525390625,3.3306884765625,0.209794921875,3.34533203125,0.2211376953125,3.3590869140625,0.23288085937500003,3.371953125,0.2450244140625,3.3839306640625,0.257568359375,3.39501953125,0.2719605999228395,3.4061897183641974,0.28681700906635804,3.4160457658179015,0.3021375868055556,3.4245876736111116,0.3179223331404321,3.431815441743827,0.33417124807098764,3.43772907021605,0.3508843315972222,3.4423285590277777,0.3680615837191358,3.4456139081790123,0.38570300443672845,3.447585117669753,0.40380859375,3.4482421875,0.4217664930555556,3.447645399305556,0.43950737847222227,3.4458550347222223,0.45703125,3.44287109375,0.4743381076388889,3.4386935763888893,0.49142795138888895,3.433322482638889,0.50830078125,3.4267578125,0.5247938368055556,3.419080946180556,0.540744357638889,3.4103732638888893,0.55615234375,3.400634765625,0.5710177951388888,3.389865451388889,0.5853407118055556,3.3780653211805554,0.59912109375,3.365234375,0.594970703125,3.34765625,0.5908203125,3.330078125,0.586669921875,3.3125,0.58251953125,3.294921875,0.578369140625,3.27734375,0.57421875,3.259765625,0.570068359375,3.2421875,0.56591796875,3.224609375,0.5519824218750001,3.2367773437500005,0.5375195312500001,3.2482812500000002,0.522529296875,3.25912109375,0.5070117187500001,3.269296875,0.490966796875,3.27880859375,0.4743945312500001,3.2870703125000014,0.457294921875,3.29349609375,0.43966796875,3.2980859375,0.421513671875,3.3008398437500004,0.40283203125,3.3017578125]],"M":[[0.4794921875,2,0.4625244140625,2,0.445556640625,2,0.4285888671875,2,0.41162109375,2,0.4079326923076923,2.0194411057692307,0.4042442908653846,2.0388822115384615,0.4005558894230769,2.0583233173076922,0.3968674879807692,2.077764423076923,0.39317908653846156,2.0972055288461537,0.38949068509615387,2.1166466346153845,0.38580228365384617,2.1360877403846152,0.38211388221153847,2.155528846153846,0.37842548076923077,2.1749699519230767,0.3747370793269231,2.1944110576923075,0.3710486778846154,2.2138521634615387,0.3673602764423077,2.2332932692307694,0.363671875,2.252734375,0.35998347355769234,2.272175480769231,0.3562950721153846,2.2916165865384617,0.35260667067307694,2.3110576923076924,0.34891826923076924,2.330498798076923,0.34522986778846154,2.349939903846154,0.34154146634615384,2.3693810096153847,0.33785306490384615,2.3888221153846154,0.33416466346153845,2.408263221153846,0.33047626201923075,2.427704326923077,0.3267878605769231,2.4471454326923077,0.32309945913461535,2.4665865384615384,0.3194110576923077,2.486027644230769,0.31572265625,2.50546875,0.3120342548076923,2.5249098557692307,0.3083458533653846,2.5443509615384614,0.3046574519230769,2.563792067307692,0.3009690504807692,2.5832331730769234,0.2972806490384615,2.6026742788461537,0.2935922475961538,2.622115384615385,0.2899038461538462,2.641556490384615,0.2862154447115385,2.6609975961538463,0.2825270432692308,2.6804387019230766,0.2788386418269231,2.699879807692308,0.2751502403846154,2.7193209134615386,0.2714618389423077,2.7387620192307693,0.26777343750000004,2.758203125,0.2640850360576923,2.777644230769231,0.26039663461538465,2.7970853365384616,0.2567082331730769,2.8165264423076923,0.25301983173076925,2.835967548076923,0.24933143028846153,2.855408653846154,0.24564302884615385,2.8748497596153846,0.24195462740384616,2.8942908653846153,0.23826622596153846,2.913731971153846,0.23457782451923076,2.933173076923077,0.2308894230769231,2.9526141826923076,0.22720102163461536,2.9720552884615383,0.2235126201923077,2.991496394230769,0.21982421875,3.0109375,0.21613581730769232,3.030378605769231,0.2124474158653846,3.0498197115384613,0.20875901442307693,3.0692608173076925,0.20507061298076923,3.088701923076923,0.20138221153846153,3.108143028846154,0.19769381009615383,3.1275841346153848,0.19400540865384616,3.1470252403846155,0.19031700721153844,3.1664663461538463,0.18662860576923077,3.185907451923077,0.18294020432692307,3.2053485576923078,0.1792518028846154,3.2247896634615385,0.17556340144230767,3.2442307692307693,0.171875,3.263671875,0.16796875,3.263671875,0.16873046875000006,3.2469531250000006,0.169453125,3.228828125,0.17013671875,3.209296875,0.17078125,3.188359375,0.17138671875,3.166015625,0.17179528061224492,3.149344308035715,0.172183912627551,3.1322544642857144,0.17255261479591832,3.1147460937499996,0.17290138711734693,3.096819196428571,0.17323022959183673,3.078473772321429,0.17353914221938777,3.059709821428571,0.173828125,3.04052734375,0.1740872130102041,3.021085778061225,0.17430644132653061,3.001544563137755,0.17448580994897958,2.9819036989795915,0.174625318877551,2.9621631855867347,0.1747249681122449,2.942323022959184,0.17478475765306123,2.9223832110969385,0.1748046875,2.90234375,0.1748046875,2.8827275815217392,0.1748046875,2.863111413043478,0.1748046875,2.8434952445652173,0.1748046875,2.8238790760869565,0.1748046875,2.804262907608696,0.1748046875,2.7846467391304346,0.1748046875,2.765030570652174,0.1748046875,2.745414402173913,0.1748046875,2.7257982336956523,0.1748046875,2.706182065217391,0.1748046875,2.6865658967391304,0.1748046875,2.6669497282608696,0.1748046875,2.647333559782609,0.1748046875,2.6277173913043477,0.1748046875,2.608101222826087,0.1748046875,2.588485054347826,0.1748046875,2.5688688858695654,0.1748046875,2.549252717391304,0.1748046875,2.5296365489130435,0.1748046875,2.5100203804347827,0.1748046875,2.490404211956522,0.1748046875,2.4707880434782608,0.1748046875,2.451171875,0.1748046875,2.4315557065217392,0.1748046875,2.4119395380434785,0.1748046875,2.3923233695652173,0.1748046875,2.3727072010869565,0.1748046875,2.3530910326086953,0.1748046875,2.3334748641304346,0.1748046875,2.313858695652174,0.1748046875,2.294242527173913,0.1748046875,2.2746263586956523,0.1748046875,2.2550101902173916,0.1748046875,2.2353940217391304,0.1748046875,2.2157778532608696,0.1748046875,2.1961616847826084,0.1748046875,2.1765455163043477,0.1748046875,2.156929347826087,0.1748046875,2.137313179347826,0.1748046875,2.1176970108695654,0.1748046875,2.0980808423913047,0.1748046875,2.0784646739130435,0.1748046875,2.0588485054347827,0.1748046875,2.0392323369565215,0.1748046875,2.0196161684782608,0.1748046875,2,0.155517578125,2,0.13623046875,2,0.116943359375,2,0.09765625,2,0.09765625,2.0198296440972223,0.09765625,2.0396592881944446,0.09765625,2.0594889322916665,0.09765625,2.079318576388889,0.09765625,2.099148220486111,0.09765625,2.1189778645833335,0.09765625,2.1388075086805554,0.09765625,2.1586371527777777,0.09765625,2.178466796875,0.09765625,2.1982964409722223,0.09765625,2.2181260850694446,0.09765625,2.2379557291666665,0.09765625,2.257785373263889,0.09765625,2.277615017361111,0.09765625,2.2974446614583335,0.09765625,2.3172743055555554,0.09765625,2.3371039496527777,0.09765625,2.35693359375,0.09765625,2.3767632378472223,0.09765625,2.3965928819444446,0.09765625,2.4164225260416665,0.09765625,2.436252170138889,0.09765625,2.456081814236111,0.09765625,2.4759114583333335,0.09765625,2.4957411024305554,0.09765625,2.5155707465277777,0.09765625,2.535400390625,0.09765625,2.5552300347222223,0.09765625,2.5750596788194446,0.09765625,2.594889322916667,0.09765625,2.614718967013889,0.09765625,2.634548611111111,0.09765625,2.654378255208333,0.09765625,2.6742078993055554,0.09765625,2.6940375434027777,0.09765625,2.7138671875,0.09765625,2.7336968315972223,0.09765625,2.7535264756944446,0.09765625,2.7733561197916665,0.09765625,2.793185763888889,0.09765625,2.813015407986111,0.09765625,2.8328450520833335,0.09765625,2.8526746961805554,0.09765625,2.8725043402777777,0.09765625,2.892333984375,0.09765625,2.9121636284722223,0.09765625,2.9319932725694446,0.09765625,2.9518229166666665,0.09765625,2.971652560763889,0.09765625,2.991482204861111,0.09765625,3.0113118489583335,0.09765625,3.0311414930555554,0.09765625,3.0509711371527777,0.09765625,3.07080078125,0.09765625,3.0906304253472223,0.09765625,3.1104600694444446,0.09765625,3.1302897135416665,0.09765625,3.150119357638889,0.09765625,3.169949001736111,0.09765625,3.1897786458333335,0.09765625,3.2096082899305554,0.09765625,3.2294379340277777,0.09765625,3.249267578125,0.09765625,3.2690972222222223,0.09765625,3.2889268663194446,0.09765625,3.3087565104166665,0.09765625,3.328586154513889,0.09765625,3.348415798611111,0.09765625,3.3682454427083335,0.09765625,3.3880750868055554,0.09765625,3.4079047309027777,0.09765625,3.427734375,0.115234375,3.427734375,0.1328125,3.427734375,0.150390625,3.427734375,0.16796875,3.427734375,0.185546875,3.427734375,0.203125,3.427734375,0.220703125,3.427734375,0.22440925973360656,3.408267161885246,0.22811539446721313,3.3887999487704916,0.23182152920081966,3.3693327356557377,0.23552766393442623,3.3498655225409837,0.2392337986680328,3.3303983094262297,0.24293993340163933,3.3109310963114753,0.2466460681352459,3.2914638831967213,0.25035220286885246,3.2719966700819674,0.254058337602459,3.252529456967213,0.2577644723360656,3.233062243852459,0.2614706070696721,3.213595030737705,0.26517674180327866,3.1941278176229506,0.26888287653688525,3.1746606045081966,0.2725890112704918,3.1551933913934427,0.2762951460040984,3.1357261782786887,0.2800012807377049,3.1162589651639343,0.28370741547131145,3.0967917520491803,0.28741355020491804,3.0773245389344264,0.2911196849385246,3.057857325819672,0.2948258196721312,3.038390112704918,0.2985319544057377,3.018922899590164,0.30223808913934425,2.99945568647541,0.30594422387295084,2.9799884733606556,0.3096503586065574,2.9605212602459017,0.3133564933401639,2.9410540471311477,0.3170626280737705,2.9215868340163933,0.32076876280737704,2.9021196209016393,0.3244748975409836,2.8826524077868854,0.32818103227459017,2.8631851946721314,0.3318871670081967,2.843717981557377,0.3355933017418033,2.824250768442623,0.33929943647540983,2.8047835553278686,0.3430055712090164,2.7853163422131146,0.3467117059426229,2.7658491290983607,0.3504178406762295,2.7463819159836067,0.3541239754098361,2.7269147028688527,0.3578301101434426,2.7074474897540983,0.36153624487704916,2.6879802766393444,0.36524237961065575,2.66851306352459,0.3689485143442623,2.649045850409836,0.3726546490778688,2.629578637295082,0.3763607838114754,2.610111424180328,0.38006691854508196,2.5906442110655736,0.3837730532786885,2.5711769979508197,0.3874791880122951,2.5517097848360653,0.3911853227459016,2.5322425717213113,0.39489145747950816,2.5127753586065573,0.39859759221311475,2.4933081454918034,0.40230372694672134,2.4738409323770494,0.4060098616803279,2.454373719262295,0.4097159964139344,2.434906506147541,0.413422131147541,2.415439293032787,0.41712826588114754,2.3959720799180326,0.4208344006147541,2.3765048668032787,0.42454053534836067,2.3570376536885247,0.4282466700819672,2.3375704405737707,0.43195280481557374,2.3181032274590163,0.43565893954918034,2.2986360143442623,0.43936507428278687,2.279168801229508,0.4430712090163934,2.259701588114754,0.44677734375,2.240234375,0.4501953125,2.240234375,0.4539574795081967,2.259701588114754,0.45771964651639346,2.2791688012295084,0.46148181352459017,2.2986360143442623,0.46524398053278687,2.3181032274590163,0.46900614754098363,2.3375704405737703,0.47276831454918034,2.3570376536885247,0.47653048155737704,2.3765048668032787,0.48029264856557374,2.3959720799180326,0.4840548155737705,2.415439293032787,0.4878169825819672,2.434906506147541,0.4915791495901639,2.454373719262295,0.49534131659836067,2.4738409323770494,0.4991034836065574,2.4933081454918034,0.5028656506147541,2.5127753586065573,0.5066278176229508,2.5322425717213113,0.5103899846311475,2.5517097848360657,0.5141521516393442,2.5711769979508197,0.517914318647541,2.5906442110655736,0.5216764856557377,2.610111424180328,0.5254386526639344,2.629578637295082,0.5292008196721312,2.649045850409836,0.5329629866803278,2.66851306352459,0.5367251536885246,2.6879802766393444,0.5404873206967213,2.7074474897540983,0.544249487704918,2.7269147028688523,0.5480116547131147,2.7463819159836067,0.5517738217213115,2.7658491290983607,0.5555359887295082,2.7853163422131146,0.5592981557377049,2.8047835553278686,0.5630603227459017,2.824250768442623,0.5668224897540983,2.843717981557377,0.5705846567622951,2.8631851946721314,0.5743468237704918,2.8826524077868854,0.5781089907786885,2.9021196209016393,0.5818711577868853,2.9215868340163933,0.585633324795082,2.9410540471311473,0.5893954918032787,2.9605212602459017,0.5931576588114754,2.9799884733606556,0.5969198258196722,2.99945568647541,0.6006819928278688,3.018922899590164,0.6044441598360656,3.038390112704918,0.6082063268442623,3.057857325819672,0.611968493852459,3.0773245389344264,0.6157306608606558,3.0967917520491803,0.6194928278688525,3.1162589651639347,0.6232549948770492,3.1357261782786887,0.6270171618852459,3.1551933913934427,0.6307793288934427,3.1746606045081966,0.6345414959016393,3.1941278176229506,0.6383036629098361,3.213595030737705,0.6420658299180328,3.233062243852459,0.6458279969262295,3.252529456967213,0.6495901639344263,3.2719966700819674,0.653352330942623,3.2914638831967213,0.6571144979508197,3.3109310963114753,0.6608766649590164,3.3303983094262293,0.6646388319672132,3.3498655225409837,0.6684009989754098,3.3693327356557377,0.6721631659836065,3.388799948770492,0.6759253329918032,3.408267161885246,0.6796875,3.427734375,0.6971261160714286,3.427734375,0.7145647321428571,3.427734375,0.7320033482142857,3.427734375,0.7494419642857143,3.427734375,0.7668805803571429,3.427734375,0.7843191964285714,3.427734375,0.8017578125,3.427734375,0.8017578125,3.4079047309027777,0.8017578125,3.3880750868055554,0.8017578125,3.3682454427083335,0.8017578125,3.348415798611111,0.8017578125,3.328586154513889,0.8017578125,3.3087565104166665,0.8017578125,3.2889268663194446,0.8017578125,3.2690972222222223,0.8017578125,3.249267578125,0.8017578125,3.2294379340277777,0.8017578125,3.2096082899305554,0.8017578125,3.1897786458333335,0.8017578125,3.169949001736111,0.8017578125,3.150119357638889,0.8017578125,3.1302897135416665,0.8017578125,3.1104600694444446,0.8017578125,3.0906304253472223,0.8017578125,3.07080078125,0.8017578125,3.0509711371527777,0.8017578125,3.0311414930555554,0.8017578125,3.0113118489583335,0.8017578125,2.991482204861111,0.8017578125,2.971652560763889,0.8017578125,2.9518229166666665,0.8017578125,2.9319932725694446,0.8017578125,2.9121636284722223,0.8017578125,2.892333984375,0.8017578125,2.8725043402777777,0.8017578125,2.8526746961805554,0.8017578125,2.832845052083333,0.8017578125,2.813015407986111,0.8017578125,2.793185763888889,0.8017578125,2.773356119791667,0.8017578125,2.7535264756944446,0.8017578125,2.7336968315972223,0.8017578125,2.7138671875,0.8017578125,2.6940375434027777,0.8017578125,2.6742078993055554,0.8017578125,2.6543782552083335,0.8017578125,2.634548611111111,0.8017578125,2.614718967013889,0.8017578125,2.5948893229166665,0.8017578125,2.5750596788194446,0.8017578125,2.5552300347222223,0.8017578125,2.535400390625,0.8017578125,2.5155707465277777,0.8017578125,2.4957411024305554,0.8017578125,2.4759114583333335,0.8017578125,2.456081814236111,0.8017578125,2.436252170138889,0.8017578125,2.4164225260416665,0.8017578125,2.3965928819444446,0.8017578125,2.3767632378472223,0.8017578125,2.35693359375,0.8017578125,2.3371039496527777,0.8017578125,2.3172743055555554,0.8017578125,2.2974446614583335,0.8017578125,2.277615017361111,0.8017578125,2.257785373263889,0.8017578125,2.2379557291666665,0.8017578125,2.2181260850694446,0.8017578125,2.1982964409722223,0.8017578125,2.178466796875,0.8017578125,2.1586371527777777,0.8017578125,2.1388075086805554,0.8017578125,2.1189778645833335,0.8017578125,2.099148220486111,0.8017578125,2.079318576388889,0.8017578125,2.0594889322916665,0.8017578125,2.0396592881944446,0.8017578125,2.0198296440972223,0.8017578125,2,0.7853515625,2,0.7689453125,2,0.7525390625,2,0.7361328125,2,0.7197265625,2,0.7197265625,2.0198709239130435,0.7197265625,2.039741847826087,0.7197265625,2.0596127717391304,0.7197265625,2.079483695652174,0.7197265625,2.0993546195652173,0.7197265625,2.1192255434782608,0.7197265625,2.139096467391304,0.7197265625,2.1589673913043477,0.7197265625,2.178838315217391,0.7197265625,2.1987092391304346,0.7197265625,2.2185801630434785,0.7197265625,2.2384510869565215,0.7197265625,2.2583220108695654,0.7197265625,2.278192934782609,0.7197265625,2.2980638586956523,0.7197265625,2.317934782608696,0.7197265625,2.3378057065217392,0.7197265625,2.3576766304347827,0.7197265625,2.377547554347826,0.7197265625,2.3974184782608696,0.7197265625,2.417289402173913,0.7197265625,2.4371603260869565,0.7197265625,2.45703125,0.7197265625,2.4769021739130435,0.7197265625,2.496773097826087,0.7197265625,2.5166440217391304,0.7197265625,2.536514945652174,0.7197265625,2.5563858695652173,0.7197265625,2.5762567934782608,0.7197265625,2.596127717391304,0.7197265625,2.6159986413043477,0.7197265625,2.635869565217391,0.7197265625,2.6557404891304346,0.7197265625,2.6756114130434785,0.7197265625,2.6954823369565215,0.7197265625,2.7153532608695654,0.7197265625,2.735224184782609,0.7197265625,2.7550951086956523,0.7197265625,2.774966032608696,0.7197265625,2.7948369565217392,0.7197265625,2.8147078804347827,0.7197265625,2.834578804347826,0.7197265625,2.8544497282608696,0.7197265625,2.874320652173913,0.7197265625,2.8941915760869565,0.7197265625,2.9140625,0.7197464923469389,2.9322086256377555,0.7198062818877551,2.950374681122449,0.7199059311224489,2.9685606664540813,0.7200454400510204,2.986766581632653,0.7202248086734694,3.0049924266581636,0.720444036989796,3.0232382015306123,0.720703125,3.04150390625,0.7209921077806123,3.059640066964286,0.7213010204081632,3.0774972098214284,0.721629862882653,3.0950753348214284,0.7219786352040816,3.1123744419642856,0.7223473373724489,3.12939453125,0.7227359693877552,3.146135602678571,0.72314453125,3.16259765625,0.7237304687500001,3.18484375,0.72431640625,3.20587890625,0.72490234375,3.225703125,0.7254882812500001,3.2443164062500003,0.72607421875,3.26171875,0.72216796875,3.26171875,0.7184344951923077,3.2423076923076923,0.7147010216346154,3.2228966346153847,0.7109675480769231,3.203485576923077,0.7072340745192308,3.1840745192307693,0.7035006009615384,3.1646634615384617,0.6997671274038462,3.145252403846154,0.6960336538461538,3.1258413461538463,0.6923001802884615,3.1064302884615387,0.6885667067307693,3.0870192307692306,0.6848332331730769,3.067608173076923,0.6810997596153846,3.0481971153846152,0.6773662860576923,3.0287860576923076,0.6736328125,3.009375,0.6698993389423077,2.9899639423076922,0.6661658653846154,2.9705528846153846,0.662432391826923,2.951141826923077,0.6586989182692308,2.9317307692307693,0.6549654447115385,2.9123197115384616,0.6512319711538461,2.892908653846154,0.6474984975961539,2.8734975961538463,0.6437650240384616,2.8540865384615386,0.6400315504807692,2.834675480769231,0.6362980769230769,2.815264423076923,0.6325646033653847,2.795853365384615,0.6288311298076923,2.7764423076923075,0.62509765625,2.75703125,0.6213641826923076,2.737620192307692,0.6176307091346154,2.7182091346153845,0.6138972355769231,2.698798076923077,0.6101637620192307,2.679387019230769,0.6064302884615385,2.6599759615384615,0.6026968149038462,2.640564903846154,0.5989633413461538,2.621153846153846,0.5952298677884615,2.6017427884615385,0.5914963942307693,2.582331730769231,0.5877629206730769,2.562920673076923,0.5840294471153846,2.5435096153846155,0.5802959735576922,2.524098557692308,0.5765625,2.5046875,0.5728290264423077,2.4852764423076925,0.5690955528846153,2.465865384615385,0.5653620793269231,2.4464543269230767,0.5616286057692308,2.4270432692307695,0.5578951322115384,2.4076322115384614,0.5541616586538461,2.3882211538461537,0.5504281850961539,2.368810096153846,0.5466947115384615,2.3493990384615384,0.5429612379807692,2.3299879807692307,0.539227764423077,2.310576923076923,0.5354942908653846,2.2911658653846154,0.5317608173076923,2.2717548076923078,0.52802734375,2.2523437499999996,0.5242938701923077,2.2329326923076924,0.5205603966346154,2.2135216346153843,0.5168269230769231,2.1941105769230766,0.5130934495192307,2.174699519230769,0.5093599759615385,2.1552884615384613,0.5056265024038461,2.1358774038461537,0.5018930288461538,2.116466346153846,0.49815955528846156,2.0970552884615383,0.49442608173076924,2.0776442307692307,0.4906926081730769,2.058233173076923,0.48695913461538465,2.0388221153846153,0.48322566105769227,2.0194110576923077,0.4794921875,2]]};
let fontLoaded = true;

// Load TTF font and generate FONT_DATA
async function loadFont(fontPath = 'fonts/sans.ttf') {
  fontLoaded = true;
  // try {
  //   const response = await fetch(fontPath);
  //   const arrayBuffer = await response.arrayBuffer();
  //   const font = opentype.parse(arrayBuffer);

  //   FONT_DATA = {};
  //   const characters = '0123456789CM';

  //   for (const char of characters) {
  //     const glyph = font.charToGlyph(char);
  //     if (glyph) {
  //       FONT_DATA[char] = convertGlyphToLines(glyph, font);
  //     }
  //   }

  //   fontLoaded = true;
  //   console.log('Font loaded successfully:', Object.keys(FONT_DATA).length, 'characters');
  //   console.log(JSON.stringify(FONT_DATA));
  // } catch (error) {
  // }
}

// Convert glyph outline to line segments format (matching original FONT_DATA format)
function convertGlyphToLines(glyph, font) {
  const path = glyph.getPath(0, 0, font.unitsPerEm);
  const scale = 1 / font.unitsPerEm; // Normalize to 0-1 range
  const targetHeight = 2; // Match the height of current font data

  const segments = [];
  let currentSegment = [];
  let lastX = 0, lastY = 0;
  let startX = 0, startY = 0;

  const toXY = (x, y) => [x * scale, (font.unitsPerEm - y) * scale * targetHeight];

  const appendPoint = (x, y) => {
    if (currentSegment.length >= 2) {
      const lx = currentSegment[currentSegment.length - 2];
      const ly = currentSegment[currentSegment.length - 1];
      if (Math.abs(lx - x) < 1e-5 && Math.abs(ly - y) < 1e-5) {
        return; // Skip duplicate points
      }
    }
    currentSegment.push(x, y);
    lastX = x;
    lastY = y;
  };

  const subdivideLine = (x0, y0, x1, y1) => {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const steps = Math.max(1, Math.ceil(dist / 0.02)); // More steps -> smoother edges
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      appendPoint(x0 + dx * t, y0 + dy * t);
    }
  };

  const subdivideQuad = (x0, y0, x1, y1, x2, y2) => {
    const ctrlLen = Math.hypot(x0 - x1, y0 - y1) + Math.hypot(x1 - x2, y1 - y2);
    const steps = Math.max(4, Math.ceil(ctrlLen / 0.02));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const inv = 1 - t;
      const x = inv * inv * x0 + 2 * inv * t * x1 + t * t * x2;
      const y = inv * inv * y0 + 2 * inv * t * y1 + t * t * y2;
      appendPoint(x, y);
    }
  };

  const subdivideCubic = (x0, y0, x1, y1, x2, y2, x3, y3) => {
    const ctrlLen =
      Math.hypot(x0 - x1, y0 - y1) +
      Math.hypot(x1 - x2, y1 - y2) +
      Math.hypot(x2 - x3, y2 - y3);
    const steps = Math.max(6, Math.ceil(ctrlLen / 0.02));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const inv = 1 - t;
      const x =
        inv * inv * inv * x0 +
        3 * inv * inv * t * x1 +
        3 * inv * t * t * x2 +
        t * t * t * x3;
      const y =
        inv * inv * inv * y0 +
        3 * inv * inv * t * y1 +
        3 * inv * t * t * y2 +
        t * t * t * y3;
      appendPoint(x, y);
    }
  };

  path.commands.forEach(cmd => {
    if (cmd.type === 'M') {
      if (currentSegment.length > 0) {
        segments.push([...currentSegment]);
      }
      const [x, y] = toXY(cmd.x, cmd.y);
      currentSegment = [x, y];
      lastX = x;
      lastY = y;
      startX = x;
      startY = y;
    } else if (cmd.type === 'L') {
      const [x, y] = toXY(cmd.x, cmd.y);
      subdivideLine(lastX, lastY, x, y);
    } else if (cmd.type === 'Q') {
      const [x1, y1] = toXY(cmd.x1, cmd.y1);
      const [x, y] = toXY(cmd.x, cmd.y);
      subdivideQuad(lastX, lastY, x1, y1, x, y);
    } else if (cmd.type === 'C') {
      const [x1, y1] = toXY(cmd.x1, cmd.y1);
      const [x2, y2] = toXY(cmd.x2, cmd.y2);
      const [x, y] = toXY(cmd.x, cmd.y);
      subdivideCubic(lastX, lastY, x1, y1, x2, y2, x, y);
    } else if (cmd.type === 'Z') {
      // Close path back to contour start
      subdivideLine(lastX, lastY, startX, startY);
      if (currentSegment.length > 0) {
        segments.push([...currentSegment]);
      }
      currentSegment = [];
      lastX = startX;
      lastY = startY;
    }
  });

  if (currentSegment.length > 0) {
    segments.push([...currentSegment]);
  }

  // Filter out very short segments and return
  return segments.filter(segment => segment.length >= 4); // Need at least 2 points (4 coordinates)
}

// Initialize font loading when the script loads
loadFont();

// Create 3D text geometry
function create3DText(text, position, size = 1.0, thickness = 0.1) {
  const positions = [];
  const normals = [];
  const indices = [];
  let vertexIndex = 0;

  const lines = text.split('\n');
  let yOffset = 0;

  for (const line of lines) {
    let xOffset = 0;

    for (const char of line) {
      if (char === ' ') {
        xOffset += 1;
        continue;
      }

      const charData = FONT_DATA[char];
      if (!charData) continue;

      // Create 3D geometry for each line segment in the character
      for (const segment of charData) {
        for (let i = 0; i < segment.length - 2; i += 2) {
          const x1 = (segment[i] + xOffset) * size;
          const y1 = (segment[i + 1] + yOffset) * size;
          const x2 = (segment[i + 2] + xOffset) * size;
          const y2 = (segment[i + 3] + yOffset) * size;

          // Create a thin 3D line segment (quad)
          const dx = x2 - x1;
          const dy = y2 - y1;
          const length = Math.sqrt(dx * dx + dy * dy);
          const nx = -dy / length * thickness;
          const ny = dx / length * thickness;

          // Front face
          positions.push(
            position[0] + x1 - nx/2, position[1] + y1 - ny/2, position[2] + thickness/2,
            position[0] + x1 + nx/2, position[1] + y1 + ny/2, position[2] + thickness/2,
            position[0] + x2 + nx/2, position[1] + y2 + ny/2, position[2] + thickness/2,
            position[0] + x2 - nx/2, position[1] + y2 - ny/2, position[2] + thickness/2
          );

          // Back face
          positions.push(
            position[0] + x1 - nx/2, position[1] + y1 - ny/2, position[2] - thickness/2,
            position[0] + x1 + nx/2, position[1] + y1 + ny/2, position[2] - thickness/2,
            position[0] + x2 + nx/2, position[1] + y2 + ny/2, position[2] - thickness/2,
            position[0] + x2 - nx/2, position[1] + y2 - ny/2, position[2] - thickness/2
          );

          // Normals for front face
          for (let j = 0; j < 4; j++) {
            normals.push(0, 0, 1);
          }

          // Normals for back face
          for (let j = 0; j < 4; j++) {
            normals.push(0, 0, -1);
          }

          // Indices for front face
          indices.push(
            vertexIndex, vertexIndex + 1, vertexIndex + 2,
            vertexIndex, vertexIndex + 2, vertexIndex + 3
          );

          // Indices for back face
          indices.push(
            vertexIndex + 4, vertexIndex + 6, vertexIndex + 5,
            vertexIndex + 4, vertexIndex + 3, vertexIndex + 6
          );

          // Side faces
          for (let side = 0; side < 4; side++) {
            const nextSide = (side + 1) % 4;
            indices.push(
              vertexIndex + side, vertexIndex + nextSide, vertexIndex + nextSide + 4,
              vertexIndex + side, vertexIndex + nextSide + 4, vertexIndex + side + 4
            );

            // Add normals for side faces
            const sideNormal = side % 2 === 0 ? [-nx, -ny, 0] : [nx, ny, 0];
            normals.push(...sideNormal, ...sideNormal, ...sideNormal, ...sideNormal);
          }

          vertexIndex += 8;
        }
      }

      xOffset += 1; // Character spacing
    }

    yOffset += 3; // Line spacing
  }

  // Texture coordinates (not used for text but required)
  const textureCoords = new Array(positions.length / 3 * 2).fill(0);

  return {
    positions: positions,
    normals: normals,
    textureCoords: textureCoords,
    indices: indices,
    vertexCount: indices.length,
  };
}

function create3DTextVertical(text, position, size = 1.0, thickness = 0.1) {
  // Bottom-to-top vertical text rendering
  const charSpacingMultiplier = 1.3; // 20% more gap between characters
  const positions = [];
  const normals = [];
  const indices = [];
  let vertexIndex = 0;

  const lines = text.split('\n');
  let xOffset = 0; // Will be used for centering if multiple lines

  for (const line of lines) {
    // For bottom-to-top vertical text, calculate total height first
    let charCount = 0;
    for (const char of line) {
      if (char !== ' ' && FONT_DATA[char]) charCount++;
    }

    // Start from bottom and move up (3 units per character)
    let yOffset = (charCount - 1) * 3 * charSpacingMultiplier; // Start from bottom with extra spacing

    for (const char of line) {
      if (char === ' ') {
        yOffset -= 1.5 * charSpacingMultiplier; // Move up for spaces
        continue;
      }

      const charData = FONT_DATA[char];
      if (!charData) continue;

      // Create 3D geometry for each line segment in the character
      for (const segment of charData) {
        for (let i = 0; i < segment.length - 2; i += 2) {
          const x1 = (segment[i] + xOffset) * size;
          const y1 = (segment[i + 1] + yOffset) * size; // Start from bottom and go upwards
          const x2 = (segment[i + 2] + xOffset) * size;
          const y2 = (segment[i + 3] + yOffset) * size; // Start from bottom and go upwards

          // Create a thin 3D line segment (quad)
          const dx = x2 - x1;
          const dy = y2 - y1;
          const length = Math.sqrt(dx * dx + dy * dy);
          const nx = -dy / length * thickness;
          const ny = dx / length * thickness;

          // Front face
          positions.push(
            position[0] + x1 - nx/2, position[1] + y1 - ny/2, position[2] + thickness/2,
            position[0] + x1 + nx/2, position[1] + y1 + ny/2, position[2] + thickness/2,
            position[0] + x2 + nx/2, position[1] + y2 + ny/2, position[2] + thickness/2,
            position[0] + x2 - nx/2, position[1] + y2 - ny/2, position[2] + thickness/2
          );

          // Back face
          positions.push(
            position[0] + x1 - nx/2, position[1] + y1 - ny/2, position[2] - thickness/2,
            position[0] + x1 + nx/2, position[1] + y1 + ny/2, position[2] - thickness/2,
            position[0] + x2 + nx/2, position[1] + y2 + ny/2, position[2] - thickness/2,
            position[0] + x2 - nx/2, position[1] + y2 - ny/2, position[2] - thickness/2
          );

          // Normals for front face
          for (let j = 0; j < 4; j++) {
            normals.push(0, 0, 1);
          }

          // Normals for back face
          for (let j = 0; j < 4; j++) {
            normals.push(0, 0, -1);
          }

          // Indices for front face
          indices.push(
            vertexIndex, vertexIndex + 1, vertexIndex + 2,
            vertexIndex, vertexIndex + 2, vertexIndex + 3
          );

          // Indices for back face
          indices.push(
            vertexIndex + 4, vertexIndex + 6, vertexIndex + 5,
            vertexIndex + 4, vertexIndex + 3, vertexIndex + 6
          );

          // Side faces
          for (let side = 0; side < 4; side++) {
            const nextSide = (side + 1) % 4;
            indices.push(
              vertexIndex + side, vertexIndex + nextSide, vertexIndex + nextSide + 4,
              vertexIndex + side, vertexIndex + nextSide + 4, vertexIndex + side + 4
            );

            // Add normals for side faces
            const sideNormal = side % 2 === 0 ? [-nx, -ny, 0] : [nx, ny, 0];
            normals.push(...sideNormal, ...sideNormal, ...sideNormal, ...sideNormal);
          }

          vertexIndex += 8;
        }
      }

      yOffset -= 1.5 * charSpacingMultiplier; // Character spacing (vertical, upwards)
    }

    xOffset += 3; // Line spacing (horizontal, if multiple lines)
  }

  // Texture coordinates (not used for text but required)
  const textureCoords = new Array(positions.length / 3 * 2).fill(0);

  return {
    positions: positions,
    normals: normals,
    textureCoords: textureCoords,
    indices: indices,
    vertexCount: indices.length,
  };
}

// Create WebGL buffers for dimension guides - exact fit around real 3D pillow object only
function createDimensionGuideBuffers(gl, boundingBox) {
  // Use the precise bounding box that contains ONLY the real 3D pillow geometry
  // Labels are created separately and don't affect the guide boundaries
  const guideGeometry = createDimensionGuides(boundingBox, 0.0);

  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(guideGeometry.positions), gl.STATIC_DRAW);

  const normalBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(guideGeometry.normals), gl.STATIC_DRAW);

  const textureCoordBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, textureCoordBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(guideGeometry.textureCoords), gl.STATIC_DRAW);

  const indexBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(guideGeometry.indices), gl.STATIC_DRAW);

  return {
    position: positionBuffer,
    normal: normalBuffer,
    textureCoord: textureCoordBuffer,
    indices: indexBuffer,
    vertexCount: guideGeometry.vertexCount,
  };
}

function createReferenceLineBuffers(gl, boundingBox) {
  const referenceGeometry = createReferenceLines(boundingBox);

  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(referenceGeometry.positions), gl.STATIC_DRAW);

  const normalBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(referenceGeometry.normals), gl.STATIC_DRAW);

  const textureCoordBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, textureCoordBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(referenceGeometry.textureCoords), gl.STATIC_DRAW);

  const indexBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(referenceGeometry.indices), gl.STATIC_DRAW);

  return {
    position: positionBuffer,
    normal: normalBuffer,
    textureCoord: textureCoordBuffer,
    indices: indexBuffer,
    vertexCount: referenceGeometry.vertexCount,
  };
}

// Create WebGL buffers for dimension text labels
function createDimensionTextBuffers(gl, boundingBox) {
  const { minX, maxX, minY, maxY, minZ, maxZ } = boundingBox;

  // Calculate actual 3D dimensions
  const width = maxX - minX;
  const height = maxY - minY;
  const depth = maxZ - minZ;

  // Calculate aspect ratio
  const aspectRatio = width / height;

  // Determine which dimension is larger and scale accordingly
  let displayWidthCm, displayHeightCm;
  if (width >= height) {
    // Width is the larger dimension
    displayWidthCm = maxSizeCm ;
    displayHeightCm = maxSizeCm / aspectRatio;
  } else {
    // Height is the larger dimension
    displayHeightCm = maxSizeCm;
    displayWidthCm = maxSizeCm * aspectRatio;
  }

  // Round to nearest integer for clean display
  displayWidthCm = Math.round(displayWidthCm);
  displayHeightCm = Math.round(displayHeightCm);

  // Create text labels in CM
  const widthText = `${displayWidthCm} CM`;
  const heightText = `${displayHeightCm} CM`;

  // Position labels on their respective reference lines
  // Left vertical guide line is at X = minX - offset, bottom horizontal guide line is at Y = minY - offset
  const maxDim = Math.max(width, height);
  const offset = maxDim * 0.02; // Same offset as reference lines
  const centerZ = minZ; // Position at front face of guide

  // Center positions for the reference lines
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  const textSize = maxDim * 0.03; // 3% of largest dimension (2x larger)
  const verticalTextGap = textSize * 4.0; // Gap for vertical text (left side)
  const horizontalTextGap = textSize * 6.0; // Gap for horizontal text (bottom side) - 1 font height gap above the line

  // Calculate text dimensions for proper centering
  const heightTextLength = heightText.replace(/\s/g, '').length; // Count non-space characters
  const widthTextLength = widthText.replace(/\s/g, '').length; // Count non-space characters
  const verticalCharSpacingMultiplier = 1.2; // Keep in sync with create3DTextVertical spacing
  const heightTextHeight = heightTextLength * 1.5 * verticalCharSpacingMultiplier * textSize; // Each character takes 1.5 units vertically (with extra spacing)
  const widthTextWidth = widthTextLength * 1.25 * textSize; // Approximate character width (reduced spacing)

  // Center text within the reference line spans
  const verticalTextCenterY = centerY  - heightTextHeight * 1.5; // Start vertical text from the top of the left reference line
  const horizontalTextCenterX = centerX - widthTextWidth / 2; // Center horizontal text within reference line width

  // Height label positioned to the left of left vertical line with gap, centered vertically along the line
  const heightLabelStartX = minX - offset - verticalTextGap; // Start position reflects increased gap
  const heightLabelGeometry = create3DTextVertical(heightText, [heightLabelStartX, verticalTextCenterY, centerZ], textSize, textSize * 0.15);
  // Width label positioned below bottom horizontal line with gap, centered horizontally along the line
  const widthLabelGeometry = create3DText(widthText, [horizontalTextCenterX, minY - offset - horizontalTextGap, centerZ], textSize, textSize * 0.15);

  // Combine geometries
  const positions = [...widthLabelGeometry.positions, ...heightLabelGeometry.positions];
  const normals = [...widthLabelGeometry.normals, ...heightLabelGeometry.normals];
  const textureCoords = [...widthLabelGeometry.textureCoords, ...heightLabelGeometry.textureCoords];
  const indices = [...widthLabelGeometry.indices, ...heightLabelGeometry.indices.map(i => i + widthLabelGeometry.positions.length / 3)];

  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

  const normalBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(normals), gl.STATIC_DRAW);

  const textureCoordBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, textureCoordBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(textureCoords), gl.STATIC_DRAW);

  const indexBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);

  return {
    position: positionBuffer,
    normal: normalBuffer,
    textureCoord: textureCoordBuffer,
    indices: indexBuffer,
    vertexCount: indices.length,
  };
}

// Create simple 3D dimension guide lines (just width and height indicators around real 3D object)
function createDimensionGuides(boundingBox, offset = 0.0) {
  const { minX, maxX, minY, maxY, minZ, maxZ } = boundingBox;

  // Create simple dimension lines - just the outline edges that touch the real object
  // No complex wireframe, just the essential boundary lines
  const positions = [];
  const indices = [];
  let vertexIndex = 0;

  // Only create lines that directly touch the real 3D object boundaries
  // Bottom edges (touching the real object)
  positions.push(minX, minY, minZ, maxX, minY, minZ); // bottom-back edge
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  positions.push(maxX, minY, minZ, maxX, maxY, minZ); // bottom-right edge
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  positions.push(maxX, maxY, minZ, minX, maxY, minZ); // bottom-front edge
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  positions.push(minX, maxY, minZ, minX, minY, minZ); // bottom-left edge
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  // Top edges (touching the real object)
  positions.push(minX, minY, maxZ, maxX, minY, maxZ); // top-back edge
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  positions.push(maxX, minY, maxZ, maxX, maxY, maxZ); // top-right edge
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  positions.push(maxX, maxY, maxZ, minX, maxY, maxZ); // top-front edge
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  positions.push(minX, maxY, maxZ, minX, minY, maxZ); // top-left edge
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  // Vertical edges connecting top and bottom (touching real object)
  positions.push(minX, minY, minZ, minX, minY, maxZ); // back-left vertical
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  positions.push(maxX, minY, minZ, maxX, minY, maxZ); // back-right vertical
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  positions.push(maxX, maxY, minZ, maxX, maxY, maxZ); // front-right vertical
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  positions.push(minX, maxY, minZ, minX, maxY, maxZ); // front-left vertical
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  // Create normals (simple outward normals for visibility)
  const normals = new Array(positions.length).fill(0);

  // Create texture coordinates (not used for wireframe but required)
  const textureCoords = new Array(positions.length / 3 * 2).fill(0);

  return {
    positions: positions,
    normals: normals,
    textureCoords: textureCoords,
    indices: indices,
    vertexCount: indices.length,
  };
}

// Create reference lines (always visible white lines)
function createReferenceLines(boundingBox) {
  const { minX, maxX, minY, maxY, minZ, maxZ } = boundingBox;

  // Calculate dimensions and offset
  const width = maxX - minX;
  const height = maxY - minY;
  const maxDim = Math.max(width, height);
  const offset = maxDim * 0.02; // 2% of max dimension

  // Center positions
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const centerZ = minZ; // Position at front face of guide

  const positions = [];
  const indices = [];
  let vertexIndex = 0;

  // Bottom center line (horizontal, moved down by offset)
  positions.push(centerX - width/2, minY - offset, centerZ); // left end
  positions.push(centerX + width/2, minY - offset, centerZ); // right end
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  // Left center line (vertical, moved left by offset)
  positions.push(minX - offset, centerY - height/2, centerZ); // bottom end
  positions.push(minX - offset, centerY + height/2, centerZ); // top end
  indices.push(vertexIndex, vertexIndex + 1);
  vertexIndex += 2;

  // Create normals (simple outward normals for visibility)
  const normals = new Array(positions.length).fill(0);

  // Create texture coordinates (not used but required)
  const textureCoords = new Array(positions.length / 3 * 2).fill(0);

  return {
    positions: positions,
    normals: normals,
    textureCoords: textureCoords,
    indices: indices,
    vertexCount: indices.length,
  };
}

let lastTime = 0;
const LOW_POWER_FRAME_INTERVAL = isLowPowerDevice ? (1000 / 30) : 0;
let lastFrameTick = 0;
// function animate(time) {
//   if (lastTime != 0 && !isDragging) {
//     const delta = (time - lastTime) / 1000;
//     rotY -= delta * rotationSpeed;
//   }
//   if (isAnimating) {
//     const elapsed = (time - animStartTime) / 1000;
//     if (elapsed >= animationDuration) {
//       currentScale = 1;
//       isAnimating = false;
//       rotationSpeed = 0.628; // Normal speed after animation
//     } else {
//       currentScale = elapsed / animationDuration;
//       rotationSpeed = animSpinSpeed; // Animation spin speed
//     }
//   }
//   lastTime = time;
//   drawScene();
//   requestAnimationFrame(animate);
// }
let rotationEnabled = true;
function animate(time) {
  if (LOW_POWER_FRAME_INTERVAL) {
    const deltaMs = time - lastFrameTick;
    if (deltaMs < LOW_POWER_FRAME_INTERVAL) {
      requestAnimationFrame(animate);
      return;
    }
    lastFrameTick = time;
  }
  if (lastTime !== 0 && !isDragging && rotationEnabled) {
    const delta = (time - lastTime) / 1000;
    rotY -= delta * rotationSpeed;
  }

  if (isAnimating) {
    const elapsed = (time - animStartTime) / 1000;

    if (elapsed >= animationDuration) {
      currentScale = 1;
      isAnimating = false;
      rotationSpeed = 0.25; 
    } else {
      currentScale = elapsed / animationDuration;
      rotationSpeed = animSpinSpeed * 0.4;
    }
  }

  lastTime = time;
  drawScene();
  requestAnimationFrame(animate);
}